import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, NavLink, Navigate, useParams, useNavigate, useLocation } from "react-router-dom";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { AuthProvider, useAuth } from "./AuthContext";
import { api } from "./api";
import { CLAUSES, CLAUSE_TITLES, CLAUSE_FORMATS, CLAUSE_NOTES, CODE_ROUTES } from "./formats";
import { printFormat, printContent, printPages, PagedDoc, FixedPagesDoc, FormatHeader, FormatTable, FormatFormDoc, FormatTopbar, SignatureInput, PersonSignature, ActiveToggle, inDate } from "./formatKit";
import { Clause4, Clause7 } from "./clause47";
import { Clause6 } from "./clause6";
import "./index.css";

const SIDE_ICONS = {
  dash: <svg className="ic" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /></svg>,
  book: <svg className="ic" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" /></svg>,
  file: <svg className="ic" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /></svg>,
  building: <svg className="ic" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="1" /><path d="M9 22v-4h6v4" /><path d="M8 6h.01M12 6h.01M16 6h.01M8 10h.01M12 10h.01M16 10h.01" /></svg>,
  sitemap: <svg className="ic" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="3" width="6" height="5" rx="1" /><rect x="3" y="16" width="6" height="5" rx="1" /><rect x="15" y="16" width="6" height="5" rx="1" /><path d="M12 8v4M6 16v-2h12v2" /></svg>,
  list: <svg className="ic" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 6h13M8 12h13M8 18h13" /><path d="M3 6h.01M3 12h.01M3 18h.01" /></svg>,
  lock: <svg className="ic" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>,
  users: <svg className="ic" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /></svg>,
  db: <svg className="ic" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M21 5v14c0 1.66-4 3-9 3s-9-1.34-9-3V5" /><path d="M3 12c0 1.66 4 3 9 3s9-1.34 9-3" /></svg>,
  audit: <svg className="ic" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="8" y="2" width="8" height="4" rx="1" /><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" /><path d="M9 12h6M9 16h6" /></svg>,
};

function Sidebar({ collapsed, onToggle }) {
  const { can, logo } = useAuth();
  const [adminOpen, setAdminOpen] = useState(() => localStorage.getItem("admin_open") !== "0");
  const toggleAdmin = () => { const v = !adminOpen; setAdminOpen(v); localStorage.setItem("admin_open", v ? "1" : "0"); };
  const [openClause, setOpenClause] = useState(() => { try { const s = localStorage.getItem("open_clauses"); return s ? JSON.parse(s) : { 4: true }; } catch (e) { return { 4: true }; } });
  const toggleClause = (n) => { const v = { ...openClause, [n]: !openClause[n] }; setOpenClause(v); localStorage.setItem("open_clauses", JSON.stringify(v)); };
  const [openSec, setOpenSec] = useState(() => { try { const s = localStorage.getItem("open_secs"); return s ? JSON.parse(s) : {}; } catch (e) { return {}; } });
  const toggleSec = (key) => { const v = { ...openSec, [key]: openSec[key] === false ? true : false }; setOpenSec(v); localStorage.setItem("open_secs", JSON.stringify(v)); };
  const link = (to, label, icon) => <NavLink to={to} title={label} className={({ isActive }) => isActive ? "active" : ""}>{icon}<span className="lbl">{label}</span></NavLink>;
  return (
    <div className={"side" + (collapsed ? " collapsed" : "")}>
      <div className="brand">
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {logo && <img src={logo} alt="Sanber Labs" style={{ height: 32, background: "#fff", borderRadius: 6, padding: 3 }} />}
          <div className="btext"><div className="bt">Sanber Labs</div><div className="bs">Quality System · ISO 17025</div></div>
        </div>
        <button className="nav-toggle" onClick={onToggle} title={collapsed ? "Show menu" : "Hide menu"} aria-label={collapsed ? "Show menu" : "Hide menu"}>
          {collapsed
            ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6" /></svg>
            : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 6l-6 6 6 6" /></svg>}
        </button>
      </div>
      {link("/", "Dashboard", SIDE_ICONS.dash)}
      {link("/alerts", "Alert Center", SIDE_ICONS.audit)}
      {link("/compliance", "Compliance calendar", SIDE_ICONS.audit)}
      {can("clause_documents", "view") && link("/internal-audit", "Internal audit", SIDE_ICONS.lock)}
      {can("rulebook", "view") && <>
        <div className="grp">Rulebook</div>
        {link("/rulebook/quality_manual", "Quality Manual", SIDE_ICONS.book)}
        {link("/rulebook/procedure_manual", "Procedure Manual", SIDE_ICONS.book)}
        {link("/rulebook/format_manual", "Format Manual", SIDE_ICONS.book)}
      </>}
      {can("clause_documents", "view") && <>
        <div className="grp">Documents</div>
        {CLAUSES.map(c => {
          const fmts = CLAUSE_FORMATS[String(c.n)] || [];
          if (collapsed) return <div key={c.n}>{link("/clause/" + c.n, "Clause " + c.n, SIDE_ICONS.file)}</div>;
          return <div key={c.n}>
            <div className="grp" onClick={() => toggleClause(c.n)} style={{ cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span>Clause {c.n} · {c.t}</span>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">{openClause[c.n] ? <path d="M6 9l6 6 6-6" /> : <path d="M9 6l6 6-6 6" />}</svg>
            </div>
            {openClause[c.n] && (() => {
              const secs = [];
              fmts.forEach(f => { let s = secs.find(x => x.name === f.section); if (!s) { s = { name: f.section, items: [] }; secs.push(s); } s.items.push(f); });
              return secs.map(s => {
                const secKey = c.n + "|" + s.name;
                const secOpen = openSec[secKey] !== false;
                return <div key={s.name}>
                  <div className="side-sec" onClick={() => toggleSec(secKey)} style={{ cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span>{s.name}</span>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">{secOpen ? <path d="M6 9l6 6 6-6" /> : <path d="M9 6l6 6-6 6" />}</svg>
                  </div>
                  {secOpen && s.items.map(f => <NavLink key={f.coll} to={"/clause/" + c.n + "/" + f.coll} title={(f.code ? f.code + " — " : "") + f.title} className={({ isActive }) => "side-fmt" + (isActive ? " active" : "")}>
                    <span className="side-code">{f.code}</span><span className="lbl">{f.title}</span>
                  </NavLink>)}
                </div>;
              });
            })()}
            {openClause[c.n] && fmts.length === 0 && <div style={{ padding: "4px 0 6px 16px", fontSize: 12, color: "var(--text-2)" }}>No formats yet</div>}
          </div>;
        })}
      </>}
      <div className="grp">Setup</div>
      {can("company", "view") && link("/company", "Company", SIDE_ICONS.building)}
      {can("rulebook", "view") && link("/obsolete", "Obsolete documents", SIDE_ICONS.file)}
      {!collapsed && <div className="grp" onClick={toggleAdmin} style={{ cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span>Administration</span>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">{adminOpen ? <path d="M6 9l6 6 6-6" /> : <path d="M9 6l6 6-6 6" />}</svg>
      </div>}
      {(collapsed || adminOpen) && <>
        {can("masters", "view") && link("/masters", "Masters", SIDE_ICONS.list)}
        {can("roles", "view") && link("/roles", "RBAC", SIDE_ICONS.lock)}
        {can("users", "view") && link("/users", "Users", SIDE_ICONS.users)}
        {can("backup", "view") && link("/backup", "Backup & restore", SIDE_ICONS.db)}
        {can("backup", "view") && link("/recycle-bin", "Recycle bin", SIDE_ICONS.file)}
        {(can("users", "view") || can("roles", "view")) && link("/audit-log", "Audit log", SIDE_ICONS.audit)}
      </>}
    </div>
  );
}

// Small inline line-icons for the login screen (stroke = currentColor).
const LI = {
  shieldCheck: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l7 3v5c0 4.5-3 7.9-7 9-4-1.1-7-4.5-7-9V6z" /><path d="M9 12l2 2 4-4" /></svg>,
  target: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="4" /><circle cx="12" cy="12" r="1" /></svg>,
  trend: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 17l6-6 4 4 7-7" /><path d="M17 5h4v4" /></svg>,
  users: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="8" r="3.2" /><path d="M3.5 19a5.5 5.5 0 0 1 11 0" /><path d="M16 5.2a3.2 3.2 0 0 1 0 5.6" /><path d="M18 19a5.5 5.5 0 0 0-3-4.9" /></svg>,
  badge: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="9" r="5" /><path d="M9 13.5L8 21l4-2 4 2-1-7.5" /></svg>,
  check: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M8.5 12.5l2.5 2.5 4.5-5" /></svg>,
  shield: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l7 3v5c0 4.5-3 7.9-7 9-4-1.1-7-4.5-7-9V6z" /></svg>,
  user: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="3.6" /><path d="M5 20a7 7 0 0 1 14 0" /></svg>,
  lock: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></svg>,
  eye: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" /><circle cx="12" cy="12" r="3" /></svg>,
  eyeOff: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3l18 18" /><path d="M10.6 6.2A9.9 9.9 0 0 1 12 5c6.5 0 10 7 10 7a17 17 0 0 1-3.6 4.3" /><path d="M6.2 6.3A17 17 0 0 0 2 12s3.5 7 10 7a9.6 9.6 0 0 0 4.2-.9" /><path d="M9.5 9.6a3 3 0 0 0 4.2 4.2" /></svg>,
  microscope: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><path d="M6 20h12" /><path d="M9 20a6 6 0 0 0 6-9.5" /><path d="M9.5 4.5l3.5 3.5-2.5 2.5L7 7z" /><path d="M11 10.5L8.5 13" /><path d="M8 16h3" /></svg>,
  sso: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="4" width="9" height="16" rx="1.5" /><path d="M13 9h6a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1h-6" /><path d="M7 8h3M7 12h3M16 13h1" /></svg>,
  alert: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 8v4.5M12 16h.01" /></svg>,
};

function Login() {
  const { login } = useAuth();
  const [u, setU] = useState(() => { try { return localStorage.getItem("qms_saved_user") || ""; } catch (_) { return ""; } });
  const [p, setP] = useState(""); const [e, setE] = useState("");
  const [mustChange, setMustChange] = useState(false);
  const [np, setNp] = useState(""); const [np2, setNp2] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [remember, setRemember] = useState(() => { try { return !!localStorage.getItem("qms_saved_user"); } catch (_) { return false; } });
  const [forgot, setForgot] = useState(false);
  const [sso, setSso] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (ev) => {
    ev.preventDefault(); setE(""); setBusy(true);
    try {
      await login(u, p);
      try { if (remember) localStorage.setItem("qms_saved_user", u); else localStorage.removeItem("qms_saved_user"); } catch (_) {}
      window.location.href = "/";
    } catch (x) { setE(x.message); setBusy(false); }
  };
  const doChange = async (ev) => {
    ev.preventDefault(); setE("");
    if (np.length < 8) { setE("New password must be at least 8 characters."); return; }
    if (np !== np2) { setE("Passwords do not match."); return; }
    try { await api.changePassword({ old_password: p, new_password: np }); window.location.href = "/"; } catch (x) { setE(x.message); }
  };
  const logo = <img className="lc-logo" src="/logo.png" alt="Sanber Labs" onError={(ev) => { ev.target.style.display = 'none'; }} />;
  return <div className="login-page login-imgbg" style={{ backgroundImage: "linear-gradient(180deg,rgba(240,246,255,.28) 0%,rgba(224,238,255,.36) 100%), url('/login-bg.jpg')" }}>
    <div className="login-bg" aria-hidden="true">
      <span className="lp-blob b1" /><span className="lp-blob b2" /><span className="lp-blob b3" />
      <span className="lp-grid" />
      {/* molecular / network pattern */}
      <svg className="lp-net" viewBox="0 0 520 320" preserveAspectRatio="xMidYMid meet">
        <g stroke="#9cc2f5" strokeWidth="1" fill="none" opacity="0.7">
          <path d="M40 70 L150 44 L225 120 L128 185 L40 70" />
          <path d="M225 120 L340 78 L430 150 L340 215 L225 120" />
          <path d="M128 185 L225 255 L340 215" />
        </g>
        <g fill="#60A5FA">
          {[[40, 70], [150, 44], [225, 120], [128, 185], [340, 78], [430, 150], [340, 215], [225, 255]].map(([x, y], i) =>
            <circle key={i} cx={x} cy={y} r="3.2" className={"lp-node n" + (i % 4)} />)}
        </g>
      </svg>
      {/* thin elegant wave lines */}
      <svg className="lp-waves" viewBox="0 0 1440 220" preserveAspectRatio="none">
        <path d="M0 130 C 240 90, 480 170, 720 130 S 1200 90, 1440 130" />
        <path d="M0 160 C 240 120, 480 200, 720 160 S 1200 120, 1440 160" />
        <path d="M0 100 C 240 60, 480 140, 720 100 S 1200 60, 1440 100" />
      </svg>
      {/* laboratory scene — detailed microscope + glassware (real background layer) */}
      <svg className="lp-lab" viewBox="0 0 480 300" fill="none">
        <defs>
          <radialGradient id="labGlow" cx="30%" cy="45%" r="70%">
            <stop offset="0" stopColor="#e8f2ff" stopOpacity="0.95" /><stop offset="1" stopColor="#e8f2ff" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="lgMetal" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#eef5ff" /><stop offset="0.5" stopColor="#c3d8f4" /><stop offset="1" stopColor="#93b6e2" />
          </linearGradient>
          <linearGradient id="lgGlass" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#e4eefc" /><stop offset="1" stopColor="#aecbef" />
          </linearGradient>
          <linearGradient id="lgLiquid" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#9cc2f5" /><stop offset="1" stopColor="#6aa0e6" />
          </linearGradient>
        </defs>
        <ellipse cx="150" cy="175" rx="200" ry="150" fill="url(#labGlow)" />
        {/* microscope */}
        <g transform="translate(20,22)" stroke="#6f97cc" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round">
          <path d="M34 240 q0 -26 44 -26 h48 q44 0 44 26 z" fill="url(#lgMetal)" />
          <rect x="86" y="203" width="34" height="13" rx="5" fill="#cfe0f7" />
          <path d="M120 214 C 156 206 162 156 152 126 L136 128 C 144 156 134 194 108 206 Z" fill="url(#lgMetal)" />
          <circle cx="134" cy="182" r="13" fill="url(#lgMetal)" /><circle cx="134" cy="182" r="5" fill="#9db9e2" stroke="none" />
          <path d="M96 182 h18 l-4 16 h-10 z" fill="#dbe9fb" strokeWidth="1.5" />
          <rect x="66" y="170" width="74" height="12" rx="4" fill="url(#lgMetal)" />
          <rect x="86" y="166" width="36" height="5" rx="2" fill="#eef5ff" strokeWidth="1.5" />
          <circle cx="103" cy="152" r="14" fill="url(#lgMetal)" />
          <rect x="94" y="158" width="8" height="16" rx="3" fill="#9db9e2" strokeWidth="1.5" /><rect x="105" y="158" width="8" height="13" rx="3" fill="#b3cbec" strokeWidth="1.5" />
          <rect x="92" y="100" width="23" height="54" rx="8" fill="url(#lgMetal)" />
          <rect x="90" y="84" width="20" height="20" rx="6" fill="url(#lgMetal)" />
          <rect x="93" y="70" width="14" height="16" rx="5" fill="#a7c7ee" strokeWidth="2" />
        </g>
        {/* Erlenmeyer flask */}
        <g transform="translate(258,150)" stroke="#6f97cc" strokeWidth="2.5" strokeLinejoin="round">
          <path d="M16 4 h22 M20 4 v22 L6 60 q-4 9 7 9 h28 q11 0 7 -9 L38 26 V4" fill="url(#lgGlass)" />
          <path d="M10 50 q14 6 28 0 v9 q0 5 -6 5 H16 q-6 0 -6 -5 z" fill="url(#lgLiquid)" strokeWidth="0" />
        </g>
        {/* beaker */}
        <g transform="translate(330,178)" stroke="#6f97cc" strokeWidth="2.5" strokeLinejoin="round">
          <path d="M4 4 h42 v44 q0 8 -8 8 H12 q-8 0 -8 -8 z" fill="url(#lgGlass)" />
          <path d="M6 32 h38 v14 q0 6 -6 6 H12 q-6 0 -6 -6 z" fill="url(#lgLiquid)" strokeWidth="0" />
          <path d="M4 6 l-7 -3" />
        </g>
        {/* test tubes */}
        <g transform="translate(406,150)" stroke="#6f97cc" strokeWidth="2" strokeLinejoin="round">
          <rect x="0" y="0" width="11" height="66" rx="5.5" fill="url(#lgGlass)" />
          <path d="M1.5 46 h8 v13 q0 4 -4 4 t-4 -4 z" fill="url(#lgLiquid)" strokeWidth="0" />
          <rect x="18" y="8" width="11" height="60" rx="5.5" fill="url(#lgGlass)" />
          <path d="M19.5 40 h8 v20 q0 4 -4 4 t-4 -4 z" fill="url(#lgLiquid)" strokeWidth="0" />
        </g>
      </svg>
      <span className="lp-dot d1" /><span className="lp-dot d2" /><span className="lp-dot d3" /><span className="lp-dot d4" /><span className="lp-dot d5" /><span className="lp-dot d6" /><span className="lp-dot d7" />
    </div>
    <div className="login-shell">
      <section className="login-brand">
        <span className="lb-photo-veil" aria-hidden="true" />
        <div className="lb-values">
          {[["Quality First", LI.shieldCheck], ["Precision Always", LI.target], ["Continuous Improvement", LI.trend], ["Customer Satisfaction", LI.users]].map(([t, ic]) =>
            <div className="lb-value" key={t}><span className="lb-ico">{ic}</span><span>{t}</span></div>)}
        </div>
        <div className="lb-headline">
          <h1 className="lb-title">Excellence in<br /><span className="grad">Every Process</span></h1>
          <div className="lb-rule"><span /></div>
          <p className="lb-desc">Empowering quality, ensuring<br />compliance, driving success.</p>
        </div>
        <div className="lb-badges glass">
          {[["ISO/IEC 17025", "Compliant", LI.badge], ["Reliable", "& Accurate", LI.check], ["Secure", "& Trusted", LI.shield]].map(([a, b, ic], i) =>
            <React.Fragment key={a}>{i > 0 && <span className="lb-sep" />}<div className="lb-badge"><span className="lb-bico">{ic}</span><div><b>{a}</b><span>{b}</span></div></div></React.Fragment>)}
        </div>
      </section>
      <section className="login-panel">
        <form className="login-card glass" onSubmit={mustChange ? doChange : submit}>
          <span className="lc-glare" aria-hidden="true" />
          <span className="lc-dots" aria-hidden="true" />
          <div className="lc-head">
            {logo}
            <h2 className="lc-title">Sanber Labs QMS</h2>
            <div className="lc-sub"><span className="lc-subico">{LI.shield}</span>Quality Management System • ISO/IEC 17025</div>
            <div className="lc-status"><span className="lc-status-dot" />Secure Quality Environment</div>
          </div>
          {mustChange ? <>
            <div className="lc-note">This account is using the factory-default password. Choose a new one to continue.</div>
            <label>New password (min 8 characters)</label>
            <div className="lc-field"><span className="lc-fico">{LI.lock}</span><input type={showPass ? "text" : "password"} value={np} onChange={e => setNp(e.target.value)} placeholder="Enter a new password" />
              <button type="button" className="lc-eye" aria-label={showPass ? "Hide password" : "Show password"} onClick={() => setShowPass(s => !s)}>{showPass ? LI.eyeOff : LI.eye}</button></div>
            <label>Confirm new password</label>
            <div className="lc-field"><span className="lc-fico">{LI.lock}</span><input type={showPass ? "text" : "password"} value={np2} onChange={e => setNp2(e.target.value)} placeholder="Re-enter the new password" /></div>
            {e && <div className="lc-error" role="alert">{LI.alert}<span>{e}</span></div>}
            <button className="lc-submit" type="submit">{LI.lock}<span>Change password & continue</span></button>
          </> : <>
            <label htmlFor="lg-user">Username</label>
            <div className="lc-field"><span className="lc-fico">{LI.user}</span><input id="lg-user" value={u} onChange={e => setU(e.target.value)} placeholder="Enter your username" autoComplete="username" autoFocus /></div>
            <label htmlFor="lg-pass">Password</label>
            <div className="lc-field"><span className="lc-fico">{LI.lock}</span><input id="lg-pass" type={showPass ? "text" : "password"} value={p} onChange={e => setP(e.target.value)} placeholder="Enter your password" autoComplete="current-password" />
              <button type="button" className="lc-eye" aria-label={showPass ? "Hide password" : "Show password"} onClick={() => setShowPass(s => !s)}>{showPass ? LI.eyeOff : LI.eye}</button></div>
            <div className="lc-row">
              <label className="lc-check"><input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)} />Remember me</label>
              <button type="button" className="lc-forgot" onClick={() => setForgot(f => !f)}>Forgot password?</button>
            </div>
            {forgot && <div className="lc-note">Please contact your Quality Manager / administrator to reset your password.</div>}
            {e && <div className="lc-error" role="alert">{LI.alert}<span>{e}</span></div>}
            <button className="lc-submit" type="submit" disabled={busy}>{busy ? <span className="lc-spin" aria-hidden="true" /> : LI.lock}<span>{busy ? "Signing in…" : "Sign in"}</span></button>
            <div className="lc-or"><span>or continue with</span></div>
            <div className="lc-sso-wrap">
              <button type="button" className="lc-sso" onClick={() => setSso(s => !s)} aria-label="SSO Login">{LI.sso}</button>
              <div className="lc-sso-label">SSO Login</div>
            </div>
            {sso && <div className="lc-note">Single sign-on is not configured yet. Contact your administrator to enable SSO.</div>}
            <div className="lc-iso"><span className="lc-iso-ico">{LI.shieldCheck}</span><div><b>ISO/IEC 17025</b><span>Quality Framework</span></div></div>
          </>}
        </form>
        <div className="login-foot">© 2025 Sanber Labs. All rights reserved.</div>
      </section>
    </div>
  </div>;
}

function Dashboard() {
  const [d, setD] = useState(null);
  const [cal, setCal] = useState({ overdue: [], due_soon: [] });
  const [ready, setReady] = useState(null);
  const [gaps, setGaps] = useState({ gaps: [], total: 0 });
  const [findings, setFindings] = useState([]);
  const [comp, setComp] = useState([]);
  const [people, setPeople] = useState([]);
  const [signsList, setSignsList] = useState([]);
  const [alerts, setAlerts] = useState(null);
  const { can } = useAuth();
  useEffect(() => {
    api.notifSummary().then(setAlerts).catch(() => {});
    api.dashboard().then(setD);
    api.calibrationAlerts().then(setCal).catch(() => {});
    api.readiness().then(setReady).catch(() => {});
    api.fieldGaps().then(setGaps).catch(() => {});
    api.c4List("audit_findings").then(setFindings).catch(() => {});
    api.compliance().then(r => setComp((r.tasks || []).filter(t => t.status !== "ok"))).catch(() => {});
    api.personnelPublic().then(setPeople).catch(() => {});
    api.signatoriesPublic().then(setSignsList).catch(() => {});
  }, []);
  const reloadGaps = () => api.fieldGaps().then(setGaps).catch(() => {});
  const closeFinding = async (f) => {
    await api.c4Edit("audit_findings", f.id, { ...f, status: "closed" });
    api.c4List("audit_findings").then(setFindings).catch(() => {});
  };
  if (!d) return <div className="main">Loading…</div>;
  const counts = (ready && ready.counts) || {};
  const docs = (ready && ready.documents) || {};
  const clauseStats = CLAUSES.map(c => {
    const fmts = CLAUSE_FORMATS[String(c.n)] || [];
    const filled = fmts.filter(f => f.lims || f.system || f.tally || (counts[f.coll] || 0) > 0).length;
    return { ...c, total: fmts.length, filled, pct: fmts.length ? Math.round(100 * filled / fmts.length) : 0 };
  });
  const DOC_LABELS = { quality_manual: "Quality Manual", procedure_manual: "Procedure Manual", format_manual: "Format Manual" };
  const alertRow = (key, name, badge, badgeClass) => <NavLink key={key} to="/clause/6/equipment_list" className="row" style={{ justifyContent: "space-between", padding: "6px 0", borderTop: "1px solid var(--border)", textDecoration: "none", color: "inherit" }}><span>{name}</span><span className={badgeClass}>{badge}</span></NavLink>;
  return <div className="main main-wide">
    <div className="topbar" style={{ display: "block" }}>
      <h2>Audit readiness</h2>
      <div style={{ color: "var(--text-2)", fontSize: 13, marginTop: 2 }}>ISO/IEC 17025:2017 — clause-wise status of controlled documents and quality records</div>
    </div>

    {alerts && (alerts.overdue + alerts.warning + alerts.reminder + alerts.info > 0) && <div className="card" style={{ marginBottom: 14 }}>
      <div className="row" style={{ gap: 8, alignItems: "center", marginBottom: 10 }}><span style={{ fontSize: 18 }}>🔔</span><b>QMS Alerts</b><span style={{ color: "var(--text-2)", fontSize: 13 }}>time-sensitive records</span></div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        {[["overdue", "🔴", "Overdue", "status=overdue"], ["warning", "🟠", "Due soon (≤7 days)", "severity=WARNING"], ["reminder", "🟡", "Due (≤15 days)", "severity=REMINDER"], ["info", "🟢", "Upcoming (≤30 days)", "severity=INFO"]].map(([k, icon, label, qs]) =>
          <NavLink key={k} to={"/alerts?" + qs} style={{ flex: "1 1 150px", minWidth: 150, textDecoration: "none", color: "inherit", border: "1px solid var(--border)", borderRadius: 10, padding: "12px 14px", background: "var(--surface,#fff)" }}>
            <div style={{ fontSize: 26, fontWeight: 800 }}>{icon} {alerts[k] || 0}</div>
            <div style={{ fontSize: 13, color: "var(--text-2)", marginTop: 2 }}>{label}</div>
          </NavLink>)}
      </div>
    </div>}

    <div className="clause-board">
      {clauseStats.map(c => <NavLink key={c.n} to={"/clause/" + c.n} className="clause-card">
        <div className="cc-head">
          <span className="cc-num">Clause {c.n}</span>
          <span className={"badge " + (c.pct === 100 ? "ok" : c.pct >= 50 ? "warn" : "obsolete")}>{c.filled}/{c.total} formats</span>
        </div>
        <div className="cc-title">{c.t}</div>
        <div className="cc-bar"><div className="cc-fill" style={{ width: c.pct + "%" }} /></div>
        <div className="cc-pct">{c.pct}% of formats carry records</div>
      </NavLink>)}
    </div>

    <div className="dash-cols">
      <div className="card">
        <b>Controlled documents (Rulebook)</b>
        <table style={{ marginTop: 8 }}><tbody>
          {Object.keys(DOC_LABELS).map(k => <tr key={k}>
            <td><NavLink to={"/rulebook/" + k} style={{ color: "inherit", textDecoration: "none", fontWeight: 500 }}>{DOC_LABELS[k]}</NavLink></td>
            <td>{docs[k] ? "v" + docs[k].version : "—"}</td>
            <td style={{ textAlign: "right" }}>{docs[k] && docs[k].loaded
              ? <span className="badge ok">CONTENT LOADED</span>
              : <span className="badge obsolete">PLACEHOLDER</span>}</td>
          </tr>)}
        </tbody></table>
      </div>

      <div className="card">
        <b>Calibration status</b>
        {(cal.overdue.length === 0 && cal.due_soon.length === 0) && <div style={{ color: "var(--text-2)", fontSize: 13, marginTop: 8 }}>No calibrations due in the next 30 days.</div>}
        {cal.overdue.map((e, i) => alertRow("o" + i, e.name, "Overdue " + Math.abs(e.days) + "d", "badge obsolete"))}
        {cal.due_soon.map((e, i) => alertRow("s" + i, e.name, "Due in " + e.days + "d", "badge warn"))}
      </div>

      <div className="card">
        <b>Compliance nudges</b> {comp.length > 0 ? <span className="badge obsolete">{comp.length} due</span> : <span className="badge ok">all current</span>}
        <div style={{ fontSize: 12, color: "var(--text-2)", margin: "4px 0 2px" }}>Recurring NABL tasks whose evidence record is missing or outside its window. <NavLink to="/compliance" style={{ color: "var(--info)" }}>Open calendar →</NavLink></div>
        {comp.slice(0, 8).map(t => <div key={t.id} className="row" style={{ justifyContent: "space-between", padding: "6px 0", borderTop: "1px solid var(--border)", fontSize: 13 }}>
          <span><b style={{ fontWeight: 500 }}>{t.title}</b> <span style={{ color: "var(--text-3)" }}>({t.freq})</span></span>
          <span className="badge obsolete">{t.status === "never" ? "no record" : "overdue"}</span>
        </div>)}
      </div>

      <div className="card">
        <b>Internal audit findings</b> {findings.filter(f => f.status !== "closed").length > 0 && <span className="badge obsolete">{findings.filter(f => f.status !== "closed").length} open</span>}
        {findings.length === 0 && <div style={{ color: "var(--text-2)", fontSize: 13, marginTop: 8 }}>No findings recorded.</div>}
        {findings.filter(f => f.status !== "closed").map(f => <div key={f.id} style={{ borderTop: "1px solid var(--border)", padding: "8px 0", fontSize: 13 }}>
          <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
            <span><span className={"badge " + (f.severity === "NC" ? "obsolete" : "warn")}>{f.severity || "OBS"}</span> <b style={{ fontWeight: 600 }}>{f.ref}</b></span>
            {can("clause_documents", "edit") && <button className="view-btn" style={{ width: "auto", padding: "0 8px", fontSize: 12 }} title="Mark resolved" onClick={() => closeFinding(f)}>Close</button>}
          </div>
          <div style={{ marginTop: 3 }}>{f.finding}</div>
          {f.recommendation && <div style={{ marginTop: 2, color: "var(--text-2)" }}>→ {f.recommendation}</div>}
        </div>)}
      </div>

      <div className="card">
        <b>Records needing correction</b> {gaps.total > 0 && <span className="badge warn">{gaps.total}</span>}
        <div style={{ fontSize: 12, color: "var(--text-2)", margin: "4px 0 2px" }}>Existing records missing fields their format requires. Data is never changed automatically — open the record and complete it.</div>
        {gaps.total === 0 && <div style={{ color: "var(--text-2)", fontSize: 13, marginTop: 6 }}>All records carry their required fields.</div>}
        {(() => {
          const byColl = {};
          gaps.gaps.forEach(g => { (byColl[g.coll] = byColl[g.coll] || []).push(g); });
          return Object.keys(byColl).map(collKey => {
            let entry = null, clauseN = "";
            Object.keys(CLAUSE_FORMATS).forEach(nn => { const f = CLAUSE_FORMATS[nn].find(x => x.coll === collKey); if (f) { entry = f; clauseN = nn; } });
            return <div key={collKey} style={{ borderTop: "1px solid var(--border)", padding: "7px 0", fontSize: 13 }}>
              <NavLink to={entry ? "/clause/" + clauseN + "/" + collKey : "#"} style={{ fontWeight: 600, color: "inherit", textDecoration: "none" }}>
                {entry ? entry.code + " — " + entry.title : collKey}
              </NavLink>
              {byColl[collKey].map(g => <GapFixRow key={g.id} g={g} people={people} signs={signsList} onSaved={reloadGaps} />)}
            </div>;
          });
        })()}
      </div>

      <div className="card">
        <b>Recent activity</b>
        {(d.recent || []).map((r, i) => <div key={i} className="row" style={{ justifyContent: "space-between", padding: "6px 0", borderTop: i ? "1px solid var(--border)" : "none", fontSize: 13 }}>
          <span><b style={{ fontWeight: 500 }}>{r.username}</b> · {r.action} · {r.area}</span>
          <span style={{ color: "var(--text-3)" }}>{(r.timestamp || "").slice(0, 16).replace("T", " ")}</span>
        </div>)}
      </div>
    </div>
  </div>;
}

function ObsoleteDocs() {
  const { can } = useAuth();
  const [list, setList] = useState([]);
  const [view, setView] = useState(null);
  const load = () => api.documents().then(async docs => {
    const all = [];
    for (const d of docs) {
      const vers = await api.versions(d.doc_key).catch(() => []);
      vers.filter(v => v.status === "obsolete").forEach(v => all.push({ ...v, doc_title: d.title, doc_key: d.doc_key }));
    }
    all.sort((a, b) => (b.approved_at || "").localeCompare(a.approved_at || ""));
    setList(all);
  });
  useEffect(() => { load(); }, []);
  const revert = async (v) => { if (window.confirm("Reinstate " + v.doc_title + " v" + v.version_no + " as the current version? The current version will move to obsolete.")) { try { await api.revertVersion(v.doc_key, v.id); setView(null); load(); alert("Reverted — v" + v.version_no + " is now the current version."); } catch (e) { alert(e.message); } } };
  const eye = <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" /><circle cx="12" cy="12" r="3" /></svg>;
  return <div className="main">
    <div className="topbar"><h2>Obsolete documents</h2></div>
    <div className="card">
      {list.length === 0
        ? <div className="empty">No obsolete versions yet. When a manual is edited and a new version is signed off, the previous version is retained here — locked and read-only.</div>
        : <table><thead><tr><th>Document</th><th>Version</th><th>Change made</th><th>Approved by</th><th>Date</th><th></th></tr></thead>
          <tbody>{list.map(v => <tr key={v.id}>
            <td>{v.doc_title}</td><td>v{v.version_no} <span className="badge obsolete">OBSOLETE</span></td>
            <td>{v.amendment && v.amendment.change_made}</td><td>{v.approved_by}</td><td>{inDate((v.approved_at || "").slice(0, 10))}</td>
            <td style={{ whiteSpace: "nowrap", textAlign: "right" }}><button className="view-btn" title="Read" aria-label="Read" onClick={() => setView(v)}>{eye}</button>{can("rulebook", "edit") && <button onClick={() => revert(v)} style={{ marginLeft: 8 }}>Revert</button>}</td>
          </tr>)}</tbody></table>}
    </div>
    {view && <div className="modal-overlay" onClick={() => setView(null)}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div className="row" style={{ justifyContent: "space-between", marginBottom: 10 }}>
          <b>{view.doc_title} — v{view.version_no} <span className="badge obsolete">OBSOLETE</span></b>
          <div className="row"><button onClick={() => printContent(view.doc_title + " — v" + view.version_no, "", normalizeDocTables(view.content))}>Print / PDF</button>{can("rulebook", "edit") && <button className="primary" onClick={() => revert(view)}>Revert to this</button>}<button onClick={() => setView(null)}>Close</button></div>
        </div>
        <div style={{ fontSize: 13, color: "var(--text-2)", marginBottom: 10 }}>
          <div>Change: {(view.amendment && view.amendment.change_made) || "—"} (clause {(view.amendment && view.amendment.section_clause) || "—"})</div>
          <div>Approved by {view.approved_by || "—"} · {inDate((view.approved_at || "").slice(0, 10))}</div>
        </div>
        <div className="card" style={{ margin: 0, boxShadow: "none" }} dangerouslySetInnerHTML={{ __html: view.content || "" }} />
      </div>
    </div>}
  </div>;
}

function GapFixRow({ g, people, signs, onSaved }) {
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(false);
  const isDate = (k) => /date|doj|doa/.test(k);
  const isSig = (k) => /sign/.test(k);
  const sigSource = (k) => (k === "qm_signature" || k === "right_sign") ? signs : people;
  const save = async () => {
    const payload = {};
    Object.keys(vals).forEach(k => { if (!k.endsWith("__id") && vals[k]) payload[k] = vals[k]; });
    if (!Object.keys(payload).length) return;
    setBusy(true);
    try { await api.c4Edit(g.coll, g.id, payload); onSaved(); } catch (e) { alert(e.message); }
    setBusy(false);
  };
  return <div style={{ color: "var(--text-2)", marginTop: 4 }}>
    <div>{g.label} — missing: <span style={{ color: "var(--warning)" }}>{g.missing.join(", ")}</span></div>
    <div className="row" style={{ flexWrap: "wrap", gap: 6, marginTop: 4 }}>
      {g.missing.map(k => isDate(k)
        ? <input key={k} type="date" title={"Add " + k} value={vals[k] || ""} onChange={e => setVals({ ...vals, [k]: e.target.value })} style={{ maxWidth: 150, minHeight: 32, padding: "2px 8px" }} />
        : isSig(k)
          ? <select key={k} title={"Add " + k} value={vals[k + "__id"] || ""} onChange={e => { const src = sigSource(k); const p = src.find(x => x.id === e.target.value); setVals({ ...vals, [k + "__id"]: e.target.value, [k]: p ? p.signature_image : "" }); }} style={{ maxWidth: 190, minHeight: 32, padding: "2px 8px" }}>
              <option value="">{k}: pick signatory…</option>
              {sigSource(k).map(p => <option key={p.id} value={p.id}>{p.name || p.role}</option>)}
            </select>
          : <input key={k} placeholder={k} value={vals[k] || ""} onChange={e => setVals({ ...vals, [k]: e.target.value })} style={{ maxWidth: 150, minHeight: 32, padding: "2px 8px" }} />)}
      <button className="primary" style={{ minHeight: 32, padding: "0 12px" }} disabled={busy} onClick={save}>{busy ? "Saving…" : "Save"}</button>
    </div>
  </div>;
}

function InternalAudit() {
  const { can, user } = useAuth();
  const [audits, setAudits] = useState([]);
  const [bank, setBank] = useState([]);
  const [people, setPeople] = useState([]);
  const [cur, setCur] = useState(null);
  const [form, setForm] = useState(null);
  const load = () => { api.c4List("audit_checklists").then(setAudits); };
  useEffect(() => { load(); api.c4List("audit_question_bank").then(setBank).catch(() => {}); api.personnelPublic().then(setPeople).catch(() => {}); }, []);
  const startNew = () => setForm({ audit_no: "IA-" + new Date().getFullYear() + "-", planned_date: "", auditor: "", auditee: "", scope: "Full management system (ISO/IEC 17025:2017 clauses 4–8)" });
  const create = async () => {
    if (!form.audit_no || !form.auditor) { alert("Audit No. and Auditor are required"); return; }
    const items = bank.sort((a, b) => (a.ref || "").localeCompare(b.ref || "", undefined, { numeric: true }))
      .map(q => ({ ref: q.ref, question: q.question, result: "", remarks: "" }));
    const doc = await api.c4Add("audit_checklists", { ...form, items, status: "in_progress" });
    await api.c4Add("internal_audit_plan", { year: String(new Date().getFullYear()), audit_no: form.audit_no, area: form.scope, auditor: form.auditor, auditee: form.auditee, planned_date: form.planned_date, actual_date: "", status: "In progress" });
    setForm(null); setCur(doc); load();
  };
  const setItem = (i, k, v) => setCur({ ...cur, items: cur.items.map((x, xi) => xi === i ? { ...x, [k]: v } : x) });
  const saveProgress = async () => { await api.c4Edit("audit_checklists", cur.id, cur); load(); alert("Progress saved"); };
  const finish = async () => {
    const un = (cur.items || []).filter(x => !x.result);
    if (un.length) { alert(un.length + " question(s) unanswered. Answer all (use NA where not applicable)."); return; }
    const today = new Date().toISOString().slice(0, 10);
    const ncs = cur.items.filter(x => x.result === "NC");
    const obs = cur.items.filter(x => x.result === "Observation");
    for (const x of [...ncs, ...obs]) {
      await api.c4Add("audit_observation", { audit_no: cur.audit_no, date: today, clause: x.ref, observation: x.remarks || x.question, type: x.result === "NC" ? "NC" : "Observation", auditee: cur.auditee, remarks: "" });
      await api.c4Add("audit_findings", { severity: x.result === "NC" ? "NC" : "OBS", ref: "IA " + cur.audit_no + " · " + x.ref, finding: x.remarks || x.question, recommendation: "Corrective action per 8.7", status: "open", created_at: today, created_by: cur.auditor });
    }
    await api.c4Add("internal_audit_report", { audit_no: cur.audit_no, date: today, scope: cur.scope, summary: "Conforming: " + cur.items.filter(x => x.result === "Conforming").length + ", NC: " + ncs.length + ", Observations: " + obs.length + ", NA: " + cur.items.filter(x => x.result === "NA").length, nc_count: String(ncs.length), auditor: cur.auditor });
    await api.c4Add("audit_summary", { audit_no: cur.audit_no, date: today, total_ncs: String(ncs.length), closed: "0", open: String(ncs.length), remarks: obs.length + " observation(s); corrective actions to be tracked to closure per 8.7" });
    await api.c4Edit("audit_checklists", cur.id, { ...cur, status: "completed", completed_at: today });
    alert("Audit closed: plan, observation sheets, report and summary records created (8.8F-01 to 8.8F-04)." + (ncs.length + obs.length > 0 ? " Findings added to the dashboard register." : ""));
    setCur(null); load();
  };
  const clausesOf = (items) => { const g = {}; (items || []).forEach((x, i) => { const c = (x.ref || "").split(".")[0]; (g[c] = g[c] || []).push([x, i]); }); return g; };
  if (cur) {
    const groups = clausesOf(cur.items);
    const done = (cur.items || []).filter(x => x.result).length;
    return <div className="main main-wide">
      <div className="topbar">
        <h2>Internal audit {cur.audit_no} <span className="badge">{done}/{(cur.items || []).length} answered</span></h2>
        <div className="row">
          <button onClick={() => { setCur(null); load(); }}>Back</button>
          {cur.status !== "completed" && <button onClick={saveProgress}>Save progress</button>}
          {cur.status !== "completed" && <button className="primary" onClick={finish}>Finish & generate records</button>}
        </div>
      </div>
      {Object.keys(groups).map(c => <div key={c} className="card">
        <h3 style={{ margin: "0 0 8px" }}>Clause {c}</h3>
        <table><thead><tr><th style={{ width: "8%" }}>Ref</th><th style={{ width: "42%" }}>Checkpoint</th><th style={{ width: "18%" }}>Result</th><th>Evidence / remarks</th></tr></thead>
          <tbody>{groups[c].map(([x, i]) => <tr key={i}>
            <td>{x.ref}</td><td>{x.question}</td>
            <td><select disabled={cur.status === "completed"} value={x.result} onChange={e => setItem(i, "result", e.target.value)} style={{ minHeight: 34 }}>
              <option value="">—</option><option>Conforming</option><option>NC</option><option>Observation</option><option>NA</option></select></td>
            <td><input disabled={cur.status === "completed"} value={x.remarks || ""} onChange={e => setItem(i, "remarks", e.target.value)} style={{ minHeight: 34 }} /></td>
          </tr>)}</tbody></table>
      </div>)}
    </div>;
  }
  return <div className="main main-wide">
    <div className="topbar">
      <h2>Internal audits</h2>
      {can("clause_documents", "add") && !form && <button className="primary" onClick={startNew}>Start new internal audit</button>}
    </div>
    <div className="pending-note" style={{ background: "var(--info-bg)", color: "var(--info)", borderColor: "transparent", marginBottom: 16 }}>
      Guided clause-by-clause audit per 8.8 (Option A). Answer every checkpoint; on finish the app generates the
      Internal Audit Plan (8.8F-01), Observation Sheets (8.8F-02), Report (8.8F-03) and Summary (8.8F-04), and
      NC/Observation checkpoints flow into the dashboard findings register for corrective action tracking.
    </div>
    {form && <div className="card">
      <label>Audit No.</label><input value={form.audit_no} onChange={e => setForm({ ...form, audit_no: e.target.value })} style={{ maxWidth: 240 }} />
      <label>Planned date</label><input type="date" value={form.planned_date} onChange={e => setForm({ ...form, planned_date: e.target.value })} style={{ maxWidth: 240 }} />
      <label>Auditor (must be independent of the audited activity)</label>
      <select value={form.auditor} onChange={e => setForm({ ...form, auditor: e.target.value })} style={{ maxWidth: 320 }}>
        <option value="">— select —</option>{people.map(p => <option key={p.id} value={p.name}>{p.name}{p.designation ? " (" + p.designation + ")" : ""}</option>)}</select>
      <label>Auditee</label><input value={form.auditee} onChange={e => setForm({ ...form, auditee: e.target.value })} style={{ maxWidth: 320 }} />
      <label>Scope</label><input value={form.scope} onChange={e => setForm({ ...form, scope: e.target.value })} />
      <div className="row" style={{ marginTop: 10 }}><button className="primary" onClick={create}>Create & open checklist</button><button onClick={() => setForm(null)}>Cancel</button></div>
    </div>}
    <div className="card">
      <table><thead><tr><th>Audit No.</th><th>Auditor</th><th>Planned</th><th>Status</th><th></th></tr></thead>
        <tbody>{audits.map(a => <tr key={a.id}>
          <td>{a.audit_no}</td><td>{a.auditor}</td><td>{a.planned_date}</td>
          <td>{a.status === "completed" ? <span className="badge ok">COMPLETED</span> : <span className="badge warn">IN PROGRESS</span>}</td>
          <td style={{ textAlign: "right" }}><button onClick={() => setCur(a)}>{a.status === "completed" ? "View" : "Continue"}</button></td>
        </tr>)}{audits.length === 0 && <tr><td colSpan={5} style={{ color: "var(--text-2)" }}>No internal audits yet.</td></tr>}</tbody></table>
    </div>
  </div>;
}

function ComplianceCalendar() {
  const { can } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [raw, setRaw] = useState([]);
  const [fsheets, setFsheets] = useState([]);
  const [iqc, setIqc] = useState(null);
  const [managing, setManaging] = useState(false);
  const [form, setForm] = useState(null);
  const FREQS = ["daily", "weekly", "monthly", "quarterly", "half-yearly", "yearly"];
  const IQC_COLLS = { replicate_testing: "replicate", retesting_retained: "retest", blind_samples: "blind" };
  const IQC_LABEL = { replicate: "Replicate Testing (7.7F-03)", retest: "Re-testing of Retained Items (7.7F-04)", blind: "Testing of Blind Samples (7.7F-06)" };
  const FREQ_DAYS = { daily: 1, weekly: 7, monthly: 31, quarterly: 92, "half-yearly": 183, yearly: 366 };
  const load = () => { api.compliance().then(r => setTasks(r.tasks || [])).catch(() => {}); api.c4List("compliance_tasks").then(setRaw).catch(() => {}); api.c4List("functional_check_record").then(setFsheets).catch(() => {}); api.iqcStatus(2026).then(setIqc).catch(() => {}); };
  useEffect(() => { load(); }, []);
  const funcCov = (freq) => {
    const win = FREQ_DAYS[freq] || 31;
    const now = Date.now();
    let done = 0, last = null;
    fsheets.forEach(s => {
      const ticks = s.ticks || {};
      let latest = null;
      Object.keys(ticks).forEach(dk => { const has = ticks[dk] && Object.values(ticks[dk]).some(v => v === "Y" || v === "N"); if (!has) return; const dd = new Date(dk); if (!isNaN(dd.getTime()) && (!latest || dd > latest)) latest = dd; });
      if (latest) { if (!last || latest > last) last = latest; if (Math.floor((now - latest.getTime()) / 86400000) < win) done++; }
    });
    const total = fsheets.length;
    const status = total > 0 && done === total ? "ok" : (done > 0 ? "overdue" : "never");
    return { status, done, total, last: last ? last.toISOString().slice(0, 10) : null };
  };
  const statusOf = (t) => t.coll === "functional_check_record" ? funcCov(t.freq).status : t.status;
  const FORMAT_OPTIONS = (() => {
    const out = [];
    Object.keys(CLAUSE_FORMATS).forEach(nn => CLAUSE_FORMATS[nn].forEach(f => out.push({ coll: f.coll, code: f.code || "", title: f.title, clause: (f.section || "").split(" ")[0] })));
    out.sort((a, b) => (a.code || "").localeCompare(b.code || "", undefined, { numeric: true }));
    return out;
  })();
  const badge = (st) => st === "ok" ? <span className="badge ok">DONE / IN WINDOW</span>
    : st === "overdue" ? <span className="badge obsolete">OVERDUE</span>
    : <span className="badge obsolete">NO RECORD YET</span>;
  const fmtLink = (coll) => {
    let entry = null, clauseN = "";
    Object.keys(CLAUSE_FORMATS).forEach(nn => { const f = CLAUSE_FORMATS[nn].find(x => x.coll === coll); if (f) { entry = f; clauseN = nn; } });
    return entry ? <NavLink to={"/clause/" + clauseN + "/" + coll} style={{ color: "var(--info)", textDecoration: "none" }}>{entry.code} — {entry.title}</NavLink> : coll;
  };
  const pickFormat = (coll) => { const f = FORMAT_OPTIONS.find(x => x.coll === coll); setForm({ ...form, coll, title: (form.title || (f ? f.title : "")), clause: (form.clause || (f ? f.clause : "")) }); };
  const startNew = () => setForm({ title: "", coll: "", freq: "monthly", clause: "", is_active: true });
  const save = async () => {
    if (!form.title || !form.coll) { alert("Task title and an evidence format are required."); return; }
    const body = { title: form.title, coll: form.coll, freq: form.freq, clause: form.clause, is_active: form.is_active !== false };
    if (form.id) await api.c4Edit("compliance_tasks", form.id, body); else await api.c4Add("compliance_tasks", body);
    setForm(null); load();
  };
  const del = async (t) => { if (window.confirm("Delete compliance task \"" + t.title + "\"?")) { await api.c4Del("compliance_tasks", t.id); load(); } };
  const open_ = tasks.filter(t => statusOf(t) !== "ok").length;
  const canManage = can("clause_documents", "add") || can("clause_documents", "edit");
  return <div className="main main-wide">
    <div className="topbar">
      <div>
        <h2>NABL compliance calendar {open_ > 0 && <span className="badge obsolete">{open_} due</span>}</h2>
        <div style={{ color: "var(--text-2)", fontSize: 13, marginTop: 2 }}>Status is derived from the actual records in each format — a task shows DONE only when a record exists inside its frequency window. Functional-check tasks require every machine to be checked.</div>
      </div>
      {canManage && <button onClick={() => { setManaging(!managing); setForm(null); }}>{managing ? "Done managing" : "Manage tasks"}</button>}
    </div>
    {managing && <div className="card">
      <div className="row" style={{ justifyContent: "space-between", marginBottom: 8 }}>
        <b>Compliance tasks</b>
        {can("clause_documents", "add") && !form && <button className="primary" onClick={startNew}>+ Add task</button>}
      </div>
      {form && <div style={{ marginBottom: 12, borderBottom: "1px solid var(--border)", paddingBottom: 12 }}>
        <label>Task title</label><input value={form.title || ""} onChange={e => setForm({ ...form, title: e.target.value })} style={{ maxWidth: 420 }} />
        <label>Evidence format (from the Format Manual)</label>
        <select value={form.coll || ""} onChange={e => pickFormat(e.target.value)} style={{ maxWidth: 460 }}><option value="">— select format —</option>{FORMAT_OPTIONS.map(f => <option key={f.coll} value={f.coll}>{(f.code ? f.code + " — " : "") + f.title}</option>)}</select>
        <div className="row" style={{ gap: 12, flexWrap: "wrap", marginTop: 6 }}>
          <div><label>Frequency</label><select value={form.freq || "monthly"} onChange={e => setForm({ ...form, freq: e.target.value })} style={{ minWidth: 160 }}>{FREQS.map(fr => <option key={fr} value={fr}>{fr}</option>)}</select></div>
          <div><label>Clause</label><input value={form.clause || ""} onChange={e => setForm({ ...form, clause: e.target.value })} placeholder="e.g. 7.7" style={{ maxWidth: 140 }} /></div>
          <div><label>Status</label><ActiveToggle value={form.is_active} onChange={v => setForm({ ...form, is_active: v })} /></div>
        </div>
        <div className="row" style={{ marginTop: 12 }}><button className="primary" onClick={save}>Save</button><button onClick={() => setForm(null)}>Cancel</button></div>
      </div>}
      <table><thead><tr><th>Task</th><th>Clause</th><th>Evidence format</th><th>Frequency</th><th>Active</th><th style={{ textAlign: "right" }}></th></tr></thead>
        <tbody>{raw.map(t => <tr key={t.id}>
          <td style={{ fontWeight: 500 }}>{t.title}</td><td>{t.clause}</td><td>{fmtLink(t.coll)}</td><td style={{ textTransform: "capitalize" }}>{t.freq}</td>
          <td><span style={{ color: t.is_active === false ? "var(--text-2)" : "var(--success)" }}>{t.is_active === false ? "Inactive" : "Active"}</span></td>
          <td style={{ whiteSpace: "nowrap", textAlign: "right" }}>{can("clause_documents", "edit") && <button onClick={() => setForm({ ...t })}>Edit</button>}{can("clause_documents", "delete") && <button onClick={() => del(t)} style={{ marginLeft: 6, color: "var(--danger)" }}>Delete</button>}</td>
        </tr>)}
        {raw.length === 0 && <tr><td colSpan={6} style={{ color: "var(--text-2)" }}>No compliance tasks yet. Add one to start tracking.</td></tr>}</tbody></table>
    </div>}
    {iqc && iqc.activities && <div className="card">
      <h3 style={{ margin: "0 0 8px" }}>IQC Plan (7.7.1) — {iqc.year} <span style={{ fontSize: 12, fontWeight: 400, color: "var(--text-2)" }}>· status follows the per-activity plan grid</span></h3>
      <table><thead><tr><th>Activity</th><th>Planned</th><th>Completed</th><th>Overdue</th><th>Status</th></tr></thead>
        <tbody>{iqc.activities.map(a => <tr key={a.activity}>
          <td style={{ fontWeight: 500 }}>{IQC_LABEL[a.activity] || a.activity}</td>
          <td>{a.planned}</td><td>{a.completed}</td><td>{a.overdue}</td>
          <td>{a.overdue > 0 ? <span className="badge obsolete">{a.overdue} overdue</span> : (a.planned > 0 && a.completed >= a.planned ? <span className="badge ok">on track</span> : <span className="badge">{a.completed}/{a.planned}</span>)}</td>
        </tr>)}</tbody></table>
      <div style={{ marginTop: 6 }}><NavLink to="/iqc-plan" style={{ color: "var(--info)", textDecoration: "none" }}>Open IQC Plan →</NavLink></div>
    </div>}
    {FREQS.map(fr => {
      const rows = tasks.filter(t => t.freq === fr && !IQC_COLLS[t.coll]);
      if (!rows.length) return null;
      return <div key={fr} className="card">
        <h3 style={{ margin: "0 0 8px", textTransform: "capitalize" }}>{fr}</h3>
        <table><thead><tr><th>Task</th><th>Clause</th><th>Evidence format</th><th>Last record</th><th>Status</th></tr></thead>
          <tbody>{rows.map(t => {
            const fc = t.coll === "functional_check_record" ? funcCov(t.freq) : null;
            const st = fc ? fc.status : t.status;
            const last = fc ? (fc.last || "—") : (t.last_done || "—");
            return <tr key={t.id}>
              <td style={{ fontWeight: 500 }}>{t.title}{fc ? <span style={{ fontSize: 12, color: "var(--text-2)", fontWeight: 400 }}> · {fc.done}/{fc.total} machines checked</span> : null}</td>
              <td>{t.clause}</td>
              <td>{fmtLink(t.coll)}</td>
              <td>{last}</td>
              <td>{badge(st)}</td>
            </tr>;
          })}</tbody></table>
      </div>;
    })}
    {tasks.length === 0 && !managing && <div className="card">No compliance tasks defined yet.{canManage ? " Click \"Manage tasks\" to add one." : ""}</div>}
  </div>;
}

function IqcPlan() {
  const { can } = useAuth();
  const ACTS = [
    { k: "replicate", label: "Replicate Testing (7.7F-03)" },
    { k: "retest", label: "Re-testing of Retained Items (7.7F-04)" },
    { k: "blind", label: "Testing of Blind Samples (7.7F-06)" },
  ];
  const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const [act, setAct] = useState("replicate");
  const [year, setYear] = useState(2026);
  const [data, setData] = useState(null);
  const [planned, setPlanned] = useState({});
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const load = () => api.iqcPlan(act, year).then(d => { setData(d); setPlanned(JSON.parse(JSON.stringify(d.planned || {}))); setEditing(false); }).catch(() => {});
  useEffect(() => { load(); }, [act, year]);
  const isPlanned = (pid, m) => (planned[pid] || []).includes(m);
  const isDone = (pid, m) => ((data && data.completed[pid]) || []).includes(m);
  const toggle = (pid, m) => { if (!editing) return; const cur = planned[pid] || []; const nx = cur.includes(m) ? cur.filter(x => x !== m) : [...cur, m].sort((a, b) => a - b); setPlanned({ ...planned, [pid]: nx }); };
  const save = async () => { setSaving(true); try { await api.saveIqcPlan({ activity: act, year, planned }); await load(); } catch (e) { alert(e.message); } setSaving(false); };
  const actLabel = (ACTS.find(a => a.k === act) || {}).label || "";
  return <div className="main main-wide">
    <div className="topbar" style={{ display: "block" }}>
      <NavLink to="/clause/7" style={{ fontSize: 13, textDecoration: "none", color: "var(--text-2)" }}>← Clause 7 — Process requirements</NavLink>
      <h2 style={{ marginTop: 4 }}>Internal Quality Control (IQC) Plan <span className="badge">SAN-Lab/IQC-PLAN/7.7.1</span></h2>
    </div>
    <div className="row" style={{ gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
      {ACTS.map(a => <button key={a.k} onClick={() => setAct(a.k)} className={act === a.k ? "primary" : ""}>{a.label}</button>)}
      <div style={{ flex: 1 }} />
      <button onClick={() => setYear(year - 1)}>‹</button>
      <span style={{ alignSelf: "center", fontWeight: 600 }}>Year {year}</span>
      <button onClick={() => setYear(year + 1)}>›</button>
    </div>
    {!data ? <div className="card">Loading…</div> : <div className="card">
      <div className="row" style={{ justifyContent: "space-between", marginBottom: 8 }}>
        <b>{actLabel} — {year}</b>
        <div className="row" style={{ gap: 6 }}>
          {can("clause_documents", "edit") && !editing && <button onClick={() => setEditing(true)}>Edit plan</button>}
          {editing && <button className="primary" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save plan"}</button>}
          {editing && <button onClick={() => { setPlanned(JSON.parse(JSON.stringify(data.planned || {}))); setEditing(false); }}>Cancel</button>}
          {!editing && <button onClick={() => printFormat("iqc-grid")}>Print / PDF</button>}
        </div>
      </div>
      {editing && <div className="pending-note noprint" style={{ marginBottom: 8 }}>Click a month cell to plan (○) or unplan it. Completed cells (●) are filled automatically from the records and can't be toggled here.</div>}
      <div id="iqc-grid" className="fmt-doc" style={{ overflowX: "auto" }}>
        <FormatHeader code="SAN-Lab/IQC-PLAN/7.7.1" title={"IQC Plan — " + actLabel + " (Year " + year + ")"} />
        <table className="fmt-table"><thead><tr><th style={{ width: "4%" }}>Sl</th><th style={{ minWidth: 200 }}>Parameter</th>{MONTHS.map(mn => <th key={mn} style={{ textAlign: "center" }}>{mn}</th>)}</tr></thead>
          <tbody>{data.params.map((p, i) => <tr key={p.id}><td>{i + 1}</td><td style={{ textAlign: "left" }}>{p.name}</td>{MONTHS.map((mn, mi) => { const m = mi + 1; const done = isDone(p.id, m); const pl = isPlanned(p.id, m); return <td key={m} onClick={() => toggle(p.id, m)} style={{ textAlign: "center", cursor: editing ? "pointer" : "default", color: done ? "var(--success)" : "var(--text)", fontSize: 15, background: editing && pl ? "var(--info-bg)" : "transparent" }}>{done ? "●" : pl ? "○" : ""}</td>; })}</tr>)}
            {data.params.length === 0 && <tr><td colSpan={14} style={{ color: "var(--text-2)" }}>No parameters apply to this activity.</td></tr>}</tbody></table>
        <div className="fmt-footer">Legend: ○ Planned · ● Completed (auto-derived from the {actLabel} records). Frequency per master IQC Plan 7.7.1. Prepared by (Technical Manager) — Reviewed by (Quality Manager).</div>
      </div>
    </div>}
  </div>;
}

function AuditLog() {
  const PAGE = 100;
  const [logs, setLogs] = useState([]);
  const [skip, setSkip] = useState(0);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const loadMore = (s) => {
    setBusy(true);
    api.auditLogPage(s, PAGE).then(r => {
      setLogs(l => s === 0 ? r : [...l, ...r]);
      setDone(r.length < PAGE);
      setSkip(s + PAGE);
    }).catch(() => {}).finally(() => setBusy(false));
  };
  useEffect(() => { loadMore(0); }, []);
  return <div className="main">
    <div className="topbar"><h2>Audit log</h2></div>
    <div className="card">
      <table><thead><tr><th>When</th><th>User</th><th>Action</th><th>Area</th><th>Details</th></tr></thead><tbody>
        {logs.map(l => <tr key={l.id}>
          <td>{(l.timestamp || "").slice(0, 16).replace("T", " ")}</td>
          <td>{l.username}</td><td>{l.action}</td><td>{l.area}</td><td>{l.summary}</td>
        </tr>)}
      </tbody></table>
      {!done && <button onClick={() => loadMore(skip)} disabled={busy} style={{ marginTop: 10 }}>{busy ? "Loading…" : "Load older entries"}</button>}
    </div>
  </div>;
}


function ClauseIndex({ n }) {
  const fmts = CLAUSE_FORMATS[String(n)] || [];
  const [counts, setCounts] = useState({});
  useEffect(() => { api.readiness().then(r => setCounts(r.counts || {})).catch(() => {}); }, [n]);
  const sections = [];
  fmts.forEach(f => { let s = sections.find(x => x.section === f.section); if (!s) { s = { section: f.section, items: [] }; sections.push(s); } s.items.push(f); });
  const filled = fmts.filter(f => f.lims || f.system || f.tally || (counts[f.coll] || 0) > 0).length;
  return <div className="main main-wide">
    <div className="topbar">
      <h2>Clause {n} — {CLAUSE_TITLES[String(n)] || ""}</h2>
      <span className={"badge " + (filled === fmts.length && fmts.length ? "ok" : "warn")}>{filled}/{fmts.length} formats with records</span>
    </div>
    {CLAUSE_NOTES[String(n)] && <div className="pending-note" style={{ marginBottom: 16 }}>{CLAUSE_NOTES[String(n)]}</div>}
    {fmts.length === 0 && <div className="card">No formats for this clause yet.</div>}
    {sections.map(s => <div key={s.section} style={{ marginBottom: 16 }}>
      <h3>{s.section}</h3>
      <div className="row" style={{ flexWrap: "wrap", gap: 12, alignItems: "stretch" }}>
        {s.items.map(f => { const c = counts[f.coll] || 0; return <NavLink key={f.coll} to={"/clause/" + n + "/" + f.coll} className="card fmt-card" style={{ textDecoration: "none", color: "inherit", minWidth: 240, flex: "1 1 240px", maxWidth: 340, margin: 0 }}>
          <div className="row" style={{ justifyContent: "space-between" }}>
            {f.code && <span className="badge">{f.code}</span>}
            {f.lims ? <span className="badge">Maintained in LIMS</span> : f.system ? <span className="badge">Auto-maintained by QMS</span> : f.tally ? <span className="badge">Maintained in Tally</span> : <span className={"badge " + (c > 0 ? "ok" : "obsolete")}>{c > 0 ? c + " record" + (c === 1 ? "" : "s") : "empty"}</span>}
          </div>
          <div style={{ marginTop: 8, fontWeight: 600 }}>{f.title}</div>
          {f.lims ? <div style={{ marginTop: 4, fontSize: 12, color: "var(--info)" }}>operational record kept in the LIMS — register here only for exceptions</div>
            : f.system ? <div style={{ marginTop: 4, fontSize: 12, color: "var(--info)" }}>maintained automatically by the QMS (version history / obsolete documents)</div>
            : f.tally ? <div style={{ marginTop: 4, fontSize: 12, color: "var(--info)" }}>purchase orders raised and held in Tally — register here only for exceptions</div>
            : f.pending && <div style={{ marginTop: 4, fontSize: 12, color: "var(--warning)" }}>provisional format — awaiting soft copy</div>}
        </NavLink>; })}
        {n === "7" && (s.section || "").startsWith("7.7 ") && <NavLink to="/iqc-plan" className="card fmt-card" style={{ textDecoration: "none", color: "inherit", minWidth: 240, flex: "1 1 240px", maxWidth: 340, margin: 0 }}>
          <div className="row" style={{ justifyContent: "space-between" }}><span className="badge">7.7.1</span><span className="badge ok">plan</span></div>
          <div style={{ marginTop: 8, fontWeight: 600 }}>IQC Plan — Replicate / Retesting / Blind</div>
          <div style={{ marginTop: 4, fontSize: 12, color: "var(--info)" }}>monthly plan grid per activity; cells auto-tick from the records you fill</div>
        </NavLink>}
      </div>
    </div>)}
  </div>;
}

function SyncFromTests({ coll, onDone }) {
  const { can } = useAuth();
  const [busy, setBusy] = useState(false);
  if (!can("clause_documents", "add")) return null;
  const run = async () => {
    setBusy(true);
    try {
      const [tests, existing] = await Promise.all([api.c4List("test_master"), api.c4List(coll)]);
      let added = 0;
      if (coll === "test_standard_file") {
        const have = new Set(existing.map(r => (r.standard_no || "").trim().toLowerCase()));
        const seen = new Set();
        for (const t of tests) {
          const sn = (t.standard_no || "").trim();
          if (!sn || have.has(sn.toLowerCase()) || seen.has(sn.toLowerCase())) continue;
          seen.add(sn.toLowerCase());
          await api.c4Add(coll, { standard_no: sn, title: t.standard_title || t.test || "", version: "", discipline: t.discipline || "", status: "Current", remarks: "" });
          added++;
        }
      } else if (coll === "mu_evaluation") {
        const have = new Set(existing.map(r => (r.parameter || "").trim().toLowerCase()));
        for (const t of tests) {
          const p = (t.test || "").trim();
          if (!p || have.has(p.toLowerCase()) || t.nabl_scope === "No") continue;
          await api.c4Add(coll, { parameter: p, method: t.standard_no || "", mu: "", k: "2", date: "", evaluated_by: "", remarks: "" });
          added++;
        }
      }
      alert(added + " row(s) generated from the Test Master." + (added === 0 ? " (Everything already present, or the Test Master is empty — fill it under Masters → Tests & Standards.)" : ""));
      onDone();
    } catch (e) { alert(e.message); }
    setBusy(false);
  };
  return <button onClick={run} disabled={busy} style={{ marginBottom: 10 }}>{busy ? "Generating…" : "Generate rows from Test Master"}</button>;
}

// A format code (e.g. 8.3F-01) that holds more than one register. Each sub-table gets its own
// button; clicking a button opens that register. Only the active one is mounted, so switching
// reloads fresh data for that collection.
function TabbedFormatTables({ entry }) {
  const tables = entry.tables || [];
  const [tab, setTab] = useState(0);
  const t = tables[tab] || tables[0];
  if (!t) return null;
  return <>
    <div className="row noprint" style={{ gap: 8, marginBottom: 12 }}>
      {tables.map((tb, i) => <button key={tb.coll || i} className={tab === i ? "primary" : ""} onClick={() => setTab(i)} style={{ minWidth: 120 }}>{tb.tabLabel || tb.title}</button>)}
    </div>
    <FormatTable key={t.coll} code={entry.code} title={t.title} coll={t.coll}
      columns={t.columns || []} footer={t.footer || ""} headerGroups={t.headerGroups}
      userFillable={t.userFillable} approvalAfter={t.approvalAfter} approvalLabel={t.approvalLabel} />
  </>;
}

function GenericFormatPage({ n, entry }) {
  const { can } = useAuth();
  const [accepted, setAccepted] = useState(null);
  useEffect(() => { api.acceptedFormats().then(r => setAccepted(r.codes || [])).catch(() => setAccepted([])); }, []);
  const isAccepted = !!(accepted && entry.code && accepted.includes(entry.code));
  const showPending = entry.pending && !entry.lims && !entry.system && !entry.tally && !isAccepted;
  const setAccept = async (val) => { try { const r = await api.setAcceptedFormat(entry.code, val); setAccepted(r.codes || []); } catch (e) { alert(e.message); } };
  return <div className="main main-wide">
    <FormatTopbar n={n} only={entry.coll} />
    {entry.lims && <div className="pending-note" style={{ background: "var(--info-bg)", color: "var(--info)", borderColor: "transparent" }}>
      This record is maintained day-to-day in the laboratory's LIMS, which is the system of record for it.
      During an audit, present it from the LIMS. Use this register only for exceptions or extracts you want under the QMS.
    </div>}
    {entry.system && <div className="pending-note" style={{ background: "var(--info-bg)", color: "var(--info)", borderColor: "transparent" }}>
      This record is maintained automatically by the QMS: superseded document versions appear under Obsolete documents,
      and every signed change appears in each manual's Amendment Record and version history. Use this register only for
      documents controlled outside the software.
    </div>}
    {entry.tally && <div className="pending-note" style={{ background: "var(--info-bg)", color: "var(--info)", borderColor: "transparent" }}>
      Purchase orders are raised and maintained in Tally (accounts system), which is the system of record for purchasing.
      Use this register only for exceptions or extracts you want under the QMS.
    </div>}
    {showPending && <div className="pending-note">
      This format is a structural placeholder generated from the Quality Manual's List of Records.
      The columns are provisional — they will be replaced with the laboratory's own soft-copy format when it is loaded.
      {can("clause_documents", "edit") && <div style={{ marginTop: 8 }}><button onClick={() => setAccept(true)}>Accept this format</button></div>}
    </div>}
    {entry.pending && !entry.lims && !entry.system && !entry.tally && isAccepted && can("clause_documents", "edit") &&
      <div className="noprint" style={{ fontSize: 12, color: "var(--text-2)", marginBottom: 8 }}>Format accepted ✓ <button style={{ marginLeft: 8 }} onClick={() => setAccept(false)}>Revert to placeholder</button></div>}
    {entry.kind === "formdoc"
      ? <FormatFormDoc code={entry.code} title={entry.title} coll={entry.coll}
        fields={(entry.formdoc && entry.formdoc.fields) || []}
        intro={entry.formdoc && entry.formdoc.intro}
        innerTable={entry.formdoc && entry.formdoc.innerTable}
        notes={entry.formdoc && entry.formdoc.notes}
        postFields={entry.formdoc && entry.formdoc.postFields}
        formFirst={entry.formdoc && entry.formdoc.formFirst}
        userFillable={entry.userFillable || (entry.formdoc && entry.formdoc.userFillable)}
        signatures={(entry.formdoc && entry.formdoc.signatures) || []} />
      : entry.tables
      ? <TabbedFormatTables entry={entry} />
      : <><span key={entry.coll + "-sync"}>{(entry.coll === "test_standard_file" || entry.coll === "mu_evaluation") && <SyncFromTests coll={entry.coll} onDone={() => window.location.reload()} />}</span>
        <FormatTable code={entry.code} title={entry.title} coll={entry.coll}
        columns={entry.columns || []} footer={entry.footer || ""} headerGroups={entry.headerGroups} userFillable={entry.userFillable} approvalAfter={entry.approvalAfter} approvalLabel={entry.approvalLabel} /></>}
  </div>;
}

// 8.8F-01 Internal Audit Plan — the lab's printed month-by-function grid. Each saved record is one
// audit cycle/year; Previous/Next page through them, and the Quality Manager signs digitally.
const IAP_MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const IAP_FUNCS = ["Quality Manager", "Technical Manager & Testing Engineer", "Manager Customer Support"];
const iapKey = (mi, fi, kind) => mi + "_" + fi + "_" + kind;

function InternalAuditPlan() {
  const emptyForm = () => ({ cycle_no: "", year: "", cells: {}, qm_signature: "", qm_signature_id: "", qm_date: "" });
  const [form, setForm] = useState(emptyForm());
  const [items, setItems] = useState([]);
  const [people, setPeople] = useState([]);
  const [idx, setIdx] = useState(0);
  const recs = [...items].reverse();          // API returns newest-first; page oldest-first
  const total = recs.length;
  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setCell = (mi, fi, kind, v) => setForm(f => ({ ...f, cells: { ...f.cells, [iapKey(mi, fi, kind)]: v } }));
  const fillForm = (rec) => setForm(rec ? { ...emptyForm(), ...rec, cells: rec.cells || {} } : emptyForm());
  const load = async () => { const list = await api.c4List("internal_audit_plan"); setItems(list); return list; };
  useEffect(() => { load().then(list => { setIdx(list.length); fillForm(null); }); api.personnelPublic().then(setPeople).catch(() => {}); }, []);
  const goTo = (i) => { const r = [...items].reverse(); const c = Math.max(0, Math.min(r.length, i)); setIdx(c); fillForm(c < r.length ? r[c] : null); };
  const newEntry = () => { setIdx(items.length); fillForm(null); };
  const resetCurrent = () => fillForm(idx < total ? recs[idx] : null);
  const save = async () => {
    try {
      const saved = form.id ? await api.c4Edit("internal_audit_plan", form.id, form) : await api.c4Add("internal_audit_plan", form);
      const list = await load();
      const r = [...list].reverse();
      const savedId = (saved && saved.id) || form.id;
      const pos = savedId ? r.findIndex(x => x.id === savedId) : -1;
      const at = pos >= 0 ? pos : Math.max(0, r.length - 1);
      setIdx(at); fillForm(r[at]);
      window.alert("Saved.");
    } catch (e) { window.alert("Save failed: " + ((e && e.message) || e)); }
  };
  // printFormat copies innerHTML; React holds input values on the DOM property, not the attribute —
  // mirror them so the printout isn't blank.
  const printForm = () => {
    const el = document.getElementById("fmt-iap");
    if (el) el.querySelectorAll("input").forEach(inp => inp.setAttribute("value", inp.value));
    printFormat("fmt-iap");
  };
  const ci = { width: "100%", border: "none", background: "transparent", minHeight: 26, padding: "2px 4px", boxSizing: "border-box", textAlign: "center" };
  const hf = { border: "none", borderBottom: "1px solid var(--border-strong)", background: "transparent", padding: "1px 4px" };
  return <div className="main main-wide">
    <FormatTopbar n="8" only="internal_audit_plan" />
    <div className="card">
      <div className="row" style={{ justifyContent: "space-between", marginBottom: 8 }}>
        <b>Internal Audit Plan <span className="badge">8.8F-01</span>
          <span style={{ marginLeft: 8, fontSize: 12, color: "var(--text-2)" }}>{idx < total ? `Record ${idx + 1} of ${total}` : (total ? `New entry · ${total} saved` : "New entry")}</span></b>
        <div className="row">
          <button className="noprint" onClick={() => goTo(idx - 1)} disabled={idx === 0} title="Previous record">◀ Previous</button>
          <button className="noprint" onClick={() => goTo(idx + 1)} disabled={idx >= total} title="Next record">Next ▶</button>
          <button className="noprint" onClick={newEntry} title="Start a new blank plan">New</button>
          <button className="primary" onClick={save}>Save</button>
          <button className="noprint" onClick={resetCurrent} title="Discard unsaved changes">Clear</button>
          <button onClick={printForm}>Print / PDF</button>
        </div>
      </div>
      <div id="fmt-iap" className="fmt-doc" style={{ overflowX: "auto" }}>
        <FormatHeader code="8.8F-01" title="Internal Audit Plan" />
        <div className="row" style={{ justifyContent: "space-between", margin: "12px 0", gap: 12, flexWrap: "wrap" }}>
          <div><b>INTERNAL AUDIT CYCLE NO.:</b> <input value={form.cycle_no} onChange={e => setF("cycle_no", e.target.value)} style={{ ...hf, width: 90 }} /></div>
          <div><b>YEAR:</b> <input value={form.year} onChange={e => setF("year", e.target.value)} style={{ ...hf, width: 90 }} /></div>
        </div>
        <table className="fmt-table">
          <thead>
            <tr>
              <th rowSpan={3} style={{ width: 100 }}>Month</th>
              <th colSpan={6} style={{ textAlign: "center" }}>Function to be audited</th>
            </tr>
            <tr>{IAP_FUNCS.map((fn, fi) => <th key={fi} colSpan={2} style={{ textAlign: "center" }}>{fn}</th>)}</tr>
            <tr>{IAP_FUNCS.map((fn, fi) => [
              <th key={fi + "p"} style={{ textAlign: "center" }}>Plan Date of Audit</th>,
              <th key={fi + "a"} style={{ textAlign: "center" }}>Actual Date of Audit</th>,
            ])}</tr>
          </thead>
          <tbody>{IAP_MONTHS.map((mo, mi) => <tr key={mi}>
            <td style={{ fontWeight: 600 }}>{mo}</td>
            {IAP_FUNCS.map((fn, fi) => [
              <td key={fi + "p"}><input value={form.cells[iapKey(mi, fi, "plan")] || ""} onChange={e => setCell(mi, fi, "plan", e.target.value)} style={ci} /></td>,
              <td key={fi + "a"}><input value={form.cells[iapKey(mi, fi, "actual")] || ""} onChange={e => setCell(mi, fi, "actual", e.target.value)} style={ci} /></td>,
            ])}
          </tr>)}</tbody>
        </table>
        <div style={{ marginTop: 28, display: "flex", justifyContent: "flex-end" }}>
          <div style={{ textAlign: "center", minWidth: 220 }}>
            <PersonSignature label="(Quality Manager)" people={people} value={form.qm_signature} valueId={form.qm_signature_id} onPick={(id, sig) => setForm(f => ({ ...f, qm_signature_id: id, qm_signature: sig }))} minWidth={220} />
            <div style={{ marginTop: 4 }}>Date: <input value={form.qm_date} onChange={e => setF("qm_date", e.target.value)} style={{ ...hf, width: 130 }} /></div>
          </div>
        </div>
      </div>
    </div>
  </div>;
}

// 8.8F-04 Internal Audit Summary — the lab's printed NC-summary form: header fields, a
// nonconformance table, and the Quality Manager's digital signature. Previous/Next page saved records.
const IAS_COLS = [
  ["nc_no", "NC No."],
  ["clause_no", "Clause No. (ISO 17025)"],
  ["nc_details", "Nonconformance Details"],
  ["actions_plan", "Actions Decided and Plan date of completion"],
  ["actual_completion", "Actual Date of Completion of Action"],
  ["remarks", "Remarks"],
];

// Textarea that grows to fit its content (no fixed height, no internal scrollbar) so the full
// paragraph is visible on screen. Height re-fits whenever the value changes (typing or when a
// different saved record is loaded). Print is handled separately in printFormat (control -> text).
function AutoTextarea({ value, onChange, style, ...rest }) {
  const ref = React.useRef(null);
  const fit = () => { const el = ref.current; if (el) { el.style.height = "auto"; el.style.height = el.scrollHeight + "px"; } };
  React.useLayoutEffect(fit, [value]);
  return <textarea ref={ref} value={value} onChange={onChange} style={{ ...style, overflow: "hidden", resize: "none" }} {...rest} />;
}

function InternalAuditSummary() {
  const blankRows = (n) => Array.from({ length: n }, () => ({}));
  const emptyForm = () => ({ audit_no: "", area_of_audit: "", dates_of_audit: "", auditor_name: "", rows: blankRows(8), qm_signature: "", qm_signature_id: "", qm_date: "" });
  const [form, setForm] = useState(emptyForm());
  const [items, setItems] = useState([]);
  const [people, setPeople] = useState([]);
  const [idx, setIdx] = useState(0);
  const recs = [...items].reverse();
  const total = recs.length;
  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setCell = (i, k, v) => setForm(f => ({ ...f, rows: f.rows.map((r, ri) => ri === i ? { ...r, [k]: v } : r) }));
  const addRow = () => setForm(f => ({ ...f, rows: [...f.rows, {}] }));
  const delRow = (i) => setForm(f => ({ ...f, rows: f.rows.filter((_, ri) => ri !== i) }));
  const isBlankRow = (r) => !IAS_COLS.some(c => String(r[c[0]] || "").trim() !== "");
  const fillForm = (rec) => setForm(rec ? { ...emptyForm(), ...rec, rows: (rec.rows && rec.rows.length ? rec.rows : blankRows(8)) } : emptyForm());
  const load = async () => { const list = await api.c4List("audit_summary"); setItems(list); return list; };
  useEffect(() => { load().then(list => { setIdx(list.length); fillForm(null); }); api.personnelPublic().then(setPeople).catch(() => {}); }, []);
  const goTo = (i) => { const r = [...items].reverse(); const c = Math.max(0, Math.min(r.length, i)); setIdx(c); fillForm(c < r.length ? r[c] : null); };
  const newEntry = () => { setIdx(items.length); fillForm(null); };
  const resetCurrent = () => fillForm(idx < total ? recs[idx] : null);
  const save = async () => {
    const body = { ...form, rows: form.rows.filter(r => !isBlankRow(r)) };
    try {
      const saved = form.id ? await api.c4Edit("audit_summary", form.id, body) : await api.c4Add("audit_summary", body);
      const list = await load();
      const r = [...list].reverse();
      const savedId = (saved && saved.id) || form.id;
      const pos = savedId ? r.findIndex(x => x.id === savedId) : -1;
      const at = pos >= 0 ? pos : Math.max(0, r.length - 1);
      setIdx(at); fillForm(r[at]);
      window.alert("Saved.");
    } catch (e) { window.alert("Save failed: " + ((e && e.message) || e)); }
  };
  // Delete the whole current page/record (reuses the c4Del API), with confirmation.
  const deletePage = async () => {
    const rec = idx < total ? recs[idx] : null;
    if (!rec || !rec.id) { window.alert("This is a new blank entry — nothing to delete."); return; }
    if (!window.confirm("Are you sure you want to delete this page?\n\nThis action cannot be undone.")) return;
    try {
      await api.c4Del("audit_summary", rec.id);
      const list = await load(); const r = [...list].reverse();
      const at = Math.min(idx, r.length);
      setIdx(at); fillForm(at < r.length ? r[at] : null);
    } catch (e) { window.alert("Delete failed: " + ((e && e.message) || e)); }
  };
  const printForm = () => {
    const el = document.getElementById("fmt-ias");
    if (el) {
      el.querySelectorAll("input").forEach(inp => inp.setAttribute("value", inp.value));
      el.querySelectorAll("textarea").forEach(t => { t.textContent = t.value; });
    }
    printFormat("fmt-ias");
  };
  const ci = { width: "100%", border: "none", background: "transparent", minHeight: 44, padding: "2px 4px", boxSizing: "border-box", resize: "vertical" };
  const hf = { border: "none", borderBottom: "1px solid var(--border-strong)", background: "transparent", padding: "1px 4px" };
  return <div className="main main-wide">
    <FormatTopbar n="8" only="audit_summary" />
    <div className="card">
      <div className="row" style={{ justifyContent: "space-between", marginBottom: 8 }}>
        <b>Internal Audit Summary <span className="badge">8.8F-04</span>
          <span style={{ marginLeft: 8, fontSize: 12, color: "var(--text-2)" }}>{idx < total ? `Record ${idx + 1} of ${total}` : (total ? `New entry · ${total} saved` : "New entry")}</span></b>
        <div className="row">
          <button className="noprint" onClick={() => goTo(idx - 1)} disabled={idx === 0} title="Previous record">◀ Previous</button>
          <button className="noprint" onClick={() => goTo(idx + 1)} disabled={idx >= total} title="Next record">Next ▶</button>
          <button className="noprint" onClick={newEntry} title="Start a new blank summary">New</button>
          <button className="primary" onClick={save}>Save</button>
          <button className="noprint" onClick={resetCurrent} title="Discard unsaved changes">Clear</button>
          {idx < total && <button className="noprint" onClick={deletePage} title="Delete this page/record" style={{ color: "#fff", background: "var(--danger)", borderColor: "var(--danger)" }}>🗑 Delete Page</button>}
          <button onClick={printForm}>Print / PDF</button>
        </div>
      </div>
      <div id="fmt-ias" className="fmt-doc" style={{ overflowX: "auto" }}>
        <FormatHeader code="8.8F-04" title="Internal Audit Summary" />
        <div style={{ margin: "12px 0", lineHeight: 2 }}>
          <div className="row" style={{ gap: 18, flexWrap: "wrap", alignItems: "baseline" }}>
            <span><b>Audit No.</b> <input value={form.audit_no} onChange={e => setF("audit_no", e.target.value)} style={{ ...hf, width: 120 }} /></span>
            <span><b>Area of Audit</b> <input value={form.area_of_audit} onChange={e => setF("area_of_audit", e.target.value)} style={{ ...hf, width: 220 }} /></span>
            <span><b>Date(s) of Audit</b> <input value={form.dates_of_audit} onChange={e => setF("dates_of_audit", e.target.value)} style={{ ...hf, width: 180 }} /></span>
          </div>
          <div style={{ marginTop: 6 }}><b>Auditor's Name:</b> <input value={form.auditor_name} onChange={e => setF("auditor_name", e.target.value)} style={{ ...hf, width: 340 }} /></div>
        </div>
        <table className="fmt-table">
          <thead><tr><th style={{ width: 60 }}>NC No.</th>{IAS_COLS.slice(1).map(c => <th key={c[0]}>{c[1]}</th>)}<th className="noprint" style={{ width: 34 }}></th></tr></thead>
          <tbody>{form.rows.map((r, i) => <tr key={i}>
            {IAS_COLS.map(c => <td key={c[0]} style={{ verticalAlign: "top" }}><AutoTextarea rows={2} value={r[c[0]] || ""} onChange={e => setCell(i, c[0], e.target.value)} style={{ ...ci, minWidth: c[0] === "nc_no" ? 40 : 90 }} /></td>)}
            <td className="noprint" style={{ textAlign: "center" }}><button onClick={() => delRow(i)} title="Remove row" style={{ padding: "2px 6px" }}>✕</button></td>
          </tr>)}</tbody>
        </table>
        <div style={{ marginTop: 28, display: "flex", justifyContent: "flex-end" }}>
          <div style={{ textAlign: "center", minWidth: 220 }}>
            <PersonSignature label="(Quality Manager)" people={people} value={form.qm_signature} valueId={form.qm_signature_id} onPick={(id, sig) => setForm(f => ({ ...f, qm_signature_id: id, qm_signature: sig }))} minWidth={220} />
            <div style={{ marginTop: 4 }}>Date: <input value={form.qm_date} onChange={e => setF("qm_date", e.target.value)} style={{ ...hf, width: 130 }} /></div>
          </div>
        </div>
      </div>
    </div>
  </div>;
}

// A signature slot: pick a person from the personnel master and their stored signature shows in the
// cell (prints). The picker is hidden on print; a caption line sits beneath.
// Thin alias kept for the Internal Audit Report call sites; the canonical implementation
// (person-select → auto-fill saved signature) now lives in formatKit's PersonSignature.
function IARSig({ label, value, valueId, people, onPick }) {
  return <PersonSignature label={label} value={value} valueId={valueId} people={people} onPick={onPick} minWidth={160} />;
}

// 8.8F-03 Internal Audit Report — the lab's per-NC report (one record each): header fields, the
// non-conformance / corrective-action sections, and digital signatures. Previous/Next page saved reports.
function InternalAuditReport() {
  const emptyForm = () => ({
    audit_no: "", area_of_audit: "", nc_no: "", date: "", auditor_name: "", auditee_name: "",
    nc_observed: "", clause_no: "", sig_auditee1: "", sig_auditor1: "",
    agreed_action: "", target_date: "", sig_auditee2: "", date_agreed: "",
    actual_action: "", verification: "",
    sig_auditor_final: "", date_auditor: "", sig_qm: "", date_qm: "",
  });
  const [form, setForm] = useState(emptyForm());
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [people, setPeople] = useState([]);
  const recs = [...items].reverse();
  const total = recs.length;
  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));
  // Pick a person → store both their id (to keep the selection) and their master signature.
  const sigPick = (key, label) => <IARSig label={label} people={people} value={form[key]} valueId={form[key + "_id"]} onPick={(id, sig) => setForm(f => ({ ...f, [key + "_id"]: id, [key]: sig }))} />;
  const fillForm = (rec) => setForm(rec ? { ...emptyForm(), ...rec } : emptyForm());
  const load = async () => { const list = await api.c4List("internal_audit_report"); setItems(list); return list; };
  useEffect(() => { load().then(list => { setIdx(list.length); fillForm(null); }); api.personnelPublic().then(setPeople).catch(() => {}); }, []);
  const goTo = (i) => { const r = [...items].reverse(); const c = Math.max(0, Math.min(r.length, i)); setIdx(c); fillForm(c < r.length ? r[c] : null); };
  const newEntry = () => { setIdx(items.length); fillForm(null); };
  const resetCurrent = () => fillForm(idx < total ? recs[idx] : null);
  const save = async () => {
    try {
      const saved = form.id ? await api.c4Edit("internal_audit_report", form.id, form) : await api.c4Add("internal_audit_report", form);
      const list = await load();
      const r = [...list].reverse();
      const savedId = (saved && saved.id) || form.id;
      const pos = savedId ? r.findIndex(x => x.id === savedId) : -1;
      const at = pos >= 0 ? pos : Math.max(0, r.length - 1);
      setIdx(at); fillForm(r[at]);
      window.alert("Saved.");
    } catch (e) { window.alert("Save failed: " + ((e && e.message) || e)); }
  };
  const printForm = () => {
    const el = document.getElementById("fmt-iar");
    if (el) {
      el.querySelectorAll("input").forEach(inp => inp.setAttribute("value", inp.value));
      el.querySelectorAll("textarea").forEach(t => { t.textContent = t.value; });
    }
    printFormat("fmt-iar");
  };
  const hf = { border: "none", borderBottom: "1px solid var(--border-strong)", background: "transparent", padding: "1px 4px" };
  const ta = { width: "100%", border: "none", background: "transparent", padding: "4px", boxSizing: "border-box", resize: "vertical" };
  const lblBox = { fontWeight: 600, background: "var(--surface-2)" };
  return <div className="main main-wide">
    <FormatTopbar n="8" only="internal_audit_report" />
    <div className="card">
      <div className="row" style={{ justifyContent: "space-between", marginBottom: 8 }}>
        <b>Internal Audit Report <span className="badge">8.8F-03</span>
          <span style={{ marginLeft: 8, fontSize: 12, color: "var(--text-2)" }}>{idx < total ? `Record ${idx + 1} of ${total}` : (total ? `New entry · ${total} saved` : "New entry")}</span></b>
        <div className="row">
          <button className="noprint" onClick={() => goTo(idx - 1)} disabled={idx === 0} title="Previous record">◀ Previous</button>
          <button className="noprint" onClick={() => goTo(idx + 1)} disabled={idx >= total} title="Next record">Next ▶</button>
          <button className="noprint" onClick={newEntry} title="Start a new blank report">New</button>
          <button className="primary" onClick={save}>Save</button>
          <button className="noprint" onClick={resetCurrent} title="Discard unsaved changes">Clear</button>
          <button onClick={printForm}>Print / PDF</button>
        </div>
      </div>
      <div id="fmt-iar" className="fmt-doc" style={{ overflowX: "auto" }}>
        <FormatHeader code="8.8F-03" title="Internal Audit Report" />
        <div style={{ margin: "12px 0", lineHeight: 2.1 }}>
          <div className="row" style={{ gap: 16, flexWrap: "wrap", alignItems: "baseline" }}>
            <span><b>Audit No.</b> <input value={form.audit_no} onChange={e => setF("audit_no", e.target.value)} style={{ ...hf, width: 90 }} /></span>
            <span><b>Area of Audit</b> <input value={form.area_of_audit} onChange={e => setF("area_of_audit", e.target.value)} style={{ ...hf, width: 200 }} /></span>
            <span><b>NC No.</b> <input value={form.nc_no} onChange={e => setF("nc_no", e.target.value)} style={{ ...hf, width: 90 }} /></span>
            <span><b>Date:</b> <input value={form.date} onChange={e => setF("date", e.target.value)} style={{ ...hf, width: 120 }} /></span>
          </div>
          <div className="row" style={{ gap: 16, flexWrap: "wrap", alignItems: "baseline", marginTop: 4 }}>
            <span><b>Auditor's Name:</b> <input value={form.auditor_name} onChange={e => setF("auditor_name", e.target.value)} style={{ ...hf, width: 240 }} /></span>
            <span><b>Auditee's Name:</b> <input value={form.auditee_name} onChange={e => setF("auditee_name", e.target.value)} style={{ ...hf, width: 240 }} /></span>
          </div>
        </div>
        <table className="fmt-table">
          <tbody>
            <tr>
              <td style={lblBox}>Non-Conformance Observed:</td>
              <td style={{ ...lblBox, width: "38%" }}>Clause No: <input value={form.clause_no} onChange={e => setF("clause_no", e.target.value)} style={{ ...hf, width: 130 }} /></td>
            </tr>
            <tr><td colSpan={2}><textarea rows={5} value={form.nc_observed} onChange={e => setF("nc_observed", e.target.value)} style={ta} /></td></tr>
            <tr>
              <td>{sigPick("sig_auditee1", "Auditee's Sign.")}</td>
              <td>{sigPick("sig_auditor1", "Auditor's Sign.")}</td>
            </tr>
            <tr>
              <td style={lblBox}>Agreed Corrective Action:</td>
              <td style={lblBox}>Target Date: <input value={form.target_date} onChange={e => setF("target_date", e.target.value)} style={{ ...hf, width: 130 }} /></td>
            </tr>
            <tr>
              <td><textarea rows={5} value={form.agreed_action} onChange={e => setF("agreed_action", e.target.value)} style={ta} /></td>
              <td style={{ width: "38%", verticalAlign: "bottom" }}>{sigPick("sig_auditee2", "Auditee's Sign.")}</td>
            </tr>
            <tr><td style={lblBox}>Actual Corrective Action Taken:</td><td style={lblBox}>Date: <input value={form.date_agreed} onChange={e => setF("date_agreed", e.target.value)} style={{ ...hf, width: 130 }} /></td></tr>
            <tr><td colSpan={2}><textarea rows={5} value={form.actual_action} onChange={e => setF("actual_action", e.target.value)} style={ta} /></td></tr>
            <tr><td colSpan={2} style={lblBox}>Verification of Action Taken:</td></tr>
            <tr><td colSpan={2}><textarea rows={5} value={form.verification} onChange={e => setF("verification", e.target.value)} style={ta} /></td></tr>
          </tbody>
        </table>
        <div className="row" style={{ justifyContent: "space-between", marginTop: 28, gap: 24, flexWrap: "wrap" }}>
          <div>{sigPick("sig_auditor_final", "(Auditor)")}<div style={{ textAlign: "center", marginTop: 2 }}>Date: <input value={form.date_auditor} onChange={e => setF("date_auditor", e.target.value)} style={{ ...hf, width: 120 }} /></div></div>
          <div>{sigPick("sig_qm", "(Quality Manager)")}<div style={{ textAlign: "center", marginTop: 2 }}>Date: <input value={form.date_qm} onChange={e => setF("date_qm", e.target.value)} style={{ ...hf, width: 120 }} /></div></div>
        </div>
      </div>
    </div>
  </div>;
}

function ClausePage() {
  const { n, coll } = useParams();
  if (!coll) return <ClauseIndex n={n} />;
  const entry = (CLAUSE_FORMATS[String(n)] || []).find(f => f.coll === coll);
  if (entry && entry.kind !== "custom") return <GenericFormatPage n={n} entry={entry} />;
  if (coll === "customer_feedback") return <CustomerFeedback />;   // custom, before clause-number routing
  if (n === "4") return <Clause4 only={coll} />;
  if (n === "5" && coll === "org_chart") return <OrgChart />;
  if (n === "6") return <Clause6 only={coll} />;
  if (n === "7") return <Clause7 only={coll} />;
  if (n === "8" && coll === "internal_audit_plan") return <InternalAuditPlan />;
  if (n === "8" && coll === "audit_summary") return <InternalAuditSummary />;
  if (n === "8" && coll === "internal_audit_report") return <InternalAuditReport />;
  return <div className="main"><div className="topbar"><h2>Clause {n}</h2></div>
    <div className="card">This clause has no formats yet.</div></div>;
}

// ---- Cross-document reference linking -------------------------------------
// Turns format codes (4.1F-01) and procedure codes (4.1P-01) found anywhere in
// a controlled document into links: F-codes open the live format page in the
// app (falling back to the Format Manual sheet), P-codes open the procedure
// in the Quality Procedure Manual.
const DOC_CODE_RE = /(\d{1,2}\.\d{1,2}\s?[FP]-\s?\d{2}[A-Z]?)/g;

function docCodeHref(rawCode, docKey) {
  const code = rawCode.replace(/\s+/g, "");
  if (code.indexOf("P-") !== -1) {
    return docKey === "procedure_manual" ? "#ref-" + code : "/rulebook/procedure_manual#ref-" + code;
  }
  const r = CODE_ROUTES[code];
  if (r) return "/clause/" + r.clause + "/" + r.coll;
  return docKey === "format_manual" ? "#ref-" + code : "/rulebook/format_manual#ref-" + code;
}

function linkifyDoc(html, docKey) {
  try {
    const parsed = new DOMParser().parseFromString(html, "text/html");
    // Unwrap prior internal doc links so codes are re-detected uniformly on every render.
    Array.prototype.slice.call(parsed.querySelectorAll("a")).forEach(function (a) {
      const href = a.getAttribute("href") || "";
      DOC_CODE_RE.lastIndex = 0;
      const internal = a.hasAttribute("data-doclink") || a.classList.contains("doc-ref")
        || href.indexOf("#ref-") === 0 || href.indexOf("/clause/") === 0 || href.indexOf("/rulebook/") === 0
        || DOC_CODE_RE.test(a.textContent || "");
      if (internal) { while (a.firstChild) a.parentNode.insertBefore(a.firstChild, a); a.parentNode.removeChild(a); }
    });
    const walker = parsed.createTreeWalker(parsed.body, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) {
      const nd = walker.currentNode;
      if (nd.parentElement && nd.parentElement.closest("a, h1, h2, h3, h4, h5")) continue;
      DOC_CODE_RE.lastIndex = 0;
      if (DOC_CODE_RE.test(nd.nodeValue)) nodes.push(nd);
    }
    nodes.forEach(nd => {
      const frag = parsed.createDocumentFragment();
      const parts = nd.nodeValue.split(DOC_CODE_RE);
      parts.forEach((part, i) => {
        if (i % 2 === 1) {
          const a = parsed.createElement("a");
          a.setAttribute("href", docCodeHref(part, docKey));
          a.setAttribute("data-doclink", "1");
          a.className = "doc-ref";
          a.textContent = part.replace(/\s+/g, "");
          frag.appendChild(a);
        } else if (part) {
          frag.appendChild(parsed.createTextNode(part));
        }
      });
      nd.parentNode.replaceChild(frag, nd);
    });
    return parsed.body.innerHTML;
  } catch (e) { return html; }
}

function slugifyHeading(text, used) {
  var base = (text || "").toLowerCase().trim().replace(/[^\w\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60);
  if (!base) base = "sec";
  var id = base, i = 2;
  while (used.has(id)) { id = base + "-" + i; i++; }
  used.add(id);
  return id;
}

// ---- Quality Manual INTERNAL navigation -----------------------------------
// Hard-coded map (built by inspecting the real stored doc.pages, not guessed) from a clause /
// section / annexure / form code → the doc.pages INDEX (data-pi) that holds that content. Used to
// make Contents items AND in-document clause/code references jump to the exact page. Form &
// procedure codes live on the ANX.-C "List of Formats" / ANX.-D "List of Procedures" pages.
const QM_NAV_CLAUSE = {
  "4": 10, "4.1": 11, "4.2": 12, "5": 13, "6": 20, "6.1": 21, "6.2": 22, "6.3": 24, "6.4": 26,
  "6.5": 28, "6.6": 30, "7": 31, "7.1": 33, "7.2": 35, "7.3": 35, "7.4": 37, "7.5": 37, "7.6": 38,
  "7.7": 39, "7.8": 43, "7.9": 45, "7.10": 46, "7.11": 46, "8": 48, "8.1": 48, "8.2": 49, "8.3": 52,
  "8.4": 53, "8.5": 54, "8.6": 55, "8.7": 56, "8.8": 56, "8.9": 57,
};
const QM_NAV_NAME = {
  "scope": 5, "abbreviations": 6, "distribution list": 7, "introduction": 8,
  "quality policy and objectives": 9, "release authorization": 1, "amendment record": 2, "contents": 2,
};
const QM_NAV_ANX = { "anxa": 59, "anxb": 60, "anxc": 62, "anxd": 64, "anxf": 66 };
const QM_FORMCODE_IDX = 62;   // ANX.-C — List of Formats (form codes catalogued here)
const QM_PROCCODE_IDX = 64;   // ANX.-D — List of Procedures

// Resolve a heading / label to its page index (used by the Contents panel).
function qmNavIndexFor(text) {
  var t = String(text || "").replace(/ /g, " ").trim();
  var m = t.match(/^(?:Section\s+)?(\d{1,2}(?:\.\d{1,2}){0,2})\b/);   // leading clause number
  if (m && QM_NAV_CLAUSE[m[1]] != null) return QM_NAV_CLAUSE[m[1]];
  var anx = t.toLowerCase().replace(/[^a-z]/g, "");
  if (anx.indexOf("anx") === 0 && QM_NAV_ANX[anx.slice(0, 4)] != null) return QM_NAV_ANX[anx.slice(0, 4)];
  var key = t.toLowerCase().replace(/\s+/g, " ").trim();
  if (QM_NAV_NAME[key] != null) return QM_NAV_NAME[key];
  return null;
}

// Pretty-print a Contents label (Abbreviations, Distribution List, Quality Policy and Objectives)
// without touching clause numbers, codes, "Section N —" dividers, or real acronyms.
function qmNiceLabel(text) {
  var t = String(text || "").replace(/ /g, " ").trim();
  if (/\d/.test(t) || /—|–/.test(t)) return t;            // numbered / "Section N —" headings: leave as-is
  var small = { of: 1, and: 1, the: 1, for: 1, to: 1, in: 1, "a": 1, an: 1, or: 1 };
  return t.toLowerCase().split(/\s+/).map(function (w, i) {
    if (i > 0 && small[w]) return w;
    return w.charAt(0).toUpperCase() + w.slice(1);
  }).join(" ");
}

// Wrap in-document clause references ("clause 7.7", "refer 6.4") and form/procedure codes
// (7.7F-01, 6.4F-04, 7.7P-01) in internal links that jump to the matching page. Conservative: a
// bare number is linked ONLY after an explicit reference word, so ordinary numbers stay plain.
function linkifyQmPage(html) {
  if (!html || html.indexOf("<") === -1) return html || "";
  var CODE = /^\d{1,2}\.\d{1,2}\s?[FP]-\s?\d{2}[A-Z]?$/;
  var SPLIT = /(\d{1,2}\.\d{1,2}\s?[FP]-\s?\d{2}[A-Z]?|(?:clause|refer(?:\s+to)?|as\s+per|see|section|para)\s+\d{1,2}(?:\.\d{1,2}){0,2})/gi;
  try {
    var parsed = new DOMParser().parseFromString(html, "text/html");
    var walker = parsed.createTreeWalker(parsed.body, NodeFilter.SHOW_TEXT);
    var hits = [];
    while (walker.nextNode()) {
      var nd = walker.currentNode;
      if (nd.parentElement && nd.parentElement.closest("a, h1, h2, h3, thead, .qm-foot")) continue;
      SPLIT.lastIndex = 0;
      if (SPLIT.test(nd.nodeValue)) hits.push(nd);
    }
    hits.forEach(function (nd) {
      var frag = parsed.createDocumentFragment();
      var parts = nd.nodeValue.split(SPLIT);
      parts.forEach(function (part, i) {
        if (i % 2 === 1) {
          var idx = null, code = part.replace(/\s+/g, "");
          if (CODE.test(code)) { idx = code.indexOf("P-") !== -1 ? QM_PROCCODE_IDX : QM_FORMCODE_IDX; }
          else { var cm = part.match(/(\d{1,2}(?:\.\d{1,2}){0,2})\s*$/); if (cm && QM_NAV_CLAUSE[cm[1]] != null) idx = QM_NAV_CLAUSE[cm[1]]; }
          if (idx != null) {
            var a = parsed.createElement("a");
            a.className = "qm-xref"; a.setAttribute("data-goto", String(idx)); a.textContent = part;
            frag.appendChild(a);
          } else { frag.appendChild(parsed.createTextNode(part)); }
        } else if (part) { frag.appendChild(parsed.createTextNode(part)); }
      });
      nd.parentNode.replaceChild(frag, nd);
    });
    return parsed.body.innerHTML;
  } catch (e) { return html; }
}

function normalizeHeadings(html) {
  try {
    var parsed = new DOMParser().parseFromString(html, "text/html");
    var used = new Set();
    parsed.querySelectorAll("h1[id], h2[id], h3[id]").forEach(function (h) { if (h.id) used.add(h.id); });
    parsed.querySelectorAll("h1, h2, h3").forEach(function (h) { if (!h.id) h.id = slugifyHeading(h.textContent || "", used); });
    // The imported Word content joins EVERY word with a non-breaking space (&nbsp; -> U+00A0),
    // so each paragraph is one unbreakable run that can't wrap and overflows off the right edge.
    // Convert those to ordinary spaces at render time (display only, stored content untouched) so
    // text wraps at word boundaries. Serial-cell detection already strips both, so tables are safe.
    var walker = parsed.createTreeWalker(parsed.body, NodeFilter.SHOW_TEXT, null);
    for (var n = walker.nextNode(); n; n = walker.nextNode()) {
      if (n.nodeValue && n.nodeValue.indexOf(" ") !== -1) n.nodeValue = n.nodeValue.replace(/ /g, " ");
    }
    return parsed.body.innerHTML;
  } catch (e) { return html; }
}

// Normalise imported manual tables so the layout reads correctly on screen AND in print:
//  1) "Banner" rows (a title / discipline line pasted as a short row in a wide table) have far
//     fewer cells than the table's real column count. Because that long title cell sat in column 1
//     with no colspan, it stretched column 1 — which is also the Sl / Sr. No. column — making it
//     huge and squeezing every content column. We give each banner row's first cell the missing
//     colspan so the title spans the full width and column 1 is freed.
//  2) If the first column is a serial column (values like 01, 02, 03…), we tag those cells with
//     `sl-cell` so CSS can render a compact, centered Sl column (screen + print).
// Data/content is never altered — only colspan on banner rows and a layout class are added.
function normalizeDocTables(html) {
  if (!html || html.indexOf("<table") === -1) return html || "";
  var doc;
  try { doc = new DOMParser().parseFromString(html, "text/html"); } catch (e) { return html; }
  doc.querySelectorAll("table").forEach(function (table) {
    var rows = Array.from(table.rows || []);
    if (rows.length < 2) return;
    var spanOf = function (tr) { return Array.from(tr.cells).reduce(function (s, c) { return s + (parseInt(c.getAttribute("colspan"), 10) || 1); }, 0); };
    var maxCols = Math.max.apply(null, rows.map(spanOf));
    if (maxCols < 3) return;                      // tiny tables (e.g. footers) are left untouched
    // A banner/title line is a row with just 1-2 cells in a many-column table (e.g. "NAME OF THE
    // LABORATORY…", "DISCIPLINE: Chemical"). NOT a normal data row that merely has fewer cells than
    // an over-fragmented header. Only 1-2 cell rows are spanned, so real data rows are never touched.
    var txtOf = function (c) { return String((c && c.textContent) || "").replace(/[\s ]/g, ""); };
    var banner = new Set();
    rows.forEach(function (tr) {
      var cells = Array.from(tr.cells);
      var span = spanOf(tr);
      if (span >= maxCols || !cells.length) return;
      // A true banner/title line is a single-cell row, OR a row whose only non-empty cell is the
      // first one (title + trailing empty filler cells). Rows with two or more FILLED cells are
      // real data rows that merely have fewer columns than an over-fragmented header — never spanned.
      var filled = cells.filter(function (c) { return txtOf(c); });
      var isBanner = cells.length === 1 || (filled.length <= 1 && txtOf(cells[0]));
      if (!isBanner) return;
      var first = cells[0];
      first.setAttribute("colspan", String((parseInt(first.getAttribute("colspan"), 10) || 1) + (maxCols - span)));
      banner.add(tr);
    });
    // Serial-column detection over the non-banner rows (header + data).
    var fullRows = rows.filter(function (tr) { return !banner.has(tr); });
    var firstCells = fullRows.map(function (tr) { return tr.cells[0]; }).filter(Boolean);
    var isSerial = function (t) { return /^0*\d{1,3}$/.test(String(t || "").replace(/[\s\u00a0]/g, "")); };
    var numeric = firstCells.filter(function (c) { return isSerial(c.textContent); }).length;
    if (firstCells.length >= 3 && numeric >= Math.ceil((firstCells.length - 1) * 0.5)) {
      firstCells.forEach(function (c) { c.classList.add("sl-cell"); });
    }
  });
  return doc.body.innerHTML;
}

// Inject the controlled-copy holder (set once under Company -> Document control)
// wherever a "Holder Name :" line appears in a manual.
function injectHolder(html, holder) {
  if (!holder) return html;
  return html.replace(/(Holder Name\s*:)\s*([^<]*)/g, "$1 " + holder);
}

// Auto-fill the Amendment Record table from the app's own version history:
// every signed-off change (edit or import) appears as a row with section,
// date, change, reason and approver — no manual bookkeeping.
// Rows auto-derived from the app's own signed version history (fallback / seed).
// Locate the manual's Amendment Record table in a parsed document. Header text uses
// non-breaking spaces (&nbsp; ->  ), so normalise whitespace before matching, and
// fall back to the amendment table's distinctive column labels.
function findAmendmentTable(parsed) {
  const norm = (s) => (s || "").replace(/ /g, " ").replace(/\s+/g, " ").toLowerCase();
  const tables = Array.from(parsed.querySelectorAll("table"));
  return tables.find(t => { const x = norm(t.textContent); return x.includes("amendment made") && x.includes("reason"); })
    || tables.find(t => { const x = norm(t.textContent); return x.includes("page no") && x.includes("clause") && x.includes("amendment"); })
    || null;
}

// Read a cell's content: a signature <img> returns its data-URL, else trimmed text.
const amendCellText = (td) => {
  const img = td.querySelector && td.querySelector("img");
  if (img) return img.getAttribute("src") || "";
  if (td.classList && td.classList.contains("amend-sign-cell")) return "";
  return (td.textContent || "").replace(/\u00a0/g, " ").trim();
};

// Parse the amendment table POSITIONALLY: real column headers, existing data rows as
// cell arrays (one string per column), and which column is the signature column.
// Editing/rendering by position guarantees columns never shift.
function parseAmendmentTable(html) {
  try {
    const parsed = new DOMParser().parseFromString(html, "text/html");
    const t = findAmendmentTable(parsed);
    if (!t) return { headers: [], rows: [], sigCol: -1 };
    const trs = Array.from(t.querySelectorAll("tr"));
    let headers = [];
    for (const tr of trs) {
      const cells = Array.from(tr.querySelectorAll("td, th")).map(c => (c.textContent || "").replace(/ /g, " ").replace(/\s+/g, " ").trim());
      if (cells.length && isNaN(parseInt(cells[0], 10))) { headers = cells; break; }
    }
    if (!headers.length) headers = ["S.", "No.", "Page No.", "Section/ Clause/ Para/ line", "(as applicable)", "Date of Amendment", "Amendment made", "Reasons of Amendment", "Signature of person authorizing Amendment"];
    const sigCol = headers.findIndex(h => /signature/i.test(h));
    const rows = [];
    trs.forEach(tr => {
      const tds = Array.from(tr.querySelectorAll("td"));
      if (!tds.length || isNaN(parseInt((tds[0].textContent || "").trim(), 10))) return;
      const cells = tds.map(amendCellText);
      while (cells.length < headers.length) cells.push("");
      rows.push(cells.slice(0, headers.length));
    });
    return { headers, rows, sigCol };
  } catch (e) { return { headers: [], rows: [], sigCol: -1 }; }
}

// Coerce any stored row (positional array, or legacy {no,page,…} object) to a cell array.
function amendRowToCells(row) {
  if (Array.isArray(row)) return row.map(c => (c == null ? "" : String(c)));
  if (row && typeof row === "object") return [row.no, row.page, row.clause, row.date, row.amendment, row.reason, row.approved_by].map(c => (c == null ? "" : String(c)));
  return [];
}

// Fill the manual's Amendment Record table from stored rows (single source of truth):
// drop the baked-in data rows + blank filler rows, then render stored rows POSITIONALLY
// (one cell per column, so nothing shifts). A signature-column cell holding an image
// data-URL renders as an <img>. With no stored rows the manual is left as authored.
function injectAmendments(html, stored) {
  try {
    if (!(stored && stored.length)) return html;
    const parsed = new DOMParser().parseFromString(html, "text/html");
    const t = findAmendmentTable(parsed);
    if (!t) return html;
    let headerLen = 9, sigCol = -1;
    for (const tr of Array.from(t.querySelectorAll("tr"))) {
      const cells = Array.from(tr.querySelectorAll("td, th"));
      if (cells.length && isNaN(parseInt((cells[0].textContent || "").trim(), 10))) { headerLen = cells.length; sigCol = cells.findIndex(c => /signature/i.test(c.textContent || "")); break; }
    }
    Array.from(t.querySelectorAll("tr")).forEach(r => {
      if (!(r.textContent || "").replace(/\s+/g, "")) { r.remove(); return; }
      const first = (r.querySelector("td") || {}).textContent;
      if (first !== undefined && !isNaN(parseInt((first || "").trim(), 10))) r.remove();
    });
    const tbody = t.querySelector("tbody") || t;
    stored.forEach(row => {
      const cells = amendRowToCells(row);
      const n = Math.max(headerLen, cells.length);
      const tr = parsed.createElement("tr");
      for (let c = 0; c < n; c++) {
        const td = parsed.createElement("td");
        const val = cells[c] != null ? String(cells[c]) : "";
        if (c === sigCol && /^data:image/.test(val)) {
          const img = parsed.createElement("img"); img.setAttribute("src", val); img.setAttribute("style", "height:34px;max-width:150px;object-fit:contain");
          td.appendChild(img);
        } else { td.textContent = val; }
        tr.appendChild(td);
      }
      tbody.appendChild(tr);
    });
    return parsed.body.innerHTML;
  } catch (e) { return html; }
}

// Format Manual: put a "Print blank" chip on every format sheet heading.
// Attribute-order-agnostic: works for both <h2 id=".." class="fm-title"> and
// <h2 class="fm-title" id="..">.
function injectSheetButtons(html) {
  return html.replace(/<h2([^>]*)>((?:(?!<\/h2>).)*)<\/h2>/g, (m, attrs, inner) => {
    if (attrs.indexOf("fm-title") === -1) return m;
    const idm = attrs.match(/id="(ref-[^"]+)"/);
    if (!idm) return m;
    return '<h2' + attrs + '>' + inner +
      '<span class="sheet-print" data-sheet="' + idm[1] + '" title="Print this blank format">Print blank</span></h2>';
  });
}

function extractSheet(html, id) {
  const parts = html.split(/(?=<h2[^>]*id="ref-)/);
  const seg = parts.find(p => p.indexOf('id="' + id + '"') !== -1);
  if (!seg) return null;
  const tm = seg.match(/<h2[^>]*>((?:(?!<span)(?!<\/h2>).)*)/);
  const title = tm ? tm[1].replace(/<[^>]+>/g, "").trim() : "Format";
  let body = seg
    .replace(/<h2[^>]*id="ref-[^"]*"[^>]*>(?:(?!<\/h2>).)*<\/h2>/, "")
    .replace(/<span class="sheet-print"[^>]*>[^<]*<\/span>/g, "");
  // Blank print: field labels ending with ":" get a dotted write-on line
  // instead of empty space.
  body = body.replace(/:\s*((?:<\/(?:b|u|i)>)*)\s*<\/p>/g,
    (m, close) => ': ' + (close || '') + ' ' + '.'.repeat(55) + '</p>');
  return { title, body };
}

// Regex that splits a heading like "7.4 Testing Processes" into its clause number
// and section name. Shared by the on-screen header, the editor, and print so all three
// derive the same Section No / Section for a heading.
const SECTION_CODE_RE = /^\s*([0-9]+(?:\.[0-9]+)*(?:\s*[FP]-?\s*[0-9]{2}[A-Z]?)?)\s*[—:.\-]?\s*([\s\S]*)$/;
function deriveSection(text) {
  const t = (text || "").replace(/\s+/g, " ").trim();
  const m = t.match(SECTION_CODE_RE);
  const has = !!(m && m[2] && m[2].trim());
  return { no: has ? m[1] : "", name: has ? m[2].trim() : t };
}

// Single source of truth for the document-control header, shared by the software view and the
// printed/PDF running header. Reads the stored `doc.header` (per-section metadata + document-level
// signatures) and falls back to legacy fields / constants so manuals without a saved header still
// render a complete block.
function docFooterMeta(doc) {
  if (!doc) return { docNo: "", docName: "", issueNo: "01", issueDate: "05/10/2024", amendNo: "00", amendDate: "", sections: {}, signatures: {} };
  const h = doc.header || {};
  const dfl = h.defaults || {};
  return {
    docNo: String(doc.doc_code || "").replace(/SAN-?Lab/i, "SANLAB"),
    docName: String(doc.title || "").toUpperCase(),
    issueNo: dfl.issue_no || doc.issue_no || "01",
    issueDate: dfl.issue_date || doc.issue_date || "05/10/2024",
    amendNo: dfl.amend_no || doc.amend_no || "00",
    amendDate: dfl.amend_date || doc.amend_date || "",
    sections: h.sections || {},
    signatures: h.signatures || {},
    coverNoFooter: true,   // page 1 is the manual's cover (own copy-control block) — no footer there
  };
}

function Rulebook() {
  const { doc_key } = useParams();
  const { can } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [holder, setHolder] = useState("");
  const [doc, setDoc] = useState(null); const [versions, setVersions] = useState([]);
  const [editing, setEditing] = useState(false);
  const [content, setContent] = useState(""); const [sc, setSc] = useState(""); const [cm, setCm] = useState(""); const [rs, setRs] = useState("");
  const [err, setErr] = useState(""); const [showHist, setShowHist] = useState(false); const [viewVer, setViewVer] = useState(null);
  const [importing, setImporting] = useState(false);
  const [amendEditing, setAmendEditing] = useState(false); const [amendRows, setAmendRows] = useState([]); const [amendSaving, setAmendSaving] = useState(false);
  const [amendHeaders, setAmendHeaders] = useState([]); const [amendSigCol, setAmendSigCol] = useState(-1);
  const [tableEditing, setTableEditing] = useState(false); const [savingTable, setSavingTable] = useState(false);
  const docRef = React.useRef(null);
  const importRef = React.useRef(null);
  const [people, setPeople] = useState([]);
  // Document-control header (per-section metadata + document-level signatures).
  const [hdrEditing, setHdrEditing] = useState(false); const [hdrSaving, setHdrSaving] = useState(false);
  const [hdrSecs, setHdrSecs] = useState({}); const [hdrSigs, setHdrSigs] = useState({}); const [hdrDfl, setHdrDfl] = useState({});
  const [secEdit, setSecEdit] = useState(null); const [secSaving, setSecSaving] = useState(false);   // per-page control-box editor
  const [tocQuery, setTocQuery] = useState("");   // Contents / section search
  // Read presentation: "Page view" renders paginated pages, each with the control box at its foot
  // (same engine as Print/PDF). "Continuous view" is the plain scrollable read view (TOC, in-place
  // amendment editing). Page view is the default so the per-page footer is visible on load.
  const [pageView, setPageView] = useState(true);
  // Per-page editor (explicit page model): opened from the ✎ Edit button on each A4 sheet.
  const [pageEdit, setPageEdit] = useState(null); const [pageSaving, setPageSaving] = useState(false);
  // When amendment editing forces a temporary switch out of Page view, remember to switch back
  // afterwards so the user returns to the page-by-page view (control-box footer on every page).
  const returnToPageView = React.useRef(false);
  const load = () => { api.document(doc_key).then(d => { setDoc(d); setContent(d.current_content); }); api.versions(doc_key).then(setVersions); };
  useEffect(() => { setEditing(false); setShowHist(false); setViewVer(null); setHdrEditing(false); load(); }, [doc_key]);
  useEffect(() => { api.companyLogo().then(r => setHolder(r.holder_name || "")).catch(() => {}); api.personnelPublic().then(setPeople).catch(() => {}); }, []);
  const normalized = React.useMemo(() => (doc && doc.current_content) ? normalizeHeadings(doc.current_content) : "", [doc]);
  const toc = React.useMemo(() => {
    if (!normalized) return [];
    try {
      const parsed = new DOMParser().parseFromString(normalized, "text/html");
      return Array.from(parsed.querySelectorAll("h1, h2, h3")).filter(h => !h.classList.contains("fm-hidden") && !h.classList.contains("notoc")).map(h => ({
        id: h.id, text: (h.textContent || "").trim().slice(0, 80), level: h.tagName === "H1" ? 1 : h.tagName === "H2" ? 2 : 3,
      })).filter(x => x.text);
    } catch (e) { return []; }
  }, [normalized]);
  // Full section list (heading id + derived clause no/name), used to key per-section header
  // metadata. The ids match the print output, so on-screen and printed values stay in sync.
  const secList = React.useMemo(() => {
    if (!normalized) return [];
    try {
      const parsed = new DOMParser().parseFromString(normalized, "text/html");
      return Array.from(parsed.querySelectorAll("h1, h2, h3")).filter(h => !h.classList.contains("fm-hidden") && !h.classList.contains("notoc")).map(h => {
        const text = (h.textContent || "").replace(/\s+/g, " ").trim();
        const d = deriveSection(text);
        return { id: h.id, text, level: h.tagName === "H1" ? 1 : h.tagName === "H2" ? 2 : 3, derivedNo: d.no, derivedName: d.name };
      }).filter(x => x.id && x.text);
    } catch (e) { return []; }
  }, [normalized]);
  const meta = React.useMemo(() => docFooterMeta(doc), [doc]);
  const sigOf = (slot) => (meta.signatures && meta.signatures[slot]) || {};
  // Explicit page model: the stored, fixed hard-copy page boundaries. When present it drives the
  // page-by-page view (one A4 sheet per stored page) instead of letting paged.js re-flow the blob.
  const pages = React.useMemo(() => (doc && Array.isArray(doc.pages)) ? doc.pages : [], [doc]);
  const hasPages = pages.length > 0;
  // Page HTML passed to the renderer, run through the same pipeline as the continuous view so the
  // page-by-page sheets stay consistent: doc-reference links, holder name, live amendment table
  // (from doc.amendments, so amendment edits show here too), and table normalisation. The raw
  // stored html (pages[i].html) is still what the per-page editor edits — only the view is processed.
  const pagesView = React.useMemo(() => pages.map(p => (p && p.html)
    ? { ...p, html: normalizeDocTables(injectAmendments(injectHolder(linkifyQmPage(p.html), holder), (doc && doc.amendments) || [])) }
    : p), [pages, holder, doc]);
  const openPageEditor = (i) => { const p = pages[i]; if (!p) return; setPageEdit({ i, ...p }); setErr(""); };
  const setPageField = (field, val) => setPageEdit(p => ({ ...p, [field]: val }));
  const savePageEditor = async () => {
    if (!pageEdit) return;
    setPageSaving(true); setErr("");
    try {
      const next = pages.map((p, idx) => idx === pageEdit.i
        ? { page: pageEdit.page, section_no: pageEdit.section_no, section: pageEdit.section,
            amend_no: pageEdit.amend_no, amend_date: pageEdit.amend_date, no_footer: !!pageEdit.no_footer,
            amendment: pageEdit.amendment || "", html: pageEdit.html || "" }
        : p);
      const r = await api.setDocPages(doc_key, next);
      setDoc(d => ({ ...d, pages: next, pages_count: r && r.count }));
      setPageEdit(null);
    } catch (e) { setErr((e && e.message) || String(e)); }
    setPageSaving(false);
  };
  // Jump to a section inside the paged (iframe) view: scroll the outer scroll container to the
  // heading's position (iframe is same-origin srcdoc, so its content is directly reachable).
  const jumpPaged = (id) => {
    const ifr = document.getElementById("qm-paged-frame"); if (!ifr) return;
    try {
      const idoc = ifr.contentDocument; if (!idoc) return;
      const el = idoc.getElementById(id) || idoc.querySelector('[id="' + id + '"]'); if (!el) return;
      let sc = ifr.parentElement;
      while (sc && sc !== document.body) { const s = window.getComputedStyle(sc); if (/(auto|scroll)/.test(s.overflowY) && sc.scrollHeight > sc.clientHeight + 5) break; sc = sc.parentElement; }
      const targetTop = ifr.getBoundingClientRect().top + el.getBoundingClientRect().top;
      if (sc && sc !== document.body) sc.scrollTop += (targetTop - sc.getBoundingClientRect().top - 16);
      else window.scrollTo({ top: (window.scrollY || 0) + targetTop - 16, behavior: "smooth" });
      // Brief highlight so the reader sees exactly which section was reached (Contents + Search).
      try { el.classList.add("mk-hl"); setTimeout(() => { try { el.classList.remove("mk-hl"); } catch (e2) {} }, 1600); } catch (e3) {}
    } catch (e) { /* cross-frame or not laid out yet — ignore */ }
  };
  // Jump to an EXACT stored page (data-pi) in the fixed-page view — used by Contents items and by
  // internal clause/code links clicked inside the document. Scrolls the sheet into view + flashes it.
  const jumpToPageIdx = (idx) => {
    if (idx == null || idx < 0) return;
    const ifr = document.getElementById("qm-paged-frame"); if (!ifr) return;
    try {
      const idoc = ifr.contentDocument; if (!idoc) return;
      const el = idoc.querySelector('.qm-sheet[data-pi="' + idx + '"]'); if (!el) return;
      let sc = ifr.parentElement;
      while (sc && sc !== document.body) { const s = window.getComputedStyle(sc); if (/(auto|scroll)/.test(s.overflowY) && sc.scrollHeight > sc.clientHeight + 5) break; sc = sc.parentElement; }
      const targetTop = ifr.getBoundingClientRect().top + el.getBoundingClientRect().top;
      if (sc && sc !== document.body) sc.scrollTop += (targetTop - sc.getBoundingClientRect().top - 12);
      else window.scrollTo({ top: (window.scrollY || 0) + targetTop - 12, behavior: "smooth" });
      try { el.classList.add("mk-hl"); setTimeout(() => { try { el.classList.remove("mk-hl"); } catch (e2) {} }, 1600); } catch (e3) {}
    } catch (e) { /* ignore */ }
  };
  // Inline signature pick (Prepared/Approved/Issued By) — select a person, auto-fill their saved
  // signature, and save immediately so the paged footers update. Preserves per-section metadata.
  const pickSigInline = async (slot, id, img) => {
    const p = people.find(x => x.id === id);
    const sig = { id: id || "", name: p ? p.name : "", designation: p ? (p.designation || "") : "", signature: img || "" };
    const h = (doc && doc.header) || {};
    try {
      const r = await api.setDocHeader(doc_key, { signatures: { ...(h.signatures || {}), [slot]: sig }, defaults: h.defaults || {}, sections: h.sections || {} });
      setDoc(d => ({ ...d, header: r.header }));
    } catch (e) { setErr((e && e.message) || String(e)); }
  };
  // ---- Header editor helpers ----
  const openHdrEditor = () => {
    const h = (doc && doc.header) || {};
    const secs = {};
    secList.forEach(s => {
      const sm = (h.sections && h.sections[s.id]) || {};
      secs[s.id] = {
        section_no: sm.section_no != null && sm.section_no !== "" ? sm.section_no : (s.derivedNo || ""),
        section: sm.section != null && sm.section !== "" ? sm.section : (s.derivedName || ""),
        issue_no: sm.issue_no || "", issue_date: sm.issue_date || "",
        amend_no: sm.amend_no || "", amend_date: sm.amend_date || "", page_no: sm.page_no || "",
      };
    });
    setHdrSecs(secs); setHdrSigs(h.signatures || {}); setHdrDfl(h.defaults || {});
    setErr(""); setHdrEditing(true);
  };
  const setHdrSecCell = (id, field, val) => setHdrSecs(m => ({ ...m, [id]: { ...(m[id] || {}), [field]: val } }));
  const setHdrDflCell = (field, val) => setHdrDfl(m => ({ ...m, [field]: val }));
  const pickHdrSig = (slot, id, img) => {
    const p = people.find(x => x.id === id);
    setHdrSigs(m => ({ ...m, [slot]: { id: id || "", name: p ? p.name : "", designation: p ? (p.designation || "") : "", signature: img || "" } }));
  };
  const saveHdr = async () => {
    setHdrSaving(true); setErr("");
    try {
      const r = await api.setDocHeader(doc_key, { signatures: hdrSigs, defaults: hdrDfl, sections: hdrSecs });
      setDoc(d => ({ ...d, header: r.header })); setHdrEditing(false);
    } catch (e) { setErr((e && e.message) || String(e)); }
    setHdrSaving(false);
  };
  // ---- Per-page document-control editor (opened from the "✎ Edit" button on each paged page) ----
  // Edits ONLY the section whose values that page's footer shows; signatures/defaults/other sections
  // are left untouched. We stay on the same page: re-jump to the section after the iframe re-renders.
  const openSecEditor = (secId) => {
    if (!secId) return;
    const s = secList.find(x => x.id === secId);
    const h = (doc && doc.header) || {};
    const sm = (h.sections && h.sections[secId]) || {};
    setSecEdit({
      id: secId,
      title: (s && s.text) || "this page",
      draft: {
        section_no: sm.section_no != null && sm.section_no !== "" ? sm.section_no : ((s && s.derivedNo) || ""),
        section: sm.section != null && sm.section !== "" ? sm.section : ((s && s.derivedName) || ""),
        issue_no: sm.issue_no || "", issue_date: sm.issue_date || "",
        amend_no: sm.amend_no || "", amend_date: sm.amend_date || "", page_no: sm.page_no || "",
      },
    });
    setErr("");
    setTimeout(() => jumpPaged(secId), 60);   // keep the viewport on the page being edited
  };
  const setSecCell = (field, val) => setSecEdit(se => se && ({ ...se, draft: { ...se.draft, [field]: val } }));
  const hdrDflOr = (field) => {
    const d = (doc && doc.header && doc.header.defaults) || {};
    const fb = { issue_no: meta.issueNo, issue_date: meta.issueDate, amend_no: meta.amendNo, amend_date: meta.amendDate };
    return d[field] || fb[field] || "";
  };
  const saveSecEdit = async () => {
    if (!secEdit) return;
    setSecSaving(true); setErr("");
    const id = secEdit.id;
    try {
      const h = (doc && doc.header) || {};
      const sections = { ...(h.sections || {}), [id]: secEdit.draft };
      const r = await api.setDocHeader(doc_key, { signatures: h.signatures || {}, defaults: h.defaults || {}, sections });
      setDoc(d => ({ ...d, header: r.header }));
      setSecEdit(null);
      setTimeout(() => jumpPaged(id), 900);   // re-jump after the iframe re-paginates
    } catch (e) { setErr((e && e.message) || String(e)); }
    setSecSaving(false);
  };
  const jump = (id) => { const el = document.getElementById(id); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); };
  const viewHtml = React.useMemo(() => {
    if (!normalized) return "";
    let h = linkifyDoc(normalized, doc_key);
    h = injectHolder(h, holder);
    h = injectAmendments(h, (doc && doc.amendments) || []);
    h = normalizeDocTables(h);
    if (doc_key === "format_manual") h = injectSheetButtons(h);
    return h;
  }, [normalized, doc_key, holder, versions, doc]);
  // Heading-id'd, table-normalised HTML fed to the paged renderer — identical to what Print/PDF
  // uses, so the on-screen page-by-page view and the printed pages match exactly.
  const pagedHtml = React.useMemo(() => normalizeDocTables(normalized || ""), [normalized]);
  // Contents search: match section number / name / heading text (covers "4.1", "Impartiality",
  // "Scope", "Quality Policy", …). Clicking a result reuses the same jump anchors as Contents.
  const tocShown = React.useMemo(() => {
    const q = tocQuery.trim().toLowerCase();
    return q ? toc.filter(h => (h.text || "").toLowerCase().includes(q)) : toc;
  }, [toc, tocQuery]);
  useEffect(() => {
    if (!doc || !location.hash) return;
    const t = setTimeout(() => { const el = document.getElementById(location.hash.slice(1)); if (el) el.scrollIntoView({ block: "start" }); }, 200);
    return () => clearTimeout(t);
  }, [doc, location.hash]);
  // Apply in-place amendment-table editing after render (so React's re-render of the
  // document HTML can't wipe the editable attributes). applyEditRef is set below.
  const applyEditRef = React.useRef(null);
  React.useEffect(() => { if (tableEditing && applyEditRef.current) applyEditRef.current(); }, [tableEditing, viewHtml]);
  const onDocClick = (e) => {
    const sp = e.target.closest && e.target.closest("[data-sheet]");
    if (sp) {
      e.preventDefault();
      const s = extractSheet(viewHtml, sp.getAttribute("data-sheet"));
      if (s) printContent(s.title, doc.doc_code, normalizeDocTables(s.body));
      return;
    }
    const a = e.target.closest && e.target.closest("a[data-doclink]");
    if (!a) return;
    e.preventDefault();
    const href = a.getAttribute("href") || "";
    if (href.startsWith("#")) { const el = document.getElementById(href.slice(1)); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); }
    else navigate(href);
  };
  if (!doc) return <div className="main">Loading…</div>;
  const save = async () => {
    setErr("");
    try { await api.newVersion(doc_key, { content, section_clause: sc, change_made: cm, reason: rs }); setEditing(false); setSc(""); setCm(""); setRs(""); load(); }
    catch (x) { setErr(x.message); }
  };
  const doImport = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (!window.confirm("Import '" + f.name + "' as the new current version of " + doc.title + "? The present version moves to Obsolete documents with full history retained.")) { e.target.value = ""; return; }
    setErr(""); setImporting(true);
    try { const fd = new FormData(); fd.append("file", f); await api.importDoc(doc_key, fd); load(); }
    catch (x) { setErr(x.message); }
    setImporting(false); e.target.value = "";
  };
  // ---- Amendment Record editor (structured, saved on the document; no version bump) ----
  // Pad/trim a cell array to the number of table columns.
  const fitRow = (cells, len) => { const a = (cells || []).slice(0, len); while (a.length < len) a.push(""); return a; };
  const openAmendEditor = () => {
    const parsed = parseAmendmentTable(normalized);
    const headers = parsed.headers.length ? parsed.headers : ["S.", "No.", "Page No.", "Section/ Clause/ Para/ line", "(as applicable)", "Date of Amendment", "Amendment made", "Reasons of Amendment", "Signature of person authorizing Amendment"];
    const stored = (doc && doc.amendments) || [];
    // First edit imports the manual's own rows so every row is editable together;
    // later edits use the saved rows. All rows are positional cell arrays.
    const seed = (stored.length ? stored.map(amendRowToCells) : parsed.rows);
    setAmendHeaders(headers); setAmendSigCol(parsed.sigCol);
    setAmendRows(seed.map(r => fitRow(r, headers.length)));
    setAmendEditing(true);
  };
  const setAmendCell = (ri, ci, v) => setAmendRows(amendRows.map((r, i) => i === ri ? r.map((c, j) => j === ci ? v : c) : r));
  const addAmendRow = () => {
    const len = amendHeaders.length || 9;
    const next = amendRows.reduce((m, r) => { const n = parseInt((r[0] || "").trim(), 10); return isNaN(n) ? m : Math.max(m, n); }, 0) + 1;
    const row = fitRow([String(next)], len);
    setAmendRows([...amendRows, row]);
  };
  const delAmendRow = (i) => setAmendRows(amendRows.filter((_, ri) => ri !== i));
  const saveAmend = async () => {
    setAmendSaving(true); setErr("");
    try { const r = await api.setAmendments(doc_key, amendRows); setDoc(d => ({ ...d, amendments: r.amendments })); setAmendEditing(false); }
    catch (x) { setErr(x.message); }
    setAmendSaving(false);
  };
  // ---- Inline (in-place) editing of the Amendment Record table on the manual page ----
  const liveAmendTable = () => {
    const root = docRef.current; if (!root) return null;
    const norm = (s) => (s || "").replace(/ /g, " ").replace(/\s+/g, " ").toLowerCase();
    const tables = Array.from(root.querySelectorAll("table"));
    return tables.find(t => { const x = norm(t.textContent); return x.includes("amendment made") && x.includes("reason"); })
      || tables.find(t => { const x = norm(t.textContent); return x.includes("page no") && x.includes("clause") && x.includes("amendment"); }) || null;
  };
  const isHeaderRow = (txts) => { const j = txts.join(" ").toLowerCase(); return j.includes("page no") || j.includes("amendment made") || (txts[0] || "").toLowerCase() === "s."; };
  // Which column is the "Signature of person authorizing Amendment" column?
  const amendSigColOf = (t) => {
    for (const tr of Array.from(t.querySelectorAll("tr"))) {
      const cells = Array.from(tr.querySelectorAll("td, th"));
      if (cells.length && isNaN(parseInt((cells[0].textContent || "").trim(), 10))) return cells.findIndex(c => /signature/i.test(c.textContent || ""));
    }
    return -1;
  };
  // Put a signature image (or a small note) into a host element (the preview area of a sign cell).
  const setSignImg = (host, dataUrl, note) => {
    host.innerHTML = "";
    if (dataUrl) { const img = document.createElement("img"); img.src = dataUrl; img.setAttribute("style", "height:34px;max-width:150px;object-fit:contain"); host.appendChild(img); }
    else if (note) { const s = document.createElement("span"); s.className = "amend-sign-hint"; s.textContent = note; host.appendChild(s); }
  };
  // Signature column cell: pick a person and their SAVED signature auto-fills (no upload box).
  const setupSignCell = (td) => {
    td.removeAttribute("contenteditable");
    td.classList.add("amend-sign-cell");
    const existing = td.querySelector("img");
    const sig = existing ? existing.getAttribute("src") : "";
    td.innerHTML = "";
    const wrap = document.createElement("div"); wrap.className = "amend-sign-wrap";
    const imgHost = document.createElement("div"); imgHost.className = "amend-sign-img";
    setSignImg(imgHost, sig);
    wrap.appendChild(imgHost);
    const sel = document.createElement("select"); sel.className = "amend-sign-select noprint";
    sel.innerHTML = '<option value="">— select person —</option>' + (people || []).map(p => '<option value="' + p.id + '">' + ((p.name || "").replace(/</g, "")) + '</option>').join("");
    sel.onclick = (e) => e.stopPropagation();
    sel.onchange = (e) => { e.stopPropagation(); const p = (people || []).find(x => x.id === sel.value); setSignImg(imgHost, p ? (p.signature_image || "") : "", (p && !p.signature_image) ? "Signature not available for this person" : ""); };
    wrap.appendChild(sel);
    td.appendChild(wrap);
  };
  // Make the live amendment table's data cells editable (text cells contentEditable,
  // the signature column a drag-drop image dropzone). Run from an effect AFTER render so
  // React's re-render of the document HTML can't wipe the attributes we set.
  const applyTableEditDom = () => {
    const t = liveAmendTable(); if (!t) return;
    const sigCol = amendSigColOf(t);
    Array.from(t.querySelectorAll("tr")).forEach(tr => {
      const tds = Array.from(tr.querySelectorAll("td")); if (!tds.length) return;
      const txts = tds.map(td => (td.textContent || "").trim());
      if (isHeaderRow(txts)) return;                    // never edit the header
      tds.forEach((td, ci) => {
        if (ci === sigCol) setupSignCell(td);           // signature column = drag-drop image
        else { td.setAttribute("contenteditable", "true"); td.classList.add("amend-edit-cell"); }
      });
    });
  };
  applyEditRef.current = applyTableEditDom;
  const startTableEdit = () => {
    // The structured amendment editor edits the live table in the continuous (scroll) view
    // DOM (docRef). In Page view the table lives inside the paged iframe, so switch to the
    // continuous view first, then enter edit mode once it has rendered.
    if (pageView) {
      returnToPageView.current = true;
      setPageView(false);
      let tries = 0;
      const tryStart = () => {
        const t = liveAmendTable();
        if (t) { setTableEditing(true); return; }
        if (tries++ < 20) setTimeout(tryStart, 100);
        else window.alert("Amendment table not found.");
      };
      setTimeout(tryStart, 120);
      return;
    }
    const t = liveAmendTable(); if (!t) { window.alert("Amendment table not found on this page."); return; }
    setTableEditing(true);
  };
  const addInlineRow = () => {
    const t = liveAmendTable(); if (!t) return;
    const tbody = t.querySelector("tbody") || t;
    const sigCol = amendSigColOf(t);
    const ref = Array.from(t.querySelectorAll("tr")).find(tr => { const c = tr.querySelector("td"); return c && !isNaN(parseInt((c.textContent || "").trim(), 10)); });
    const cols = ref ? ref.querySelectorAll("td").length : 9;
    const tr = document.createElement("tr");
    for (let i = 0; i < cols; i++) {
      const td = document.createElement("td");
      if (i === sigCol) { td.innerHTML = "&nbsp;"; tr.appendChild(td); setupSignCell(td); }
      else { td.setAttribute("contenteditable", "true"); td.className = "amend-edit-cell"; td.innerHTML = "&nbsp;"; tr.appendChild(td); }
    }
    tbody.appendChild(tr); const first = tr.querySelector("td"); if (first) first.focus();
  };
  const saveTableEdit = async () => {
    const t = liveAmendTable(); if (!t) return;
    const rows = [];
    Array.from(t.querySelectorAll("tr")).forEach(tr => {
      const tds = Array.from(tr.querySelectorAll("td")); if (tds.length < 3) return;
      const txts = tds.map(td => (td.textContent || "").replace(/ /g, " ").trim());
      if (isHeaderRow(txts) || !txts.join("").trim()) return;   // skip header + fully blank rows
      // Store POSITIONAL cell arrays (signature <img> kept as its data-URL) so nothing shifts.
      rows.push(tds.map(amendCellText));
    });
    setSavingTable(true); setErr("");
    try { const r = await api.setAmendments(doc_key, rows); setDoc(d => ({ ...d, amendments: r.amendments })); setTableEditing(false); if (returnToPageView.current) { returnToPageView.current = false; setPageView(true); } }
    catch (x) { window.alert("Save failed: " + x.message); }
    setSavingTable(false);
  };
  const cancelTableEdit = () => { setTableEditing(false); load(); if (returnToPageView.current) { returnToPageView.current = false; setPageView(true); } };
  return <div className="main main-wide">
    <div className="topbar"><h2>{doc.title} <span className="badge">{doc.doc_code}</span> <span className="badge ok">v{doc.current_version}</span></h2>
      <div className="row">
        <button onClick={() => hasPages ? printPages(doc.title, doc.doc_code, pages, docFooterMeta(doc)) : printContent(doc.title, doc.doc_code, normalizeDocTables(normalized || doc.current_content), docFooterMeta(doc))}>Print / PDF</button>
        {!editing && <button onClick={() => setPageView(v => !v)} title={pageView ? "Switch to a continuous scroll view" : "Switch to a page-by-page view with the control box at the foot of every page"}>{pageView ? "Continuous view" : "Page view"}</button>}
        <button onClick={() => setShowHist(!showHist)}>Version history</button>
        {can("rulebook", "edit") && !editing && !hdrEditing && <button onClick={openHdrEditor}>Edit header</button>}
        {can("rulebook", "edit") && !editing && <button onClick={openAmendEditor}>Edit amendment record</button>}
        {can("rulebook", "edit") && !editing && <>
          <button onClick={() => importRef.current && importRef.current.click()} disabled={importing}>{importing ? "Importing…" : "Import file"}</button>
          <input ref={importRef} type="file" accept=".html,.htm,text/html" style={{ display: "none" }} onChange={doImport} />
        </>}
        {can("rulebook", "edit") && !editing && <button className="primary" onClick={() => setEditing(true)}>Edit</button>}
      </div></div>
    {err && !editing && <div className="err">{err}</div>}
    {showHist && <div className="card"><b>Versions</b> <span style={{ color: "var(--text-3)", fontSize: 12 }}>(click a version to read it)</span>
      {versions.map(v => <div key={v.id} className="row" style={{ justifyContent: "space-between", padding: "8px 4px", borderTop: "1px solid var(--border)", cursor: "pointer" }} onClick={() => setViewVer(v)}>
        <span>v{v.version_no} — {v.amendment?.change_made} {v.status === "obsolete" && <span className="badge obsolete">OBSOLETE</span>}{v.status === "current" && <span className="badge ok">CURRENT</span>}</span>
        <span style={{ color: "var(--text-3)", fontSize: 12 }}>{v.approved_by} · {inDate((v.approved_at || "").slice(0, 10))} ›</span>
      </div>)}
    </div>}
    {amendEditing && <div className="card">
      <div className="row" style={{ justifyContent: "space-between", marginBottom: 8 }}>
        <b>Amendment Record — {doc.title} <span className="badge">{doc.doc_code}</span></b>
        <div className="row">
          <button className="primary" onClick={saveAmend} disabled={amendSaving}>{amendSaving ? "Saving…" : "Save"}</button>
          <button onClick={() => setAmendEditing(false)}>Cancel</button>
          <button onClick={addAmendRow}>Add row</button>
        </div>
      </div>
      <div style={{ fontSize: 13, color: "var(--text-2)", marginBottom: 8 }}>Every column matches the manual's Amendment Record table exactly, so nothing shifts. The signature column accepts a drag-and-drop image. New rows auto-number. Saving here does not create a new document version.</div>
      <div style={{ overflowX: "auto" }}>
        <table className="fmt-table"><thead><tr>{amendHeaders.map((h, ci) => <th key={ci} style={{ minWidth: ci === amendSigCol ? 150 : 90 }}>{h}</th>)}<th className="noprint"></th></tr></thead>
          <tbody>{amendRows.map((r, i) => <tr key={i}>
            {amendHeaders.map((h, ci) => <td key={ci}>{ci === amendSigCol
              ? <PersonSignature compact people={people} value={r[ci] || ""} onPick={(id, img) => setAmendCell(i, ci, img)} minWidth={140} />

              : <textarea rows={1} value={r[ci] || ""} onChange={e => setAmendCell(i, ci, e.target.value)} style={{ minWidth: ci === 0 ? 40 : 80, width: "100%" }} />}</td>)}
            <td className="noprint"><button onClick={() => delAmendRow(i)} style={{ color: "var(--danger)" }}>✕</button></td>
          </tr>)}{amendRows.length === 0 && <tr><td colSpan={amendHeaders.length + 1} style={{ color: "var(--text-3)", textAlign: "center" }}>No amendment rows yet. Click "Add row".</td></tr>}</tbody></table>
      </div>
      {err && <div className="err">{err}</div>}
      <div className="row" style={{ marginTop: 10, gap: 8 }}>
        <button className="primary" onClick={saveAmend} disabled={amendSaving}>{amendSaving ? "Saving…" : "Save amendment record"}</button>
        <button onClick={addAmendRow}>Add row</button>
        <button onClick={() => setAmendEditing(false)}>Cancel</button>
      </div>
    </div>}
    {hdrEditing && <div className="card">
      <div className="row" style={{ justifyContent: "space-between", marginBottom: 8 }}>
        <b>Document Control Header — {doc.title} <span className="badge">{doc.doc_code}</span></b>
        <div className="row">
          <button className="primary" onClick={saveHdr} disabled={hdrSaving}>{hdrSaving ? "Saving…" : "Save header"}</button>
          <button onClick={() => setHdrEditing(false)}>Cancel</button>
        </div>
      </div>
      <div style={{ fontSize: 13, color: "var(--text-2)", marginBottom: 10 }}>
        Doc. No. / Doc. Name are controlled by the document (<b>{meta.docNo}</b> · <b>{meta.docName}</b>). Prepared / Approved / Issued By apply to the whole manual — pick a person and their saved signature fills in automatically. Section No., Section, Issue No./Date, Amend No./Date and Page No. are set per section below. Saving here does not create a new document version.
      </div>
      <div className="row" style={{ gap: 24, flexWrap: "wrap", marginBottom: 14 }}>
        {[["prepared_by", "Prepared By"], ["approved_by", "Approved By"], ["issued_by", "Issued By"]].map(([slot, label]) => {
          const s = hdrSigs[slot] || {};
          return <div key={slot} style={{ minWidth: 180 }}>
            <label style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>{label}</label>
            <PersonSignature people={people} value={s.signature || ""} valueId={s.id || ""} onPick={(id, img) => pickHdrSig(slot, id, img)} label={s.name || ""} />
          </div>;
        })}
      </div>
      <div className="row" style={{ gap: 12, flexWrap: "wrap", marginBottom: 14, alignItems: "flex-end" }}>
        <div><label>Default Issue No.</label><input value={hdrDfl.issue_no || ""} onChange={e => setHdrDflCell("issue_no", e.target.value)} placeholder="01" style={{ maxWidth: 90 }} /></div>
        <div><label>Default Issue Date</label><input value={hdrDfl.issue_date || ""} onChange={e => setHdrDflCell("issue_date", e.target.value)} placeholder="05/10/2024" style={{ maxWidth: 130 }} /></div>
        <div><label>Default Amend No.</label><input value={hdrDfl.amend_no || ""} onChange={e => setHdrDflCell("amend_no", e.target.value)} placeholder="00" style={{ maxWidth: 90 }} /></div>
        <div><label>Default Amend Date</label><input value={hdrDfl.amend_date || ""} onChange={e => setHdrDflCell("amend_date", e.target.value)} placeholder="—" style={{ maxWidth: 130 }} /></div>
      </div>
      <div style={{ fontSize: 12, color: "var(--text-3)", marginBottom: 6 }}>Per-section values (leave Issue / Amend / Page blank to inherit the defaults above). One row per heading in this manual. Page No. left blank prints the automatic page number.</div>
      <div style={{ overflowX: "auto" }}>
        <table className="fmt-table"><thead><tr>
          <th style={{ minWidth: 70 }}>Section No.</th><th style={{ minWidth: 160 }}>Section</th>
          <th style={{ minWidth: 70 }}>Issue No.</th><th style={{ minWidth: 110 }}>Issue Date</th>
          <th style={{ minWidth: 70 }}>Amend No.</th><th style={{ minWidth: 110 }}>Amend Date</th><th style={{ minWidth: 60 }}>Page No.</th>
        </tr></thead><tbody>
          {secList.map(s => {
            const r = hdrSecs[s.id] || {}; const ip = { fontSize: 12, width: "100%" };
            return <tr key={s.id}>
              <td><input style={ip} value={r.section_no || ""} onChange={e => setHdrSecCell(s.id, "section_no", e.target.value)} /></td>
              <td><input style={ip} value={r.section || ""} onChange={e => setHdrSecCell(s.id, "section", e.target.value)} /></td>
              <td><input style={ip} value={r.issue_no || ""} onChange={e => setHdrSecCell(s.id, "issue_no", e.target.value)} placeholder={hdrDfl.issue_no || meta.issueNo} /></td>
              <td><input style={ip} value={r.issue_date || ""} onChange={e => setHdrSecCell(s.id, "issue_date", e.target.value)} placeholder={hdrDfl.issue_date || meta.issueDate} /></td>
              <td><input style={ip} value={r.amend_no || ""} onChange={e => setHdrSecCell(s.id, "amend_no", e.target.value)} placeholder={hdrDfl.amend_no || meta.amendNo} /></td>
              <td><input style={ip} value={r.amend_date || ""} onChange={e => setHdrSecCell(s.id, "amend_date", e.target.value)} placeholder={hdrDfl.amend_date || meta.amendDate || "—"} /></td>
              <td><input style={ip} value={r.page_no || ""} onChange={e => setHdrSecCell(s.id, "page_no", e.target.value)} placeholder="auto" /></td>
            </tr>;
          })}
          {secList.length === 0 && <tr><td colSpan={7} style={{ color: "var(--text-3)", textAlign: "center" }}>No sections found (the manual has no headings yet).</td></tr>}
        </tbody></table>
      </div>
      {err && <div className="err">{err}</div>}
      <div className="row" style={{ marginTop: 10, gap: 8 }}>
        <button className="primary" onClick={saveHdr} disabled={hdrSaving}>{hdrSaving ? "Saving…" : "Save header"}</button>
        <button onClick={() => setHdrEditing(false)}>Cancel</button>
      </div>
    </div>}
    {/* Per-page document-control editor (opened from a page's "✎ Edit" button). Edits ONLY this
        section; signatures/defaults/other pages are untouched. Doc No./Name stay document-level. */}
    {secEdit && <div className="modal-overlay noprint" onClick={() => setSecEdit(null)}>
      <div className="modal-box" onClick={e => e.stopPropagation()} style={{ width: 520 }}>
        <div className="row" style={{ justifyContent: "space-between", marginBottom: 6 }}>
          <b>Edit this page’s control box</b>
          <span className="badge">{secEdit.title}</span>
        </div>
        <div style={{ fontSize: 12.5, color: "var(--text-2)", marginBottom: 12 }}>
          Changes apply only to this section’s footer. Doc. No. <b>{meta.docNo}</b> and the signatures stay document-level and unchanged. Leave Issue / Amend / Page blank to inherit the document defaults.
        </div>
        <div className="sec-edit-grid">
          <label>Section No.<input value={secEdit.draft.section_no} onChange={e => setSecCell("section_no", e.target.value)} /></label>
          <label>Section Name<input value={secEdit.draft.section} onChange={e => setSecCell("section", e.target.value)} /></label>
          <label>Issue No.<input value={secEdit.draft.issue_no} onChange={e => setSecCell("issue_no", e.target.value)} placeholder={hdrDflOr("issue_no")} /></label>
          <label>Issue Date<input value={secEdit.draft.issue_date} onChange={e => setSecCell("issue_date", e.target.value)} placeholder={hdrDflOr("issue_date")} /></label>
          <label>Amend No.<input value={secEdit.draft.amend_no} onChange={e => setSecCell("amend_no", e.target.value)} placeholder={hdrDflOr("amend_no")} /></label>
          <label>Amend Date<input value={secEdit.draft.amend_date} onChange={e => setSecCell("amend_date", e.target.value)} placeholder={hdrDflOr("amend_date") || "—"} /></label>
          <label>Page No. (override)<input value={secEdit.draft.page_no} onChange={e => setSecCell("page_no", e.target.value)} placeholder="auto" /></label>
        </div>
        {err && <div className="err" style={{ marginTop: 8 }}>{err}</div>}
        <div className="row" style={{ marginTop: 14, gap: 8, justifyContent: "flex-end" }}>
          <button onClick={() => setSecEdit(null)}>Cancel</button>
          <button className="primary" onClick={saveSecEdit} disabled={secSaving}>{secSaving ? "Saving…" : "Save this page"}</button>
        </div>
      </div>
    </div>}
    {!editing && !amendEditing && can("rulebook", "edit") && (normalized || "").replace(/&nbsp;| /g, " ").toLowerCase().includes("amendment made") &&
      <div className="amend-fab noprint">
        {!tableEditing
          ? <button className="primary" onClick={startTableEdit}>✎ Edit amendment table</button>
          : <><span className="amend-fab-hint">Edit cells; pick a person in the last column to fill their signature, then</span><button className="primary" onClick={saveTableEdit} disabled={savingTable}>{savingTable ? "Saving…" : "Save amendments"}</button><button onClick={addInlineRow}>+ Add row</button><button onClick={cancelTableEdit}>Cancel</button></>}
      </div>}
    {!editing ? (pageView
      ? <div className="doc-layout">
          {toc.length > 2 && <nav className="doc-toc noprint">
            <div className="doc-toc-title">Contents</div>
            <input className="doc-toc-search" placeholder="🔍 Search section…" value={tocQuery} onChange={e => setTocQuery(e.target.value)} />
            {tocShown.map((h, i) => { const pi = hasPages ? qmNavIndexFor(h.text) : null; return <button key={h.id || i} className={"doc-toc-item lvl" + h.level} onClick={() => (pi != null ? jumpToPageIdx(pi) : jumpPaged(h.id))}>{qmNiceLabel(h.text)}</button>; })}
            {tocQuery && tocShown.length === 0 && <div className="doc-toc-empty">No section matches “{tocQuery}”.</div>}
          </nav>}
          <div className="doc-main">
            <div className="doc-paged-bar noprint">
              <div className="doc-paged-bar-row">
                <span className="doc-paged-bar-ttl">Page-by-page view</span>
                <span className="doc-paged-bar-sub">The document-control box appears at the foot of every page — exactly like Print / PDF.</span>
                <button className="link-btn" onClick={() => setPageView(false)}>Switch to continuous view</button>
              </div>
              {can("rulebook", "edit") && <div className="doc-paged-sigs">
                <span className="doc-paged-sigs-ttl">Footer signatures</span>
                {[["prepared_by", "Prepared By"], ["approved_by", "Approved By"], ["issued_by", "Issued By"]].map(([slot, label]) => {
                  const s = sigOf(slot);
                  return <div key={slot} className="doc-paged-sig">
                    <div className="doc-paged-sig-lbl">{label}</div>
                    <PersonSignature compact people={people} value={s.signature || ""} valueId={s.id || ""} onPick={(id, img) => pickSigInline(slot, id, img)} minWidth={150} />
                  </div>;
                })}
                <button className="link-btn doc-paged-sigs-more" onClick={openHdrEditor}>Edit section numbers / issue / amend / page →</button>
              </div>}
            </div>
            <div className="doc-paged">
              {hasPages
                ? <FixedPagesDoc frameId="qm-paged-frame" title={doc.title} code={doc.doc_code} pages={pagesView} footerMeta={meta} minHeight={900} onEdit={can("rulebook", "edit") ? openPageEditor : undefined} onNav={jumpToPageIdx} />
                : <PagedDoc frameId="qm-paged-frame" title={doc.title} code={doc.doc_code} html={pagedHtml} footerMeta={meta} minHeight={900} onEdit={can("rulebook", "edit") ? openSecEditor : undefined} />}
            </div>
          </div>
        </div>
      : <div className="doc-layout">
          {toc.length > 2 && <nav className="doc-toc noprint">
            <div className="doc-toc-title">Contents</div>
            <input className="doc-toc-search" placeholder="🔍 Search section…" value={tocQuery} onChange={e => setTocQuery(e.target.value)} />
            {tocShown.map((h, i) => <button key={h.id || i} className={"doc-toc-item lvl" + h.level} onClick={() => jump(h.id)}>{h.text}</button>)}
            {tocQuery && tocShown.length === 0 && <div className="doc-toc-empty">No section matches “{tocQuery}”.</div>}
          </nav>}
          <div className="doc-main">
            <div className="doc-paged-note noprint">Continuous view (no page breaks). Switch to <button className="link-btn" onClick={() => setPageView(true)}>Page view</button> to see the control box at the foot of every page.</div>
            <div ref={docRef} className="card doc-view" onClick={onDocClick} dangerouslySetInnerHTML={{ __html: viewHtml }} />
          </div>
        </div>)
      : <div className="card">
        <ReactQuill value={content} onChange={setContent} modules={{ toolbar: [["bold", "italic", "underline"], [{ header: [1, 2, 3, false] }], [{ align: [] }], [{ list: "ordered" }, { list: "bullet" }], ["link", "table"], ["clean"]] }} />
        <label>Section / clause changed</label><input value={sc} onChange={e => setSc(e.target.value)} placeholder="e.g. 7.11.2" />
        <label>Change made</label><input value={cm} onChange={e => setCm(e.target.value)} />
        <label>Reason</label><input value={rs} onChange={e => setRs(e.target.value)} />
        {err && <div className="err">{err}</div>}
        <div className="row" style={{ marginTop: 12 }}>
          <button className="primary" onClick={save}>Save new version (sign off)</button>
          <button onClick={() => { setEditing(false); setContent(doc.current_content); }}>Cancel</button>
        </div>
      </div>}
    {viewVer && <div className="modal-overlay" onClick={() => setViewVer(null)}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div className="row" style={{ justifyContent: "space-between", marginBottom: 10 }}>
          <b>{doc.title} — v{viewVer.version_no} {viewVer.status === "obsolete" ? <span className="badge obsolete">OBSOLETE</span> : <span className="badge ok">CURRENT</span>}</b>
          <div className="row"><button onClick={() => printContent(doc.title + " — v" + viewVer.version_no, doc.doc_code, normalizeDocTables(viewVer.content))}>Print / PDF</button><button onClick={() => setViewVer(null)}>Close</button></div>
        </div>
        <div style={{ fontSize: 13, color: "var(--text-2)", marginBottom: 10 }}>
          <div>Change: {(viewVer.amendment && viewVer.amendment.change_made) || "—"} (clause {(viewVer.amendment && viewVer.amendment.section_clause) || "—"})</div>
          <div>Reason: {(viewVer.amendment && viewVer.amendment.reason) || "—"}</div>
          <div>Approved by {viewVer.approved_by || "—"} · {inDate((viewVer.approved_at || "").slice(0, 10))}</div>
        </div>
        <div className="card" style={{ margin: 0, boxShadow: "none" }} dangerouslySetInnerHTML={{ __html: viewVer.content || "" }} />
      </div>
    </div>}
    {pageEdit && <div className="modal-overlay" onClick={() => setPageEdit(null)}>
      <div className="modal-box" onClick={e => e.stopPropagation()} style={{ maxWidth: 820, width: "94%" }}>
        <div className="row" style={{ justifyContent: "space-between", marginBottom: 10 }}>
          <b>Edit page {pageEdit.page || (pageEdit.i + 1)} <span className="badge">sheet {pageEdit.i + 1} of {pages.length}</span></b>
          <button onClick={() => setPageEdit(null)}>Close</button>
        </div>
        <div style={{ fontSize: 12.5, color: "var(--text-2)", marginBottom: 10 }}>These values are the footer document-control fields and content for this one page only. Editing them does not move any other page or change the stored page boundaries.</div>
        <div className="grid-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 14px" }}>
          <div><label>Printed Page No.</label><input value={pageEdit.page || ""} onChange={e => setPageField("page", e.target.value)} placeholder="e.g. 5" /></div>
          <div><label>Section No.</label><input value={pageEdit.section_no || ""} onChange={e => setPageField("section_no", e.target.value)} placeholder="e.g. 7.4" /></div>
          <div style={{ gridColumn: "1 / span 2" }}><label>Section (name)</label><input value={pageEdit.section || ""} onChange={e => setPageField("section", e.target.value)} placeholder="e.g. Handling of test or calibration items" /></div>
          <div><label>Amend. No.</label><input value={pageEdit.amend_no || ""} onChange={e => setPageField("amend_no", e.target.value)} placeholder="00" /></div>
          <div><label>Amend Date</label><input value={pageEdit.amend_date || ""} onChange={e => setPageField("amend_date", e.target.value)} placeholder="dd/mm/yyyy" /></div>
        </div>
        <label className="row" style={{ marginTop: 10, alignItems: "center", gap: 6 }}><input type="checkbox" checked={!!pageEdit.no_footer} onChange={e => setPageField("no_footer", e.target.checked)} style={{ width: "auto" }} /> This is a cover/blank page — hide the document-control footer & CONTROLLED stamp</label>
        <label style={{ marginTop: 10 }}>Amendment note on this page (optional — shown in a highlighted box above the content)</label>
        <textarea value={pageEdit.amendment || ""} onChange={e => setPageField("amendment", e.target.value)} rows={2} placeholder="Leave blank for no amendment on this page." />
        <label style={{ marginTop: 10 }}>Page content (HTML — tables, headings and text for this page)</label>
        <textarea value={pageEdit.html || ""} onChange={e => setPageField("html", e.target.value)} rows={12} style={{ fontFamily: "monospace", fontSize: 12 }} />
        {err && <div className="err">{err}</div>}
        <div className="row" style={{ marginTop: 12 }}>
          <button className="primary" onClick={savePageEditor} disabled={pageSaving}>{pageSaving ? "Saving…" : "Save this page"}</button>
          <button onClick={() => setPageEdit(null)}>Cancel</button>
        </div>
      </div>
    </div>}
  </div>;
}

const ICON_EDIT = <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z" /></svg>;
const ICON_TRASH = <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /><path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" /></svg>;

function ListMaster({ typeKey, label }) {
  const { can } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(false);
  const [val, setVal] = useState("");
  const [editId, setEditId] = useState(null);
  const [editVal, setEditVal] = useState("");
  const load = () => api.master(typeKey).then(setItems);
  useEffect(() => { load(); }, [typeKey]);
  const add = async () => { if (!val) return; await api.addMaster({ list_key: typeKey, value: val, is_active: true }); setVal(""); setAdding(false); load(); };
  const startEdit = (it) => { setEditId(it.id); setEditVal(it.value); };
  const saveEdit = async (it) => { if (!editVal) return; await api.editMaster(it.id, { list_key: typeKey, value: editVal, is_active: it.is_active }); setEditId(null); load(); };
  const toggle = async (it) => { await api.editMaster(it.id, { list_key: typeKey, value: it.value, is_active: !it.is_active }); load(); };
  const del = async (it) => { if (window.confirm("Delete \"" + it.value + "\"?")) { await api.delMaster(it.id); load(); } };
  const rows = items.filter(it => (it.value || "").toLowerCase().includes(q.toLowerCase()));
  return <>
    <div className="row" style={{ justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
      <div className="row" style={{ gap: 8, alignItems: "center" }}>{SIDE_ICONS.list}<b>{label}</b><span style={{ color: "var(--text-2)", fontSize: 13 }}>{rows.length} record{rows.length === 1 ? "" : "s"}</span></div>
      <div className="row" style={{ gap: 8 }}>
        <input placeholder="Search…" value={q} onChange={e => setQ(e.target.value)} style={{ maxWidth: 220 }} />
        {can("masters", "add") && <button className="primary" style={{ whiteSpace: "nowrap" }} onClick={() => { setAdding(true); setVal(""); }}>+ Add</button>}
      </div>
    </div>
    {adding && <div className="row" style={{ marginBottom: 12, gap: 8 }}><input autoFocus value={val} onChange={e => setVal(e.target.value)} placeholder={"New " + label.toLowerCase()} style={{ maxWidth: 320 }} onKeyDown={e => { if (e.key === "Enter") add(); }} /><button className="primary" onClick={add}>Save</button><button onClick={() => setAdding(false)}>Cancel</button></div>}
    <table><thead><tr><th>Value</th><th>Status</th><th style={{ textAlign: "right" }}>Actions</th></tr></thead><tbody>
      {rows.map(it => <tr key={it.id}>
        <td>{editId === it.id ? <input value={editVal} onChange={e => setEditVal(e.target.value)} style={{ maxWidth: 320 }} /> : it.value}</td>
        <td>{can("masters", "edit")
          ? <button onClick={() => toggle(it)} style={{ background: "none", border: "none", padding: 0, minHeight: 0, cursor: "pointer", color: it.is_active ? "var(--success)" : "var(--text-2)" }}>{it.is_active ? "Active" : "Inactive"}</button>
          : <span style={{ color: it.is_active ? "var(--success)" : "var(--text-2)" }}>{it.is_active ? "Active" : "Inactive"}</span>}</td>
        <td style={{ whiteSpace: "nowrap", textAlign: "right" }}>{editId === it.id
          ? <><button className="primary" onClick={() => saveEdit(it)}>Save</button><button onClick={() => setEditId(null)} style={{ marginLeft: 6 }}>Cancel</button></>
          : <>{can("masters", "edit") && <button className="view-btn" title="Edit" aria-label="Edit" onClick={() => startEdit(it)}>{ICON_EDIT}</button>}{can("masters", "delete") && <button className="view-btn" title="Delete" aria-label="Delete" onClick={() => del(it)} style={{ marginLeft: 6, color: "var(--danger)" }}>{ICON_TRASH}</button>}</>}</td>
      </tr>)}
      {rows.length === 0 && <tr><td colSpan={3} style={{ color: "var(--text-2)" }}>No records{q ? " matching “" + q + "”" : " yet"}.</td></tr>}
    </tbody></table>
  </>;
}

function PersonnelMaster() {
  const { can } = useAuth();
  const [items, setItems] = useState([]);
  const [desigs, setDesigs] = useState([]);
  const [form, setForm] = useState(null);
  const [q, setQ] = useState("");
  const load = () => api.personnel().then(setItems);
  useEffect(() => { load(); api.masterPublic("designations").then(d => setDesigs(d.map(x => x.value))); }, []);
  const setF = (k, v) => setForm({ ...form, [k]: v });
  const fileB64 = (file, k) => { const r = new FileReader(); r.onload = () => setF(k, r.result); r.readAsDataURL(file); };
  const save = async () => { if (!form.name) { alert("Name is required"); return; } if (form.id) await api.editPersonnel(form.id, form); else await api.addPersonnel(form); setForm(null); load(); };
  const del = async (id) => { if (window.confirm("Delete this person?")) { await api.delPersonnel(id); load(); } };
  const rows = items.filter(p => (p.name || "").toLowerCase().includes(q.toLowerCase()) || (p.designation || "").toLowerCase().includes(q.toLowerCase()));
  // Upcoming birthdays: anyone whose birthday falls in the next 7 days (the whole week before it).
  // A 15-Aug birthday shows from 8 Aug through 14 Aug — i.e. 1..7 days away (the day itself drops off).
  const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const upcomingBirthdays = React.useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const out = [];
    items.forEach(p => {
      const m = String(p.dob || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (!m) return;
      const mon = +m[2] - 1, day = +m[3];
      let next = new Date(today.getFullYear(), mon, day); next.setHours(0, 0, 0, 0);
      if (next < today) next = new Date(today.getFullYear() + 1, mon, day);
      const days = Math.round((next - today) / 86400000);
      if (days >= 1 && days <= 7) out.push({ ...p, days, mon, day });
    });
    return out.sort((a, b) => a.days - b.days);
  }, [items]);
  return <>
    <div className="row" style={{ justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
      <div className="row" style={{ gap: 8, alignItems: "center" }}>{SIDE_ICONS.users}<b>Personnel</b><span style={{ color: "var(--text-2)", fontSize: 13 }}>{rows.length} record{rows.length === 1 ? "" : "s"}</span></div>
      <div className="row" style={{ gap: 8 }}>
        {!form && <input placeholder="Search…" value={q} onChange={e => setQ(e.target.value)} style={{ maxWidth: 220 }} />}
        {can("masters", "add") && !form && <button className="primary" style={{ whiteSpace: "nowrap" }} onClick={() => setForm({ is_active: true })}>+ Add person</button>}
      </div>
    </div>
    {upcomingBirthdays.length > 0 && <div style={{ marginBottom: 14, padding: "12px 14px", borderRadius: 8, background: "var(--info-bg, #eef4ff)", border: "1px solid var(--border)" }}>
      <div className="row" style={{ gap: 8, alignItems: "center", marginBottom: 8 }}><span style={{ fontSize: 18 }}>🎂</span><b>Upcoming Birthdays</b><span style={{ color: "var(--text-2)", fontSize: 13 }}>next 7 days</span></div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>{upcomingBirthdays.map(p => <div key={p.id} className="row" style={{ gap: 10, alignItems: "center", padding: "6px 10px", borderRadius: 6, background: "var(--surface, #fff)", border: "1px solid var(--border)" }}>
        <b>{p.name}</b>
        {p.designation && <span style={{ color: "var(--text-2)", fontSize: 13 }}>{p.designation}</span>}
        <span style={{ fontSize: 13 }}>{p.day} {MONTHS[p.mon]}</span>
        <span style={{ fontSize: 12, fontWeight: 600, color: "var(--success, #16a34a)" }}>{p.days === 1 ? "tomorrow" : `in ${p.days} days`}</span>
      </div>)}</div>
    </div>}
    {form ? <div>
      <label>Name</label><input value={form.name || ""} onChange={e => setF("name", e.target.value)} style={{ maxWidth: 360 }} />
      <label>Designation</label><select value={form.designation || ""} onChange={e => setF("designation", e.target.value)} style={{ maxWidth: 360 }}><option value="">—</option>{desigs.map(d => <option key={d}>{d}</option>)}</select>
      <label>Qualification</label><input value={form.qualification || ""} onChange={e => setF("qualification", e.target.value)} style={{ maxWidth: 360 }} />
      <label>Experience</label><input value={form.experience || ""} onChange={e => setF("experience", e.target.value)} style={{ maxWidth: 360 }} />
      <label>Date of joining</label><input type="date" value={form.doj || ""} onChange={e => setF("doj", e.target.value)} style={{ maxWidth: 240 }} />
      <label>Date of birth</label><input type="date" value={form.dob || ""} onChange={e => setF("dob", e.target.value)} style={{ maxWidth: 240 }} />
      <label>Signature</label><SignatureInput value={form.signature_image} onChange={v => setF("signature_image", v)} />
      <label>Status</label><ActiveToggle value={form.is_active} onChange={v => setF("is_active", v)} />
      <div className="row" style={{ marginTop: 10 }}><button className="primary" onClick={save}>Save</button><button onClick={() => setForm(null)}>Cancel</button></div>
    </div>
      : <table><thead><tr><th>Name</th><th>Designation</th><th>Qualification</th><th>Signature</th><th>Status</th><th style={{ textAlign: "right" }}>Actions</th></tr></thead><tbody>
        {rows.map(p => <tr key={p.id}><td>{p.name}</td><td>{p.designation}</td><td>{p.qualification}</td><td>{p.signature_image ? <img src={p.signature_image} alt="sig" style={{ height: 26 }} /> : "—"}</td><td><span style={{ color: p.is_active === false ? "var(--text-2)" : "var(--success)" }}>{p.is_active === false ? "Inactive" : "Active"}</span></td>
          <td style={{ whiteSpace: "nowrap", textAlign: "right" }}>
            {can("masters", "edit") && <button className="view-btn" title="Edit" aria-label="Edit" onClick={() => setForm(p)}>{ICON_EDIT}</button>}
            {can("masters", "delete") && <button className="view-btn" title="Delete" aria-label="Delete" onClick={() => del(p.id)} style={{ marginLeft: 6, color: "var(--danger)" }}>{ICON_TRASH}</button>}
          </td></tr>)}
        {rows.length === 0 && <tr><td colSpan={6} style={{ color: "var(--text-2)" }}>No personnel{q ? " matching “" + q + "”" : " yet"}.</td></tr>}
      </tbody></table>}
  </>;
}

function SignatoriesMaster() {
  const { can } = useAuth();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null);
  const load = () => api.signatories().then(setItems);
  useEffect(() => { load(); }, []);
  const setF = (k, v) => setForm({ ...form, [k]: v });
  const save = async () => { if (!form.role) { alert("Role is required"); return; } if (form.id) await api.editSignatory(form.id, form); else await api.addSignatory(form); setForm(null); load(); };
  const del = async (id) => { if (window.confirm("Delete this signatory?")) { await api.delSignatory(id); load(); } };
  return <>
    <div className="row" style={{ justifyContent: "space-between", marginBottom: 8, flexWrap: "wrap", gap: 8 }}>
      <div className="row" style={{ gap: 8, alignItems: "center" }}>{SIDE_ICONS.lock}<b>Authorized Signatories</b><span style={{ color: "var(--text-2)", fontSize: 13 }}>{items.length} record{items.length === 1 ? "" : "s"}</span></div>
      {can("masters", "add") && !form && <button className="primary" style={{ whiteSpace: "nowrap" }} onClick={() => setForm({ is_active: true })}>+ Add signatory</button>}
    </div>
    <div style={{ fontSize: 13, color: "var(--text-2)", marginBottom: 12 }}>Fixed approver signatures (e.g. CEO, Quality Manager). Uploaded once and reused as the approver sign on any document.</div>
    {form ? <div>
      <label>Role</label><input value={form.role || ""} onChange={e => setF("role", e.target.value)} placeholder="e.g. CEO or Quality Manager" style={{ maxWidth: 360 }} />
      <label>Signature</label><SignatureInput value={form.signature_image} onChange={v => setF("signature_image", v)} />
      <label>Status</label><ActiveToggle value={form.is_active} onChange={v => setF("is_active", v)} />
      <div className="row" style={{ marginTop: 10 }}><button className="primary" onClick={save}>Save</button><button onClick={() => setForm(null)}>Cancel</button></div>
    </div>
      : <table><thead><tr><th>Role</th><th>Signature</th><th>Status</th><th style={{ textAlign: "right" }}>Actions</th></tr></thead><tbody>
        {items.map(s => <tr key={s.id}><td>{s.role}</td><td>{s.signature_image ? <img src={s.signature_image} alt="sig" style={{ height: 30 }} /> : "—"}</td><td><span style={{ color: s.is_active === false ? "var(--text-2)" : "var(--success)" }}>{s.is_active === false ? "Inactive" : "Active"}</span></td>
          <td style={{ whiteSpace: "nowrap", textAlign: "right" }}>
            {can("masters", "edit") && <button className="view-btn" title="Edit" aria-label="Edit" onClick={() => setForm(s)}>{ICON_EDIT}</button>}
            {can("masters", "delete") && <button className="view-btn" title="Delete" aria-label="Delete" onClick={() => del(s.id)} style={{ marginLeft: 6, color: "var(--danger)" }}>{ICON_TRASH}</button>}
          </td></tr>)}
        {items.length === 0 && <tr><td colSpan={4} style={{ color: "var(--text-2)" }}>No signatories yet. Add CEO and Quality Manager.</td></tr>}
      </tbody></table>}
  </>;
}

function TestMaster() {
  const { can } = useAuth();
  const [items, setItems] = useState([]);
  const [equip, setEquip] = useState([]);
  const [discs, setDiscs] = useState([]);
  const [form, setForm] = useState(null);
  const [q, setQ] = useState("");
  const load = () => api.c4List("test_master").then(setItems);
  useEffect(() => {
    load();
    api.c4List("equipment_list").then(l => setEquip(l.map(e => e.name).filter(Boolean))).catch(() => {});
    api.masterPublic("disciplines").then(d => setDiscs(d.map(x => x.value))).catch(() => {});
  }, []);
  const setF = (k, v) => setForm({ ...form, [k]: v });
  const toggleEq = (name) => {
    const cur = (form.equipment || "").split(";").map(x => x.trim()).filter(Boolean);
    const next = cur.includes(name) ? cur.filter(x => x !== name) : [...cur, name];
    setF("equipment", next.join("; "));
  };
  const save = async () => {
    if (!form.test || !form.standard_no) { alert("Test name and Standard No. are required"); return; }
    if (form.id) await api.c4Edit("test_master", form.id, form); else await api.c4Add("test_master", form);
    setForm(null); load();
  };
  const del = async (id) => { if (window.confirm("Delete this test?")) { await api.c4Del("test_master", id); load(); } };
  const rows = items.filter(t => ((t.test || "") + (t.standard_no || "")).toLowerCase().includes(q.toLowerCase()));
  return <>
    <div className="row" style={{ justifyContent: "space-between", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
      <div className="row" style={{ gap: 8 }}><b>Tests & Standards</b><span style={{ color: "var(--text-2)", fontSize: 13 }}>{rows.length} test{rows.length === 1 ? "" : "s"}</span></div>
      <div className="row" style={{ gap: 8 }}>
        {!form && <input placeholder="Search…" value={q} onChange={e => setQ(e.target.value)} style={{ maxWidth: 200 }} />}
        {can("clause_documents", "add") && !form && <button className="primary" onClick={() => setForm({ nabl_scope: "Yes" })}>+ Add test</button>}
      </div>
    </div>
    <div style={{ fontSize: 13, color: "var(--text-2)", marginBottom: 10 }}>
      The backbone registry: every test the laboratory performs, the standard it follows, and the equipment it runs on.
      The Test Standard File (7.2F-02) and MU records (7.6F-01) can be generated from here.
    </div>
    {form ? <div>
      <label>Test / Parameter</label><input value={form.test || ""} onChange={e => setF("test", e.target.value)} style={{ maxWidth: 420 }} />
      <label>Discipline</label><select value={form.discipline || ""} onChange={e => setF("discipline", e.target.value)} style={{ maxWidth: 260 }}><option value="">—</option>{discs.map(d => <option key={d}>{d}</option>)}</select>
      <label>Standard No. (IS / ASTM / …)</label><input value={form.standard_no || ""} onChange={e => setF("standard_no", e.target.value)} style={{ maxWidth: 320 }} />
      <label>Standard title</label><input value={form.standard_title || ""} onChange={e => setF("standard_title", e.target.value)} style={{ maxWidth: 520 }} />
      <label>Equipment used (from 6.4F-01)</label>
      <div className="row" style={{ flexWrap: "wrap", gap: 8, maxWidth: 720 }}>
        {equip.map(name => <label key={name} style={{ display: "flex", alignItems: "center", gap: 5, fontWeight: 400, fontSize: 13, border: "1px solid var(--border)", borderRadius: 14, padding: "3px 10px", cursor: "pointer", background: (form.equipment || "").includes(name) ? "var(--info-bg)" : "transparent" }}>
          <input type="checkbox" style={{ width: "auto", minHeight: 0 }} checked={(form.equipment || "").includes(name)} onChange={() => toggleEq(name)} />{name}</label>)}
        {equip.length === 0 && <span style={{ fontSize: 13, color: "var(--text-2)" }}>No equipment in 6.4F-01 yet.</span>}
      </div>
      <label>Under NABL scope</label><select value={form.nabl_scope || "Yes"} onChange={e => setF("nabl_scope", e.target.value)} style={{ maxWidth: 160 }}><option>Yes</option><option>No</option></select>
      <label>Remarks</label><input value={form.remarks || ""} onChange={e => setF("remarks", e.target.value)} style={{ maxWidth: 520 }} />
      <div className="row" style={{ marginTop: 10 }}><button className="primary" onClick={save}>Save</button><button onClick={() => setForm(null)}>Cancel</button></div>
    </div>
    : <table><thead><tr><th>Test / Parameter</th><th>Discipline</th><th>Standard</th><th>Equipment</th><th>NABL scope</th><th style={{ textAlign: "right" }}>Actions</th></tr></thead><tbody>
      {rows.map(t => <tr key={t.id}>
        <td style={{ fontWeight: 500 }}>{t.test}</td><td>{t.discipline}</td>
        <td>{t.standard_no}{t.standard_title ? <div style={{ fontSize: 12, color: "var(--text-2)" }}>{t.standard_title}</div> : null}</td>
        <td style={{ fontSize: 13 }}>{t.equipment}</td>
        <td>{t.nabl_scope === "No" ? <span className="badge warn">No</span> : <span className="badge ok">Yes</span>}</td>
        <td style={{ whiteSpace: "nowrap", textAlign: "right" }}>
          {can("clause_documents", "edit") && <button onClick={() => setForm(t)}>Edit</button>}
          {can("clause_documents", "delete") && <button onClick={() => del(t.id)} style={{ marginLeft: 6, color: "var(--danger)" }}>Delete</button>}
        </td></tr>)}
      {rows.length === 0 && <tr><td colSpan={6} style={{ color: "var(--text-2)" }}>No tests yet. Add every test the lab performs.</td></tr>}
    </tbody></table>}
  </>;
}

function Masters() {
  const MASTER_TYPES = [
    { key: "personnel", label: "Personnel", kind: "personnel" },
    { key: "test_master", label: "Tests & Standards", kind: "tests" },
    { key: "signatories", label: "Authorized Signatories", kind: "signatories" },
    { key: "designations", label: "Designations", kind: "list" },
    { key: "departments", label: "Departments", kind: "list" },
    { key: "disciplines", label: "Disciplines", kind: "list" },
    { key: "equipment_categories", label: "Equipment Categories", kind: "list" },
    { key: "test_methods", label: "Test Methods", kind: "list" },
    { key: "training_subjects", label: "Training Subjects", kind: "list" },
    { key: "training_agencies", label: "Training Agencies", kind: "list" },
    { key: "risk_categories", label: "Risk Categories", kind: "list" },
  ];
  const [active, setActive] = useState("personnel");
  const cur = MASTER_TYPES.find(t => t.key === active) || MASTER_TYPES[0];
  return <div className="main">
    <div className="topbar" style={{ display: "block" }}><h2>Masters</h2><div style={{ color: "var(--text-2)", fontSize: 13, marginTop: 2 }}>Manage the app's core reference data</div></div>
    <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
      <div className="card" style={{ flex: "0 0 210px", padding: 8 }}>
        {MASTER_TYPES.map(t => <div key={t.key} onClick={() => setActive(t.key)} style={{ display: "flex", alignItems: "center", gap: 9, padding: "9px 12px", borderRadius: 8, cursor: "pointer", marginBottom: 2, fontWeight: active === t.key ? 600 : 400, background: active === t.key ? "var(--brand)" : "transparent", color: active === t.key ? "var(--brand-ink)" : "inherit" }}>
          {t.kind === "personnel" ? SIDE_ICONS.users : t.kind === "signatories" ? SIDE_ICONS.lock : SIDE_ICONS.list}<span>{t.label}</span>
        </div>)}
      </div>
      <div className="card" style={{ flex: 1, minWidth: 320 }}>
        {cur.kind === "personnel" ? <PersonnelMaster /> : cur.kind === "tests" ? <TestMaster /> : cur.kind === "signatories" ? <SignatoriesMaster /> : <ListMaster key={cur.key} typeKey={cur.key} label={cur.label} />}
      </div>
    </div>
  </div>;
}

function Roles() {
  const { can } = useAuth();
  const [roles, setRoles] = useState([]); const [meta, setMeta] = useState({ areas: [], actions: [] });
  const [edit, setEdit] = useState(null);
  const [ensuring, setEnsuring] = useState(false);
  const load = () => api.roles().then(setRoles);
  useEffect(() => { load(); api.meta().then(setMeta); }, []);
  const startNew = () => setEdit({ name: "", permissions: {}, approve_as: [] });
  const toggle = (area, action) => {
    const p = { ...(edit.permissions || {}) }; const cur = new Set(p[area] || []);
    cur.has(action) ? cur.delete(action) : cur.add(action); p[area] = [...cur];
    setEdit({ ...edit, permissions: p });
  };
  const toggleSign = (stage) => {
    const cur = new Set(edit.approve_as || []);
    cur.has(stage) ? cur.delete(stage) : cur.add(stage);
    setEdit({ ...edit, approve_as: [...cur] });
  };
  const save = async () => {
    const body = { name: edit.name, permissions: edit.permissions, approve_as: edit.approve_as || [] };
    if (edit.id) await api.updateRole(edit.id, body);
    else await api.createRole(body);
    setEdit(null); load();
  };
  const ensure = async () => {
    if (!window.confirm("Create any missing standard roles from the org chart (TM Mechanical/Chemical, Testing Engineers, In-Charge Customer Support, Analyst) and grant signing rights to CEO / QM / TM roles? Existing roles are never renamed and their other permissions are untouched.")) return;
    setEnsuring(true);
    try {
      const r = await api.ensureStandardRoles();
      alert("Created: " + ((r.created || []).join(", ") || "none (all present)") + "\nEnriched: " + ((r.updated || []).join(", ") || "none"));
      load();
    } catch (e) { alert(e.message); }
    setEnsuring(false);
  };
  const SIGN_STAGES = [["tm", "Technical Manager"], ["qm", "Quality Manager"], ["ceo", "CEO"]];
  const signBadges = (r) => (r.approve_as || []).map(s => (SIGN_STAGES.find(x => x[0] === s) || [s, s])[1]).join(", ");
  return <div className="main"><div className="topbar"><h2>RBAC</h2>
    <div className="row">
      {can("roles", "add") && <button onClick={ensure} disabled={ensuring}>{ensuring ? "Creating…" : "Add standard roles (org chart)"}</button>}
      {can("roles", "add") && <button className="primary" onClick={startNew}>New role</button>}
    </div></div>
    {!edit ? <div className="card"><table><thead><tr><th>Role</th><th>Default</th><th>Can sign as</th><th></th></tr></thead><tbody>
      {roles.map(r => <tr key={r.id}><td>{r.name}</td><td>{r.is_system_default ? "Yes" : "No"}</td>
        <td style={{ fontSize: 13, color: "var(--text-2)" }}>{signBadges(r) || "—"}</td>
        <td>{can("roles", "edit") && <button onClick={() => setEdit({ approve_as: [], ...r })}>Edit permissions</button>}</td></tr>)}
    </tbody></table></div>
      : <div className="card">
        <label>Role name</label><input value={edit.name} onChange={e => setEdit({ ...edit, name: e.target.value })} style={{ maxWidth: 320 }} />
        <label style={{ marginTop: 10, display: "block" }}>Approval signing rights (which sign-off stages this role can approve)</label>
        <div className="row" style={{ flexWrap: "wrap", gap: 14, marginTop: 4 }}>
          {SIGN_STAGES.map(([k, label]) => <label key={k} style={{ display: "flex", alignItems: "center", gap: 5, fontWeight: 400 }}>
            <input type="checkbox" style={{ width: "auto", minHeight: 0 }} checked={(edit.approve_as || []).includes(k)} onChange={() => toggleSign(k)} /> Sign as {label}</label>)}
        </div>
        <div style={{ fontSize: 12, color: "var(--text-2)", marginTop: 4 }}>Signing also requires the clause_documents "approve" permission below, and the user's uploaded signature.</div>
        <table className="perm-table" style={{ marginTop: 14 }}><thead><tr><th>Area</th>{meta.actions.map(a => <th key={a}>{a}</th>)}</tr></thead><tbody>
          {meta.areas.map(area => <tr key={area}><td>{area}</td>{meta.actions.map(a => <td key={a}>
            <input type="checkbox" style={{ width: "auto" }} checked={(edit.permissions[area] || []).includes(a)} onChange={() => toggle(area, a)} /></td>)}</tr>)}
        </tbody></table>
        <div className="row" style={{ marginTop: 12 }}><button className="primary" onClick={save}>Save</button><button onClick={() => setEdit(null)}>Cancel</button></div>
      </div>}
  </div>;
}

function Users() {
  const { can } = useAuth();
  const [users, setUsers] = useState([]); const [roles, setRoles] = useState([]); const [desigs, setDesigs] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.users().then(setUsers);
  useEffect(() => { load(); api.roles().then(setRoles); api.masterPublic("designations").then(d => setDesigs(d.map(x => x.value))); }, []);
  const startNew = () => setEdit({ username: "", password: "", full_name: "", designation: "", role_id: "", is_active: true });
  const save = async () => {
    if (edit.id) await api.updateUser(edit.id, edit); else await api.createUser(edit);
    setEdit(null); load();
  };
  const sig = async (e, id) => { const fd = new FormData(); fd.append("file", e.target.files[0]); await api.uploadSig(id, fd); alert("Signature uploaded"); };
  return <div className="main"><div className="topbar"><h2>Users</h2>{can("users", "add") && <button className="primary" onClick={startNew}>New user</button>}</div>
    {!edit ? <div className="card"><table><thead><tr><th>Username</th><th>Name</th><th>Role</th><th>Active</th><th></th></tr></thead><tbody>
      {users.map(u => <tr key={u.id}><td>{u.username}</td><td>{u.full_name}</td><td>{roles.find(r => r.id === u.role_id)?.name}</td><td>{u.is_active ? "Yes" : "No"}</td>
        <td>{can("users", "edit") && <button onClick={() => setEdit({ ...u, password: "" })}>Edit</button>}</td></tr>)}
    </tbody></table></div>
      : <div className="card">
        <label>Username</label><input value={edit.username} disabled={!!edit.id} onChange={e => setEdit({ ...edit, username: e.target.value })} style={{ maxWidth: 320 }} />
        <label>Password {edit.id && "(leave blank to keep)"}</label><input type="password" value={edit.password} onChange={e => setEdit({ ...edit, password: e.target.value })} style={{ maxWidth: 320 }} />
        <label>Full name</label><input value={edit.full_name} onChange={e => setEdit({ ...edit, full_name: e.target.value })} style={{ maxWidth: 320 }} />
        <label>Designation</label><select value={edit.designation} onChange={e => setEdit({ ...edit, designation: e.target.value })} style={{ maxWidth: 320 }}><option value="">—</option>{desigs.map(d => <option key={d}>{d}</option>)}</select>
        <label>Role</label><select value={edit.role_id} onChange={e => setEdit({ ...edit, role_id: e.target.value })} style={{ maxWidth: 320 }}><option value="">—</option>{roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}</select>
        <label>Active</label><select value={edit.is_active ? "1" : "0"} onChange={e => setEdit({ ...edit, is_active: e.target.value === "1" })} style={{ maxWidth: 320 }}><option value="1">Yes</option><option value="0">No</option></select>
        {edit.id && <><label>Signature image</label><input type="file" onChange={e => sig(e, edit.id)} /></>}
        <div className="row" style={{ marginTop: 12 }}><button className="primary" onClick={save}>Save</button><button onClick={() => setEdit(null)}>Cancel</button></div>
      </div>}
  </div>;
}

function Company() {
  const { can } = useAuth();
  const [c, setC] = useState(null);
  const [people, setPeople] = useState([]);
  useEffect(() => { api.company().then(setC); api.personnelPublic().then(setPeople).catch(() => {}); }, []);
  if (!c) return <div className="main">Loading…</div>;
  const f = (k, label) => <><label>{label}</label><input value={c[k] || ""} disabled={!can("company", "edit")} onChange={e => setC({ ...c, [k]: e.target.value })} style={{ maxWidth: 420 }} /></>;
  const pickHolder = (id) => { const p = people.find(x => x.id === id); setC({ ...c, holder_personnel_id: id, holder_name: p ? p.name : "" }); };
  const save = async () => { await api.saveCompany(c); alert("Saved"); };
  return <div className="main"><div className="topbar"><h2>Company</h2></div>
    <div className="card">
      {f("legal_name", "Legal name")}{f("llp_number", "LLP number")}{f("registered_address", "Registered address")}
      {f("contact_no", "Contact no.")}{f("email", "Email")}{f("established_year", "Established year")}
      {can("company", "edit") && <div style={{ marginTop: 12 }}><button className="primary" onClick={save}>Save</button></div>}
    </div>
    <div className="card">
      <b>Document control</b>
      <div style={{ fontSize: 13, color: "var(--text-2)", margin: "4px 0 6px" }}>
        Holder of the controlled copies. Selected once here, the name appears automatically on the
        cover ("Holder Name") of the Quality Manual, Procedure Manual and Format Manual.
      </div>
      <label>Controlled-copy holder</label>
      <select value={c.holder_personnel_id || ""} disabled={!can("company", "edit")} onChange={e => pickHolder(e.target.value)} style={{ maxWidth: 360 }}>
        <option value="">— select personnel —</option>
        {people.map(p => <option key={p.id} value={p.id}>{p.name}{p.designation ? " (" + p.designation + ")" : ""}</option>)}
      </select>
      {c.holder_name && <div style={{ fontSize: 13, marginTop: 6 }}>Current holder: <b>{c.holder_name}</b></div>}
      {can("company", "edit") && <div style={{ marginTop: 12 }}><button className="primary" onClick={save}>Save</button></div>}
    </div>
  </div>;
}

const ORG_DEFAULT = [
  { node_id: "root", title: "SANBER LABS LLP", person_name: "", parent_node_id: "" },
  { node_id: "ceo", title: "CEO/QUALITY MANAGER", person_name: "", parent_node_id: "root" },
  { node_id: "tmm", title: "TECHNICAL MANAGER", person_name: "Mechanical", parent_node_id: "ceo" },
  { node_id: "tmc", title: "TECHNICAL MANAGER", person_name: "Chemical", parent_node_id: "ceo" },
  { node_id: "ics", title: "IN-CHARGE CUSTOMER SUPPORT", person_name: "", parent_node_id: "ceo" },
  { node_id: "tem", title: "Testing Engineer", person_name: "Mechanical", parent_node_id: "tmm" },
  { node_id: "tec", title: "Testing Engineer", person_name: "Chemical", parent_node_id: "tmc" },
];
const ORG_CSS = ".orgtree{padding:10px 0;text-align:center;overflow-x:auto}.orgtree ul{position:relative;padding-top:20px;display:inline-flex;justify-content:center;margin:0;padding-left:0;list-style:none}.orgtree li{position:relative;padding:20px 10px 0;display:flex;flex-direction:column;align-items:center}.orgtree li::before,.orgtree li::after{content:'';position:absolute;top:0;right:50%;border-top:1.5px solid #444;width:50%;height:20px}.orgtree li::after{right:auto;left:50%;border-left:1.5px solid #444}.orgtree li:only-child::after,.orgtree li:only-child::before{display:none}.orgtree li:only-child{padding-top:0}.orgtree li:first-child::before,.orgtree li:last-child::after{border:0 none}.orgtree li:last-child::before{border-right:1.5px solid #444}.orgtree ul ul::before{content:'';position:absolute;top:0;left:50%;border-left:1.5px solid #444;width:0;height:20px}.orgtree>ul{padding-top:0}.orgbox{border:1px solid #333;background:#fff;padding:8px 16px;min-width:120px;display:inline-block;color:#111}.orgbox .t{font-weight:700;font-size:13px}.orgbox .s{font-size:12px}.orgoval{border:1.5px solid #333;border-radius:50%;padding:16px 38px;background:#fff;font-weight:700;font-size:14px;display:inline-block;color:#111}";

function OrgChart() {
  const { can } = useAuth();
  const [o, setO] = useState({ nodes: [] });
  const [edit, setEdit] = useState(false);
  const [draft, setDraft] = useState([]);
  const load = () => api.org().then(d => setO(d && d.nodes ? d : { nodes: [] }));
  useEffect(() => { load(); }, []);
  const nodes = (o.nodes && o.nodes.length) ? o.nodes : ORG_DEFAULT;
  const kids = (pid) => nodes.filter(n => (n.parent_node_id || "") === pid);
  const renderNode = (n, isRoot) => {
    const ch = kids(n.node_id);
    return <li key={n.node_id}>
      <div className={isRoot ? "orgoval" : "orgbox"}>
        <div className="t">{n.title}</div>{n.person_name ? <div className="s">{n.person_name}</div> : null}
      </div>
      {ch.length > 0 && <ul>{ch.map(c => renderNode(c, false))}</ul>}
    </li>;
  };
  const startEdit = () => { setDraft(nodes.map(n => ({ ...n }))); setEdit(true); };
  const add = () => setDraft([...draft, { node_id: "n" + Date.now(), title: "", person_name: "", parent_node_id: "" }]);
  const upd = (i, k, v) => setDraft(draft.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const del = (i) => setDraft(draft.filter((_, idx) => idx !== i));
  const saveEdit = async () => { await api.saveOrg({ nodes: draft }); setEdit(false); load(); };
  const roots = kids("");
  return <div className="main">
    <div className="topbar" style={{ display: "block" }}><h2>Org chart <span className="badge">5.5A</span></h2></div>
    <div className="card">
      <div className="row" style={{ justifyContent: "space-between", marginBottom: 8, flexWrap: "wrap", gap: 8 }}>
        <span style={{ color: "var(--text-2)", fontSize: 13 }}>5.5 a) the organization chart of the laboratory is as given below:</span>
        <div className="row" style={{ gap: 8 }}>
          <button onClick={() => printFormat("orgchart-doc")}>Print / PDF</button>
          {can("org_chart", "edit") && !edit && <button className="primary" onClick={startEdit}>Edit</button>}
        </div>
      </div>
      {edit ? <div>
        <table><thead><tr><th>Title</th><th>Second line</th><th>Reports to</th><th></th></tr></thead><tbody>
          {draft.map((n, i) => <tr key={n.node_id}>
            <td><input value={n.title} onChange={e => upd(i, "title", e.target.value)} /></td>
            <td><input value={n.person_name} onChange={e => upd(i, "person_name", e.target.value)} placeholder="e.g. Mechanical / person name" /></td>
            <td><select value={n.parent_node_id || ""} onChange={e => upd(i, "parent_node_id", e.target.value)}><option value="">(top)</option>{draft.filter(x => x.node_id !== n.node_id).map(x => <option key={x.node_id} value={x.node_id}>{x.title || x.person_name}</option>)}</select></td>
            <td><button onClick={() => del(i)}>Remove</button></td></tr>)}
        </tbody></table>
        <div className="row" style={{ marginTop: 12 }}><button onClick={add}>Add node</button><button className="primary" onClick={saveEdit}>Save</button><button onClick={() => setEdit(false)}>Cancel</button></div>
      </div>
        : <div id="orgchart-doc" className="fmt-doc">
          <FormatHeader code="5.5A" title="ORGANIZATION CHART OF THE LABORATORY" />
          <style>{ORG_CSS}</style>
          <div className="orgtree"><ul>{roots.map(r => renderNode(r, true))}</ul></div>
        </div>}
    </div>
  </div>;
}

function RecycleBin() {
  const { can } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const load = () => api.recycleBin().then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);
  const restore = async (it) => {
    if (!window.confirm("Restore this deleted " + (it.source_collection || "record") + "? It will reappear in its original register.")) return;
    try { await api.recycleRestore(it.id); load(); alert("Restored."); } catch (e) { alert(e.message); }
  };
  const label = (rec) => {
    if (!rec) return "";
    const keys = ["name", "test", "nc_noticed", "unique_id", "vendor_name", "customer_name", "personnel_name", "employee_name", "title", "risk", "audit_no", "cert_no", "equipment_name", "username", "doc_no"];
    for (const k of keys) { if (rec[k]) return String(rec[k]).slice(0, 70); }
    return rec._id ? ("id " + (rec._id.$oid || rec._id)) : "(record)";
  };
  const rows = items.filter(it => ((it.source_collection || "") + " " + label(it.record)).toLowerCase().includes(q.toLowerCase()));
  return <div className="main"><div className="topbar" style={{ display: "block" }}>
      <h2>Recycle bin</h2>
      <div style={{ color: "var(--text-2)", fontSize: 13, marginTop: 2 }}>Deleted records are retained here (ISO/IEC 17025 §8.4 — records are never hard-deleted). Showing the most recent 200.</div>
    </div>
    <div className="card">
      <div className="row" style={{ justifyContent: "flex-end", marginBottom: 10 }}><input placeholder="Search…" value={q} onChange={e => setQ(e.target.value)} style={{ maxWidth: 240 }} /></div>
      <table><thead><tr><th>Record type</th><th>Identifier</th><th>Deleted by</th><th>When</th><th style={{ textAlign: "right" }}></th></tr></thead>
        <tbody>{rows.map(it => <tr key={it.id}>
          <td>{it.source_collection}</td>
          <td>{label(it.record)}</td>
          <td>{it.deleted_by}</td>
          <td>{(it.deleted_at || "").slice(0, 16).replace("T", " ")}</td>
          <td style={{ textAlign: "right" }}>{can("backup", "run_restore") && <button onClick={() => restore(it)}>Restore</button>}</td>
        </tr>)}
        {rows.length === 0 && <tr><td colSpan={5} style={{ color: "var(--text-2)" }}>Recycle bin is empty.</td></tr>}</tbody></table>
    </div>
  </div>;
}

function Backup() {
  const { can } = useAuth();
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => api.backups().then(setList);
  useEffect(() => { load(); }, []);
  const download = async () => {
    setBusy(true);
    try {
      const meta = await api.runBackup();                 // creates the backup server-side; returns metadata only
      const blob = await api.downloadBackupBlob(meta.id); // stream the stored file down as a blob (gzip)
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = meta.filename; a.click();
      load();
    } catch (e) { alert(e.message); }
    setBusy(false);
  };
  const downloadOld = async (b) => {
    try {
      const blob = await api.downloadBackupBlob(b.id);    // stream stored backup down as a blob (gzip)
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = b.filename; a.click();
    } catch (e) { alert(e.message); }
  };
  const delBackup = async (b) => {
    if (!window.confirm("Delete backup " + b.filename + "? The stored copy is removed permanently.")) return;
    try { await api.deleteBackup(b.id); load(); } catch (e) { alert(e.message); }
  };
  const restore = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (!window.confirm("This will REPLACE all current data with the uploaded backup. Continue?")) { e.target.value = ""; return; }
    const fd = new FormData(); fd.append("file", f);
    try {
      const r = await api.restore(fd);
      const integ = r && r.integrity;
      const msg = integ
        ? (integ.ok ? "Integrity check: all row counts match."
          : "INTEGRITY WARNINGS: " + (integ.mismatches || []).map(m => m.collection + " expected " + m.expected + " got " + m.actual).join("; ")
            + (integ.users_without_role ? "; " + integ.users_without_role + " user(s) missing a role" : ""))
        : "";
      alert("Restored " + ((r && r.restored) || []).length + " collections. " + msg + " Reloading.");
      window.location.reload();
    } catch (x) { alert(x.message); e.target.value = ""; }
  };
  const migrate = async () => {
    if (!window.confirm("Move all file attachments stored inside records (certificates, PO copies, report files) into GridFS file storage? The server refuses unless a stored backup from the last 24 hours exists. Re-runnable; already-migrated files are skipped.")) return;
    setBusy(true);
    try { const r = await api.migrateFiles(); alert("Migrated " + r.files + " file(s), " + (r.bytes / 1048576).toFixed(1) + " MB, across " + r.records + " record(s)."); } catch (e) { alert(e.message); }
    setBusy(false);
  };
  const fmtSize = (n) => n ? (n > 1048576 ? (n / 1048576).toFixed(1) + " MB" : Math.round(n / 1024) + " KB") : "—";
  return <div className="main"><div className="topbar"><h2>Backup & restore</h2></div>
    <div className="card">
      {can("backup", "run_backup") && <button className="primary" onClick={download} disabled={busy}>{busy ? "Backing up…" : "Download full backup"}</button>}
      <div style={{ fontSize: 13, color: "var(--text-2)", marginTop: 8 }}>
        Backups are stored on the server and also run automatically once every 24 hours.
        Only the newest 5 are kept — older ones are pruned automatically. Entries without a size predate
        server-side storage (metadata only).
      </div>
      {can("backup", "run_restore") && <div style={{ marginTop: 14 }}>
        <button onClick={migrate} disabled={busy}>Migrate file attachments to GridFS</button>
        <div style={{ fontSize: 12, color: "var(--text-2)", marginTop: 4 }}>One-time storage optimisation: moves uploaded certificates / PO copies / report files out of the record documents. Blocked unless a stored backup from the last 24 hours exists.</div>
      </div>}
      {can("backup", "run_restore") && <div style={{ marginTop: 14 }}>
        <label>Restore from file (replaces all data; a row-count integrity report is shown after)</label>
        <label className="file-btn">Choose backup file to restore<input type="file" accept="application/json" onChange={restore} style={{ display: "none" }} /></label>
      </div>}
      <h4>Past backups</h4>
      <table><thead><tr><th>File</th><th>By</th><th>When</th><th>Size</th><th style={{ textAlign: "right" }}></th></tr></thead><tbody>
        {list.map(b => <tr key={b.id}>
          <td>{b.filename}</td><td>{b.created_by}</td><td>{(b.created_at || "").slice(0, 16).replace("T", " ")}</td><td>{fmtSize(b.size)}</td>
          <td style={{ whiteSpace: "nowrap", textAlign: "right" }}>
            {b.gridfs_id && can("backup", "run_backup") && <button onClick={() => downloadOld(b)}>Download</button>}
            {can("backup", "delete") && <button onClick={() => delBackup(b)} style={{ marginLeft: 6, color: "var(--danger)" }}>Delete</button>}
          </td>
        </tr>)}
        {list.length === 0 && <tr><td colSpan={5} style={{ color: "var(--text-2)" }}>No backups yet.</td></tr>}
      </tbody></table>
    </div></div>;
}

function ThemeToggle() {
  const [theme, setTheme] = useState(() => document.documentElement.getAttribute("data-theme") || "light");
  const flip = () => { const t = theme === "dark" ? "light" : "dark"; document.documentElement.setAttribute("data-theme", t); localStorage.setItem("theme", t); setTheme(t); };
  return <button className="icon-btn" onClick={flip} title="Toggle theme" aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}>
    {theme === "dark"
      ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></svg>
      : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" /></svg>}
  </button>;
}

const SEV_COLOR = { CRITICAL: "#dc2626", WARNING: "#ea580c", REMINDER: "#ca8a04", INFO: "#16a34a" };
const SEV_DOT = (s) => <span style={{ display: "inline-block", width: 9, height: 9, borderRadius: "50%", background: SEV_COLOR[s] || "#64748b", flex: "0 0 auto" }} />;

// Bell + dropdown of recent alerts. Polls the unread count. Opening a record navigates to its module.
function NotificationBell() {
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const nav = useNavigate();
  const loadCount = () => api.notifUnread().then(r => setCount(r.count || 0)).catch(() => {});
  useEffect(() => { loadCount(); const t = setInterval(loadCount, 60000); return () => clearInterval(t); }, []);
  const openPanel = () => { setOpen(o => !o); if (!open) api.notifications("limit=8").then(setItems).catch(() => {}); };
  const markRead = async (n) => { if (!n.is_read) { try { await api.notifRead(n.id); loadCount(); } catch (e) {} } };
  return <div style={{ position: "relative" }}>
    <button onClick={openPanel} title="QMS Alert Center" aria-label="Notifications" style={{ position: "relative", background: "transparent", border: "none", cursor: "pointer", fontSize: 20, padding: "4px 6px" }}>
      🔔{count > 0 && <span style={{ position: "absolute", top: -2, right: -2, background: "#dc2626", color: "#fff", borderRadius: 10, fontSize: 11, fontWeight: 700, minWidth: 18, height: 18, lineHeight: "18px", textAlign: "center", padding: "0 4px" }}>{count > 99 ? "99+" : count}</span>}
    </button>
    {open && <div style={{ position: "absolute", right: 0, top: 40, zIndex: 1300, width: 360, maxWidth: "90vw", maxHeight: 460, overflowY: "auto", background: "var(--surface,#fff)", border: "1px solid var(--border)", borderRadius: 12, boxShadow: "0 12px 40px rgba(0,0,0,0.22)" }}>
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center", padding: "10px 12px", borderBottom: "1px solid var(--border)" }}>
        <b>QMS Alert Center</b>
        <button className="noprint" style={{ fontSize: 12 }} onClick={async () => { await api.notifReadAll(); loadCount(); api.notifications("limit=8").then(setItems); }}>Mark all read</button>
      </div>
      {items.length === 0 && <div style={{ padding: 16, color: "var(--text-2)", fontSize: 14 }}>No alerts.</div>}
      {items.map(n => <div key={n.id} onClick={() => markRead(n)} style={{ display: "flex", gap: 8, padding: "10px 12px", borderBottom: "1px solid var(--border)", background: n.is_read ? "transparent" : "var(--info-bg,#eef4ff)", cursor: "default" }}>
        <div style={{ marginTop: 4 }}>{SEV_DOT(n.severity)}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>{n.title}</div>
          <div style={{ fontSize: 12, color: "var(--text-2)", whiteSpace: "normal" }}>{n.message}</div>
          <div style={{ fontSize: 11, color: "var(--text-2)", marginTop: 2 }}>{n.module}</div>
        </div>
      </div>)}
      <div style={{ padding: 10, textAlign: "center", borderTop: "1px solid var(--border)" }}>
        <button className="noprint" onClick={() => { setOpen(false); nav("/alerts"); }}>Open Alert Center →</button>
      </div>
    </div>}
  </div>;
}

function TopBar() {
  const { user, logout, logo } = useAuth();
  return <div className="topbar2">
    {/* Switch to the LEAMS dashboard — a separate app served under /leams on this same origin,
        so a full-page navigation, not a React route (hence a plain <a>, not NavLink). */}
    <div className="left">
      <a className="ext-link" href="/leams/" title="Switch to the LIMS dashboard">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg>
        <span className="lbl">Switch to LIMS</span>
      </a>
    </div>
    <div className="right">
      <div className="who"><b>{user.full_name}</b>{user.role?.name}</div>
      <NotificationBell />
      <ThemeToggle />
      <button onClick={logout}>Log out</button>
      {logo ? <img className="logo" src={logo} alt="Sanber Labs" style={{ height: 34 }} /> : <span className="logoTxt">SANBER LABS</span>}
    </div>
  </div>;
}

// Floating AI Assistant (Groq-backed). Talks only to /api/ai/chat, which enforces the
// logged-in user's permissions server-side — the browser never sees the key or raw DB.
function AIAssistant() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = React.useRef(null);
  React.useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [msgs, open, busy]);
  const send = async () => {
    const q = input.trim();
    if (!q || busy) return;
    const next = [...msgs, { role: "user", content: q }];
    setMsgs(next); setInput(""); setBusy(true);
    try { const r = await api.aiChat(next); setMsgs([...next, { role: "assistant", content: (r && r.reply) || "…" }]); }
    catch (e) { setMsgs([...next, { role: "assistant", content: "Sorry, the AI Assistant is temporarily unavailable. Please try again." }]); }
    finally { setBusy(false); }
  };
  const onKey = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } };
  return <>
    <style>{`
      @keyframes aiGlow {
        0%, 100% { box-shadow: 0 8px 22px rgba(79,70,229,0.45), 0 0 0 0 rgba(34,211,238,0.40); }
        50%      { box-shadow: 0 10px 30px rgba(124,58,237,0.55), 0 0 0 10px rgba(34,211,238,0); }
      }
      @keyframes aiSpin { to { transform: rotate(360deg); } }
      .ai-fab { transition: transform .18s ease, filter .18s ease; }
      .ai-fab:hover { transform: translateY(-2px) scale(1.07); filter: brightness(1.08); }
      .ai-fab:active { transform: scale(.95); }
      .ai-fab .ai-spark { transition: transform .25s ease; }
      .ai-fab:hover .ai-spark { transform: rotate(-8deg) scale(1.06); }
    `}</style>
    <button className="ai-fab" onClick={() => setOpen(o => !o)} title="AI Assistant" aria-label="AI Assistant" style={{ position: "fixed", right: 24, bottom: 24, zIndex: 1200, width: 60, height: 60, borderRadius: "50%", border: "none", cursor: "pointer", background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 52%, #06b6d4 100%)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", animation: open ? "none" : "aiGlow 2.8s ease-in-out infinite", boxShadow: open ? "0 8px 24px rgba(0,0,0,0.3)" : undefined }}>
      {/* subtle top highlight for a glossy, premium finish */}
      <span style={{ position: "absolute", inset: 0, borderRadius: "50%", background: "radial-gradient(120% 90% at 30% 20%, rgba(255,255,255,0.35), rgba(255,255,255,0) 55%)", pointerEvents: "none" }} />
      {open
        ? <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
        : <svg className="ai-spark" width="30" height="30" viewBox="0 0 24 24" fill="#fff" aria-hidden="true">
            <path d="M12 2.6l1.7 4.5 4.5 1.7-4.5 1.7L12 15l-1.7-4.5L5.8 8.8l4.5-1.7L12 2.6z" />
            <path d="M18.4 13.2l.85 2.15 2.15.85-2.15.85-.85 2.15-.85-2.15-2.15-.85 2.15-.85.85-2.15z" opacity="0.92" />
            <path d="M5.6 14.4l.6 1.55 1.55.6-1.55.6-.6 1.55-.6-1.55-1.55-.6 1.55-.6.6-1.55z" opacity="0.85" />
          </svg>}
    </button>
    {open && <div style={{ position: "fixed", right: 22, bottom: 90, zIndex: 1200, width: 370, maxWidth: "calc(100vw - 32px)", height: 540, maxHeight: "calc(100vh - 130px)", display: "flex", flexDirection: "column", background: "var(--surface, #fff)", border: "1px solid var(--border)", borderRadius: 14, boxShadow: "0 12px 40px rgba(0,0,0,0.28)", overflow: "hidden" }}>
      <div style={{ padding: "12px 14px", background: "var(--brand, #1e2a54)", color: "#fff" }}>
        <div style={{ fontWeight: 700 }}>Sanber Labs · AI Assistant</div>
        <div style={{ fontSize: 12, opacity: 0.85 }}>Ask about your QMS data</div>
      </div>
      <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", padding: 12, display: "flex", flexDirection: "column", gap: 8, background: "var(--bg, #f6f8fc)" }}>
        {msgs.length === 0 && <div style={{ color: "var(--text-2)", fontSize: 14 }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>👋 Welcome to AI Assistant</div>
          Try: "How many records are there?", "Show today's records", "Which items are pending?"
        </div>}
        {msgs.map((m, i) => <div key={i} style={{ alignSelf: m.role === "user" ? "flex-end" : "flex-start", maxWidth: "85%", padding: "8px 11px", borderRadius: 12, fontSize: 14, whiteSpace: "pre-wrap", wordBreak: "break-word", background: m.role === "user" ? "var(--brand, #1e2a54)" : "var(--surface, #fff)", color: m.role === "user" ? "#fff" : "var(--text, #111)", border: m.role === "user" ? "none" : "1px solid var(--border)" }}>{m.content}</div>)}
        {busy && <div style={{ alignSelf: "flex-start", color: "var(--text-2)", fontSize: 13 }}>Thinking…</div>}
      </div>
      <div style={{ padding: 10, borderTop: "1px solid var(--border)", display: "flex", gap: 6 }}>
        <textarea value={input} onChange={e => setInput(e.target.value)} onKeyDown={onKey} placeholder="Ask a question…" rows={1} style={{ flex: 1, resize: "none", padding: "8px 10px", borderRadius: 8, border: "1px solid var(--border)", fontSize: 14 }} />
        <button className="primary" disabled={busy || !input.trim()} onClick={send} style={{ borderRadius: 8 }}>Send</button>
      </div>
    </div>}
  </>;
}

// 8.6F-02 Customer Feedback Record — the lab's printed feedback letter: header details, request
// letter, a tick-one-per-row rating grid (Excellent…Below Average), auto marks/%, and signatures.
const CF_PARAMS = ["Quality of Analysis", "Satisfaction with technical procedure", "Behavior of staff", "Accessibility of staff", "Timely delivery of test certificates"];
const CF_RATINGS = [["excellent", "Excellent", 6], ["very_good", "Very Good", 5], ["good", "Good", 4], ["above_avg", "Above Average", 3], ["average", "Average", 2], ["below_avg", "Below Average", 1]];

function CustomerFeedback() {
  const emptyForm = () => ({ report_no: "", company_customer: "", designation: "", address: "", salutation: "", ratings: {}, analysis_remarks: "", qm_sign: "", qm_sign_id: "", customer_sign: "" });
  const [form, setForm] = useState(emptyForm());
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [signs, setSigns] = useState([]);
  const recs = [...items].reverse();
  const total = recs.length;
  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const fillForm = (rec) => setForm(rec ? { ...emptyForm(), ...rec } : emptyForm());
  const load = async () => { const list = await api.c4List("customer_feedback"); setItems(list); return list; };
  useEffect(() => { load().then(list => { setIdx(list.length); fillForm(null); }); api.signatoriesPublic().then(setSigns).catch(() => {}); }, []);
  const goTo = (i) => { const r = [...items].reverse(); const c = Math.max(0, Math.min(r.length, i)); setIdx(c); fillForm(c < r.length ? r[c] : null); };
  const newEntry = () => { setIdx(items.length); fillForm(null); };
  const resetCurrent = () => fillForm(idx < total ? recs[idx] : null);
  const setRating = (pi, rk) => setForm(f => ({ ...f, ratings: { ...(f.ratings || {}), [pi]: (f.ratings || {})[pi] === rk ? "" : rk } }));
  const totals = () => { let s = 0; CF_PARAMS.forEach((_, pi) => { const m = CF_RATINGS.find(r => r[0] === (form.ratings || {})[pi]); if (m) s += m[2]; }); return { sum: s, pct: Math.round(s / (CF_PARAMS.length * 6) * 1000) / 10 }; };
  const pickQM = (id) => { const g = signs.find(x => x.id === id); setForm(f => ({ ...f, qm_sign_id: id, qm_sign: g ? g.signature_image : "" })); };
  const uploadCustSign = (file) => { if (!file) return; const r = new FileReader(); r.onload = () => setF("customer_sign", r.result); r.readAsDataURL(file); };
  const save = async () => { const { sum, pct } = totals(); const body = { ...form, total_marks: sum, percentage: pct }; try { const saved = form.id ? await api.c4Edit("customer_feedback", form.id, body) : await api.c4Add("customer_feedback", body); const list = await load(); const r = [...list].reverse(); const sid = (saved && saved.id) || form.id; const pos = sid ? r.findIndex(x => x.id === sid) : -1; const at = pos >= 0 ? pos : Math.max(0, r.length - 1); setIdx(at); fillForm(r[at]); window.alert("Saved."); } catch (e) { window.alert("Save failed: " + ((e && e.message) || e)); } };
  const deletePage = async () => { const rec = idx < total ? recs[idx] : null; if (!rec || !rec.id) { window.alert("This is a new blank entry — nothing to delete."); return; } if (!window.confirm("Delete this saved feedback?")) return; try { await api.c4Del("customer_feedback", rec.id); const list = await load(); const r = [...list].reverse(); const at = Math.min(idx, r.length); setIdx(at); fillForm(at < r.length ? r[at] : null); } catch (e) { window.alert("Delete failed: " + ((e && e.message) || e)); } };
  const printForm = () => { const el = document.getElementById("fmt-customer_feedback"); if (el) { el.querySelectorAll("input").forEach(i => i.setAttribute("value", i.value)); el.querySelectorAll("textarea").forEach(t => { t.textContent = t.value; }); } printFormat("fmt-customer_feedback"); };
  const { sum, pct } = totals();
  const ci = { border: "none", background: "transparent", padding: "2px 4px", borderBottom: "1px solid #999" };
  const tick = (pi, rk) => { const on = (form.ratings || {})[pi] === rk; return <span onClick={() => setRating(pi, rk)} title="Tick" style={{ display: "inline-block", width: 22, height: 22, border: "1.5px solid #333", cursor: "pointer", textAlign: "center", lineHeight: "20px", fontWeight: 700 }}>{on ? "✓" : ""}</span>; };
  return <div className="main main-wide">
    <FormatTopbar n="8" only="customer_feedback" />
    <div className="card">
      <div className="row" style={{ justifyContent: "space-between", marginBottom: 8 }}>
        <b>Customer Feedback Record <span className="badge">8.6F-02</span><span style={{ marginLeft: 8, fontSize: 12, color: "var(--text-2)" }}>{idx < total ? `Record ${idx + 1} of ${total}` : (total ? `New entry · ${total} saved` : "New entry")}</span></b>
        <div className="row">
          <button className="noprint" onClick={() => goTo(idx - 1)} disabled={idx === 0}>◀ Previous</button>
          <button className="noprint" onClick={() => goTo(idx + 1)} disabled={idx >= total}>Next ▶</button>
          <button className="noprint" onClick={newEntry}>New</button>
          <button className="primary" onClick={save}>Save</button>
          <button className="noprint" onClick={resetCurrent}>Clear</button>
          {idx < total && <button className="noprint" onClick={deletePage} style={{ color: "var(--danger)" }}>Delete</button>}
          <button className="noprint" onClick={printForm}>Print / PDF</button>
        </div>
      </div>
      <div id="fmt-customer_feedback" className="fmt-doc">
        <FormatHeader code="8.6F-02" title="Customer Feedback Record" />
        <div style={{ marginTop: 10 }}><b>Feedback for Report No.:</b> <input value={form.report_no || ""} onChange={e => setF("report_no", e.target.value)} style={{ ...ci, width: 240 }} /></div>
        <div style={{ marginTop: 10 }}><b style={{ textDecoration: "underline" }}>Name of the Company / Customer:</b> <input value={form.company_customer || ""} onChange={e => setF("company_customer", e.target.value)} style={{ ...ci, width: 300 }} /></div>
        <div>Designation : <input value={form.designation || ""} onChange={e => setF("designation", e.target.value)} style={{ ...ci, width: 260 }} /></div>
        <div>Address : <input value={form.address || ""} onChange={e => setF("address", e.target.value)} style={{ ...ci, width: 380 }} /></div>
        <p style={{ marginTop: 10, fontSize: 13, lineHeight: 1.5 }}>Dear <input value={form.salutation || ""} onChange={e => setF("salutation", e.target.value)} placeholder="Sir / Mam" style={{ ...ci, width: 150 }} />,<br />I thank you very much for trusting our organization and using our services. May I request if you could give a feedback on the Quality of our testing services. Your extremely valuable input would be of great benefit to us in improving our quality. Thanking you and assuring you of our closest co-operation.</p>
        {form.qm_sign && <img src={form.qm_sign} alt="signature" style={{ height: 38 }} />}
        <div style={{ fontWeight: 600 }}>Quality Manager <select className="noprint" value={form.qm_sign_id || ""} onChange={e => pickQM(e.target.value)} style={{ marginLeft: 8, fontSize: 12 }}><option value="">— sign —</option>{signs.map(g => <option key={g.id} value={g.id}>{g.role}</option>)}</select></div>
        <div style={{ textAlign: "center", fontWeight: 700, textDecoration: "underline", marginTop: 12 }}>Please Tick Any One Box in each section</div>
        <table className="fmt-table" style={{ marginTop: 8 }}>
          <thead><tr><th style={{ width: "26%" }}></th>{CF_RATINGS.map(r => <th key={r[0]} style={{ whiteSpace: "normal", textAlign: "center" }}>{r[1]}</th>)}</tr></thead>
          <tbody>{CF_PARAMS.map((p, pi) => <tr key={pi}><td style={{ fontWeight: 600 }}>{pi + 1}. {p}</td>{CF_RATINGS.map(r => <td key={r[0]} style={{ textAlign: "center" }}>{tick(pi, r[0])}</td>)}</tr>)}</tbody>
        </table>
        <div style={{ marginTop: 12, fontSize: 13 }}>
          <div><b>Analysis by QM/TM:</b> <input value={form.analysis_remarks || ""} onChange={e => setF("analysis_remarks", e.target.value)} style={{ ...ci, width: 420 }} /></div>
          <div style={{ marginTop: 6 }}><b>Marks for:</b> Excellent = 6, Very Good = 5, Good = 4, Above Average = 3, Average = 2, Below Average = 1</div>
          <div style={{ marginTop: 6 }}><b>Total Marks Obtained =</b> {sum} &nbsp;&nbsp;&nbsp; <b>% Calculation =</b> {pct}%</div>
          <div style={{ marginTop: 6 }}><b>Note:</b> A minimum mark for satisfactory services by lab is 75%. {pct > 0 && <span style={{ color: pct >= 75 ? "var(--success)" : "var(--danger)", fontWeight: 600 }}>({pct >= 75 ? "Satisfactory" : "Below satisfactory"})</span>}</div>
        </div>
        <div style={{ marginTop: 24, textAlign: "right" }}>
          {form.customer_sign && <img src={form.customer_sign} alt="signature" style={{ height: 42 }} />}
          <div><label className="noprint" style={{ cursor: "pointer", color: "var(--brand, #2563eb)", fontSize: 13 }}>Upload customer signature<input type="file" accept="image/*" style={{ display: "none" }} onChange={e => uploadCustSign(e.target.files[0])} /></label></div>
          <div style={{ fontWeight: 600 }}>(Signature of Customer)</div>
        </div>
      </div>
    </div>
  </div>;
}

// QMS Alert Center — full notification list with filters, mark read/resolve, and open-record.
function AlertCenter() {
  const { user } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [scopeAll, setScopeAll] = useState(false);
  const canAll = !!(user.permissions && (user.permissions.users || []).includes("view"));
  const params = new URLSearchParams(loc.search);
  const initStatus = params.get("status") || "";
  const initSev = params.get("severity") || "";
  const [status, setStatus] = useState(initStatus);
  const [severity, setSeverity] = useState(initSev);
  const load = () => {
    const qs = [];
    if (status) qs.push("status=" + status);
    if (severity) qs.push("severity=" + severity);
    if (scopeAll && canAll) qs.push("scope=all");
    qs.push("limit=200");
    api.notifications(qs.join("&")).then(setItems).catch(() => {});
  };
  useEffect(() => { load(); }, [status, severity, scopeAll]);
  const collPath = (n) => ({ equipment_list: "/clause/6/equipment_list", equipment_logbook: "/clause/6/equipment_logbook", equipment_maintenance: "/clause/6/equipment_maintenance", training_calendar: "/clause/6/training_calendar", risk_register: "/clause/8/risk_register", internal_audit_plan: "/internal-audit" }[n.coll] || "/");
  const doRead = async (n) => { await api.notifRead(n.id); load(); };
  const doResolve = async (n) => { await api.notifResolve(n.id); load(); };
  return <div className="main main-wide">
    <div className="topbar" style={{ display: "block" }}>
      <h2>QMS Alert Center</h2>
      <div style={{ color: "var(--text-2)", fontSize: 13, marginTop: 2 }}>Time-sensitive QMS reminders — due, expiry, review and overdue records</div>
    </div>
    <div className="card">
      <div className="row" style={{ gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
        <select value={status} onChange={e => setStatus(e.target.value)}><option value="">All statuses</option><option value="unread">Unread</option><option value="read">Read</option><option value="overdue">Overdue</option><option value="resolved">Resolved</option></select>
        <select value={severity} onChange={e => setSeverity(e.target.value)}><option value="">All severities</option><option value="CRITICAL">Critical</option><option value="WARNING">Warning</option><option value="REMINDER">Reminder</option><option value="INFO">Info</option></select>
        {canAll && <label className="row" style={{ gap: 6, fontSize: 13 }}><input type="checkbox" checked={scopeAll} onChange={e => setScopeAll(e.target.checked)} /> All users</label>}
        <button onClick={async () => { await api.notifReadAll(); load(); }}>Mark all read</button>
      </div>
      <table><thead><tr><th style={{ width: 30 }}></th><th>Alert</th><th>Module</th><th>Due date</th><th>Severity</th><th style={{ textAlign: "right" }}>Actions</th></tr></thead>
        <tbody>
          {items.length === 0 && <tr><td colSpan={6} style={{ color: "var(--text-2)" }}>No alerts.</td></tr>}
          {items.map(n => <tr key={n.id} style={{ background: n.is_read ? "transparent" : "var(--info-bg,#eef4ff)", opacity: n.is_resolved ? 0.6 : 1 }}>
            <td>{SEV_DOT(n.severity)}</td>
            <td><div style={{ fontWeight: 600 }}>{n.title}</div><div style={{ fontSize: 12, color: "var(--text-2)" }}>{n.message}</div></td>
            <td style={{ fontSize: 13 }}>{n.module}</td>
            <td style={{ fontSize: 13, whiteSpace: "nowrap" }}>{inDate(n.due_date)}</td>
            <td><span style={{ color: SEV_COLOR[n.severity], fontWeight: 600, fontSize: 12 }}>{n.severity}</span></td>
            <td style={{ whiteSpace: "nowrap", textAlign: "right" }}>
              <button onClick={() => { doRead(n); nav(collPath(n)); }}>Open</button>
              {!n.is_read && <button onClick={() => doRead(n)} style={{ marginLeft: 6 }}>Read</button>}
              {!n.is_resolved && <button onClick={() => doResolve(n)} style={{ marginLeft: 6 }}>Resolve</button>}
            </td>
          </tr>)}
        </tbody></table>
    </div>
  </div>;
}

function Shell() {
  const { user, loading } = useAuth();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("nav_collapsed") === "1");
  const onToggle = () => { const v = !collapsed; setCollapsed(v); localStorage.setItem("nav_collapsed", v ? "1" : "0"); };
  if (loading) return <div style={{ padding: 40 }}>Loading…</div>;
  if (!user) return <Navigate to="/login" />;
  return <div className="app">
    <Sidebar collapsed={collapsed} onToggle={onToggle} />
    <div className="content-col">
      <TopBar />
      <div className="scroll">
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/rulebook/:doc_key" element={<Rulebook />} />
          <Route path="/clause/:n" element={<ClausePage />} />
          <Route path="/clause/:n/:coll" element={<ClausePage />} />
          <Route path="/company" element={<Company />} />
          <Route path="/org-chart" element={<OrgChart />} />
          <Route path="/masters" element={<Masters />} />
          <Route path="/roles" element={<Roles />} />
          <Route path="/users" element={<Users />} />
          <Route path="/backup" element={<Backup />} />
          <Route path="/recycle-bin" element={<RecycleBin />} />
          <Route path="/audit-log" element={<AuditLog />} />
          <Route path="/alerts" element={<AlertCenter />} />
          <Route path="/compliance" element={<ComplianceCalendar />} />
          <Route path="/iqc-plan" element={<IqcPlan />} />
          <Route path="/internal-audit" element={<InternalAudit />} />
          <Route path="/obsolete" element={<ObsoleteDocs />} />
        </Routes>
      </div>
    </div>
    <AIAssistant />
  </div>;
}

export default function App() {
  return <AuthProvider><BrowserRouter><Routes>
    <Route path="/login" element={<Login />} />
    <Route path="/*" element={<Shell />} />
  </Routes></BrowserRouter></AuthProvider>;
}
